import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { collection, query, onSnapshot, doc, getDoc, setDoc, deleteDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../firebase';
import { usePriceContext } from '../context/PriceContext'; 
import IkasSettingsDialog from '../components/ikas/IkasSettingsDialog';
import TrendyolConnectionTestPanel from '../components/trendyol/TrendyolConnectionTestPanel';
import TrendyolProductInfoForm from '../components/trendyol/TrendyolProductInfoForm';
import TrendyolProductMatchingPanel from '../components/trendyol/TrendyolProductMatchingPanel';
import {
  buildTrendyolProductFirestorePatch,
  normalizeTrendyolProductInfo,
  TRENDYOL_CARGO_COMPANY_LABEL_BY_ID,
  TRENDYOL_CARGO_COMPANY_SELECT_IDS,
  TRENDYOL_DELIVERY_DURATION_OPTIONS,
  TRENDYOL_PRODUCT_FIELDS,
  TRENDYOL_VAT_RATE_OPTIONS
} from '../components/trendyol/trendyolProductFormUtils';
import { buildAutoProductContent } from '../utils/productWebContent';
import { validateIkasReadyProduct } from '../utils/validateIkasReadyProduct';
import { TAKI_URUN_TIPLERI } from '../utils/takiUrunTipleri';
import { apiUrl } from '../utils/apiBase';
import { matchesSearch } from '../utils/searchText';
import { rejectDeprecatedTestTrendyolBrandId, DEPRECATED_TEST_TRENDYOL_BRAND_ID } from '../utils/trendyolBrandIds';
import { isTrendyolProductMatched } from '../utils/trendyolListingMatch';
import { computeTrendyolMarketplaceTableButton, pickTrendyolProductUrl } from '../utils/trendyolProductListUi';
import {
  calculateUrunLaborTry,
  calculateUrunMaliyetTry,
  normalizeAdetIscilikForSave,
  normalizeIscilikForSave
} from '../utils/urunPricing';

import { 
  Box, Typography, Button, TextField, Select, MenuItem, Alert, Menu,
  Link,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  Card, CardContent, Avatar, Chip, IconButton, Grid, Stack, Dialog, 
  DialogTitle, DialogContent, DialogActions, InputAdornment, Pagination, FormControl, InputLabel, Divider, Tooltip, Paper, Autocomplete, Checkbox, FormControlLabel, Snackbar, LinearProgress, CircularProgress 
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Search as SearchIcon, 
  Inventory2 as InventoryIcon,
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon,
  Close as CloseIcon,
  Diamond as DiamondIcon,
  Info as InfoIcon,
  AttachMoney as AttachMoneyIcon,
  TrendingDown as TrendingDownIcon,
  CurrencyLira as CurrencyLiraIcon,
  ContentCopy as ContentCopyIcon,
  Store as StoreIcon,
  RestartAlt as RestartAltIcon,
  Language as LanguageIcon,
  InfoOutlined as InfoOutlinedIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon,
  Favorite as FavoriteIcon,
  Send as SendIcon,
  ArrowDropDown as ArrowDropDownIcon
} from '@mui/icons-material';

// --- SABİT DEĞİŞKENLER VE BİLEŞENLER ---

const altKategoriler = ['Tümü', 'Yüzük', 'Kolye', 'Kolye Ucu', 'Küpe', 'Bileklik', 'Bilezik', 'Set', 'Alyans', 'Bebek & Çocuk', 'Diğer'];
const safeString = (value, fallback = '') => (typeof value === 'string' ? value : fallback);
const normalizeCategoryText = (value) => String(value || '').trim().toLocaleLowerCase('tr-TR');

const formatFirestoreTs = (ts) => {
  if (!ts) return '';
  const sec = ts.seconds != null ? ts.seconds : ts._seconds;
  if (sec == null) return '';
  try {
    return new Date(sec * 1000).toLocaleString('tr-TR');
  } catch (e) {
    return '';
  }
};

const UKBA_WEB_ORIGIN = 'https://www.ukbagold.com';

const UKBA_GOLD_TRENDYOL_MARKA_ADI = 'UKBA GOLD';

/** Sunucudaki coerceBrandMapStoredValue ile aynı mantık: brandMap değeri sayı / nesne olabilir */
function coerceTrendyolBrandMapStoredValueFrontend(raw) {
  if (raw == null || raw === '') return '';
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return rejectDeprecatedTestTrendyolBrandId(String(Math.trunc(raw)));
  }
  if (typeof raw === 'string') return rejectDeprecatedTestTrendyolBrandId(raw.trim());
  if (typeof raw === 'object') {
    const cand = raw.id ?? raw.brandId ?? raw.value ?? raw.slug;
    return coerceTrendyolBrandMapStoredValueFrontend(typeof cand === 'number' ? Math.trunc(cand) : cand);
  }
  return '';
}

function normTrendyolBrandLookupKeyFrontend(str) {
  return String(str ?? '').trim().toLocaleLowerCase('tr-TR').replace(/\s+/g, ' ');
}

/** API’nin yanlış tip döndürme ihtimaline karşı (string JSON vb.) — sunucudaki ile uyumlu */
function normalizeMerchantBrandMap(raw) {
  if (raw == null || raw === '') return {};
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t) return {};
    try {
      const parsed = JSON.parse(t);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
}

/** integrations/trendyol.brandMap’te anahtar birebir olmasa bile 'UKBA GOLD’u bul */
function resolveUkbaGoldBrandIdFromMerchantCfg(cfg) {
  if (!cfg || typeof cfg !== 'object') return '';
  const bm = normalizeMerchantBrandMap(cfg.brandMap);
  const exact = coerceTrendyolBrandMapStoredValueFrontend(bm[UKBA_GOLD_TRENDYOL_MARKA_ADI]);
  if (exact) return exact;
  const want = normTrendyolBrandLookupKeyFrontend(UKBA_GOLD_TRENDYOL_MARKA_ADI);
  for (const [mapKey, mapVal] of Object.entries(bm)) {
    if (normTrendyolBrandLookupKeyFrontend(mapKey) !== want) continue;
    const id = coerceTrendyolBrandMapStoredValueFrontend(mapVal);
    if (id) return id;
  }
  return rejectDeprecatedTestTrendyolBrandId(String(cfg.defaultBrandId ?? '').trim());
}

/** ty_brandId alanına yanlışlıkla "UKBA GOLD" gibi metin yazılmışsa yok say; config’e düş */
function parseTrendyolPositiveIntegerBrandId(raw) {
  const s = rejectDeprecatedTestTrendyolBrandId(String(raw ?? '').trim());
  if (!s) return '';
  const normalized = String(s).replace(',', '.').trim().replace(/[^\d.-]/g, '');
  if (!normalized) return '';
  const n = Number(normalized);
  if (!Number.isFinite(n)) return '';
  const k = Math.trunc(n);
  if (k <= 0 || k === DEPRECATED_TEST_TRENDYOL_BRAND_ID) return '';
  return String(k);
}

function resolveTrendyolBrandIdForSend(tyBrandIdRaw, cfg) {
  const fromProduct = parseTrendyolPositiveIntegerBrandId(tyBrandIdRaw);
  if (fromProduct) return fromProduct;
  return resolveUkbaGoldBrandIdFromMerchantCfg(cfg || {});
}

function isTrendyolNumericBrandIdResolved(id) {
  return Boolean(parseTrendyolPositiveIntegerBrandId(id));
}

/** İkas mağaza ürün URL’si: kök slug (bkz. İkas SEO — ürünler domain.com/urun-adı). /urun/ öneki kullanılmaz. */
const joinUkbagoldStoreProductPath = (relativePathOrSlug) => {
  const raw = String(relativePathOrSlug || '').replace(/^\/+/, '');
  const pathOnly = raw.replace(/^urun\//i, '');
  return pathOnly ? `${UKBA_WEB_ORIGIN}/${pathOnly}` : '';
};

/** ukba mağaza ürün sayfası URL’si (İkas payload’daki canonical ile uyumlu; ikasProductId yoksa boş). */
const resolveUkbagoldProductUrl = (urun) => {
  const ikasPid = String(urun?.ikasProductId || '').trim();
  if (!ikasPid) return '';
  const slug = String(urun?.ws_slug || urun?.ikasSyncedSlug || '').trim();
  const canon = String(urun?.ws_canonicalUrl || '').trim();
  if (/^https?:\/\//i.test(canon)) return canon;
  if (!canon && slug) return joinUkbagoldStoreProductPath(slug);
  if (canon) return joinUkbagoldStoreProductPath(canon);
  return '';
};

/** Ürün listesi İkas / Trendyol pazaryeri sütunları — küçük metin butonları (yalnızca görünüm). */
const marketplaceTableBtnSx = (greenLook) => ({
  width: 70,
  minWidth: 70,
  maxWidth: 70,
  minHeight: 19,
  py: 0.1,
  px: 0.4,
  fontSize: '10.25px',
  fontWeight: 800,
  lineHeight: 1.05,
  textTransform: 'none',
  borderRadius: 1,
  boxShadow: 'none',
  whiteSpace: 'nowrap',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  ...(greenLook
    ? {
        bgcolor: '#16a34a',
        color: '#fff',
        '&:hover': { bgcolor: '#15803d', boxShadow: 'none' }
      }
    : {
        bgcolor: '#dc2626',
        color: '#fff',
        '&:hover': { bgcolor: '#b91c1c', boxShadow: 'none' }
      }),
  '&.Mui-disabled': greenLook
    ? { bgcolor: '#bbf7d0', color: '#14532d', opacity: 1 }
    : { bgcolor: '#fecaca', color: '#991b1b', opacity: 1 }
});

/** Ürün listesi Trendyol sütunu: satış yeşili / onay bekleyen gri / hata veya kapalı kırmızı */
const trendyolMarketplaceTableBtnSx = (variant) => {
  const common = {
    width: 70,
    minWidth: 70,
    maxWidth: 70,
    minHeight: 19,
    py: 0.1,
    px: 0.35,
    fontSize: '10.25px',
    fontWeight: 800,
    lineHeight: 1.05,
    textTransform: 'none',
    borderRadius: 1,
    boxShadow: 'none',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const disabledPending = {
    bgcolor: '#cbd5e1',
    color: '#475569',
    '&:hover': { bgcolor: '#cbd5e1', boxShadow: 'none', cursor: 'default' }
  };

  switch (variant) {
    case 'sale':
      return {
        ...common,
        bgcolor: '#16a34a',
        color: '#fff',
        '&:hover': { bgcolor: '#15803d', boxShadow: 'none' },
        '&.Mui-disabled': { bgcolor: '#bbf7d0', color: '#14532d', opacity: 1 }
      };
    case 'pending':
      return {
        ...common,
        bgcolor: '#94a3b8',
        color: '#fff',
        '&:hover': { bgcolor: '#64748b', boxShadow: 'none' },
        '&.Mui-disabled': disabledPending
      };
    case 'error':
      return {
        ...common,
        bgcolor: '#dc2626',
        color: '#fff',
        '&:hover': { bgcolor: '#dc2626', boxShadow: 'none', cursor: 'default' },
        '&.Mui-disabled': {
          bgcolor: '#fecaca',
          color: '#991b1b',
          opacity: 1,
          '&:hover': { bgcolor: '#fecaca' }
        }
      };
    case 'closed':
    default:
      return {
        ...common,
        bgcolor: '#dc2626',
        color: '#fff',
        '&:hover': { bgcolor: '#b91c1c', boxShadow: 'none' },
        '&.Mui-disabled': {
          bgcolor: '#fecaca',
          color: '#991b1b',
          opacity: 1,
          '&:hover': { bgcolor: '#fecaca', cursor: 'default' }
        }
      };
  }
};

/** Ürün listesi İkas sütunu tek buton (İkas mağaza / web ürün URL’si). */
const computeIkasMarketplaceTableButton = (urun) => {
  const href = resolveUkbagoldProductUrl(urun);
  const st = String(urun?.ikasSyncStatus || '').trim();
  const lastErr = String(urun?.ikasLastError || '').trim();
  const syncErr = st === 'error';
  const hasHref = Boolean(href);
  const greenLook = !syncErr && !lastErr && (hasHref || st === 'synced');
  const disabled = !hasHref || syncErr || Boolean(lastErr);
  const tooltipLines = [`ikasSyncStatus: ${st || '—'}`];
  if (hasHref) tooltipLines.push(`Bağlantı: ${href}`);
  else tooltipLines.push('Mağaza ürün bağlantısı yok');
  if (lastErr) tooltipLines.push(`Son hata: ${lastErr}`);
  return { greenLook, disabled, href: href || '', tooltip: tooltipLines.join('\n') };
};

const parseNumLoose = (v) => {
  if (v == null || v === '') return 0;
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  const x = parseFloat(String(v).replace(',', '.').trim());
  return Number.isFinite(x) ? x : 0;
};

/** Otomatik hesaplanan satış fiyatını yukarı yönlü en yakın 10 TL'ye yuvarlar (tam sayı). */
const roundAutoPriceUpToTen = (value) => {
  const n = parseNumLoose(value);
  if (!(n > 0)) return 0;
  return Math.ceil(n / 10) * 10;
};

const formatAutoPrice = (value) => String(roundAutoPriceUpToTen(value));

const displayMagazaPriceNum = (row) => {
  const n = parseNumLoose(row?.magazaFiyati);
  if (!(n > 0)) return 0;
  return row?.isManualMagaza ? n : roundAutoPriceUpToTen(n);
};

const displayPazaryeriPriceNum = (row) => {
  const n = parseNumLoose(row?.pazaryeriFiyati);
  if (!(n > 0)) return 0;
  return row?.isManualPazaryeri ? n : roundAutoPriceUpToTen(n);
};

/** Web: `ws_websiteSatisFiyati` = web satış fiyatı, indirim varsa satış (indirimli) web satış üzerinden hesaplanır. */
const webListeNum = (row) => {
  const n = parseNumLoose(row?.ws_websiteSatisFiyati);
  if (!(n > 0)) return 0;
  if (row?.isManualSite || row?.priceSource === 'manual') return n;
  return roundAutoPriceUpToTen(n);
};
const webDiscountEkTl = (row) => parseNumLoose(row?.mg_ekIndirim);
const webDiscountYuzde = (row) => parseNumLoose(row?.mg_indirimYuzde);
const webIndirimliNum = (row) => {
  const L = webListeNum(row);
  if (!(L > 0)) return 0;
  const ek = webDiscountEkTl(row);
  const y = webDiscountYuzde(row);
  if (!(ek > 0 || y > 0)) return 0;
  const out = Math.max(0, L - ek - (L * y) / 100);
  return Number.isFinite(out) ? roundAutoPriceUpToTen(out) : 0;
};
const webHasListAndDiscountedPair = (row) => {
  const L = webListeNum(row);
  const I = webIndirimliNum(row);
  return L > 0 && I > 0 && I < L - 0.009;
};

/** Kuyumcu KDV: matrah = son satış − has altın; has altın = gram × has kuru × milyem. KDV = matrah × %20 (üç sekmede aynı). */
const KUYUMCU_KDV_ORANI = 20;

/** Ürün tipi metnine göre milyem (bilinmiyorsa ürün kaydındaki milyem kullanılır). */
const getKuyumcuKdvMilyemi = (urunTipi) => {
  const t = (urunTipi || '').toLowerCase();
  if (t.includes('22 ayar')) return 0.912;
  if (t.includes('21 ayar')) return 0.875;
  if (t.includes('18 ayar')) return 0.75;
  if (t.includes('14 ayar')) return 0.585;
  if (t.includes('8 ayar')) return 0.333;
  return null;
};

/** Has altın tutarı = gram × güncel has (kur) × milyem. */
const computeKuyumcuHasAltinDegeri = (gram, hasKuru, urunTipi, urunMilyemFallback) => {
  const g = parseNumLoose(gram);
  const k = parseNumLoose(hasKuru);
  const sabit = getKuyumcuKdvMilyemi(urunTipi);
  const m = sabit != null ? sabit : parseNumLoose(urunMilyemFallback);
  if (!(g > 0 && k > 0 && m > 0)) return 0;
  return g * k * m;
};
const modalInputSx = {
  '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff', fontSize: '0.95rem' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
  '& .MuiInputLabel-root': { fontSize: '0.85rem' },
  '& input[type=number]': { MozAppearance: 'textfield' },
  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0
  }
};

/** Sadece Web Site Ürün Detay > Satış detayları (orta sıkılık). */
const webSatisDetayInputSx = {
  ...modalInputSx,
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.5,
    bgcolor: '#fff',
    fontSize: '0.82rem',
    minHeight: 36
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
  '& .MuiInputBase-input': { py: '6px', px: '10px' },
  '& .MuiInputLabel-root': { fontSize: '0.76rem' }
};

const modalPriceCardSx = {
  p: 0.4,
  borderRadius: 1.5,
  border: '1px solid #e8e4dc',
  bgcolor: '#fff',
  width: '100%',
  minWidth: 0,
  minHeight: 84,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box'
};

function ImageUploadBox({ label, preview, onChange, onRemove, onImageFile, size = 200 }) {
  const [dragOver, setDragOver] = useState(false);
  const rootRef = useRef(null);
  /** Ürün detayı yan yanına 5 kutu sığdığında (≈78px) içerik taşmasın. */
  const compact = typeof size === 'number' && size <= 100;
  const snug = typeof size === 'number' && size > 100 && size <= 140;

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const next = e.relatedTarget;
    if (!rootRef.current || (next && rootRef.current.contains(next))) return;
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f && /^image\//.test(f.type)) onImageFile?.(f);
  };

  return (
    <Box
      ref={rootRef}
      sx={{
        border: `2px dashed ${dragOver ? '#1e3a8a' : '#cbd5e1'}`,
        borderRadius: 2,
        backgroundColor: dragOver ? '#eff6ff' : '#f8fafc',
        cursor: 'pointer',
        position: 'relative',
        width: size,
        height: size,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        transition: 'border-color 0.2s, background-color 0.2s',
        '&:hover': { borderColor: '#1e3a8a', backgroundColor: '#eff6ff' }
      }}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {preview ? (
        <>
          <img
            src={preview}
            alt=""
            draggable={false}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              padding: '4px',
              pointerEvents: 'none',
              userSelect: 'none'
            }}
          />
          <IconButton
            onClick={(ev) => {
              ev.stopPropagation();
              onRemove();
            }}
            sx={{
              position: 'absolute',
              top: 2,
              right: 2,
              bgcolor: 'rgba(255,255,255,0.9)',
              padding: '2px',
              '&:hover': { bgcolor: '#fff' }
            }}
            size="small"
          >
            <DeleteIcon color="error" fontSize="small" />
          </IconButton>
        </>
      ) : (
        <label
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            padding: compact ? '2px 3px 3px' : '4px 6px 6px',
            boxSizing: 'border-box',
            gap: compact ? 0 : undefined
          }}
        >
          <CloudUploadIcon
            sx={{
              flexShrink: 0,
              fontSize:
                compact ? 16
                : snug ? 22
                : typeof size === 'number' && size <= 200
                  ? (size <= 120 ? 26 : 32)
                  : 34,
              color: '#94a3b8',
              mb: compact ? 0 : snug ? 0.35 : size <= 120 ? 0.5 : 1
            }}
          />
          <Typography
            variant="caption"
            color="textSecondary"
            fontWeight="bold"
            sx={{
              fontSize: compact ? '0.58rem' : snug ? '0.65rem' : undefined,
              lineHeight: compact ? 1.05 : 1.15,
              textAlign: 'center',
              px: compact ? 0 : 0.25
            }}
          >
            {label}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              mt: compact ? '1px' : 0.25,
              px: compact ? '1px' : 0.5,
              fontSize: compact ? '0.48rem' : snug ? '0.55rem' : undefined,
              lineHeight: compact ? 1.05 : 1.15,
              textAlign: 'center',
              opacity: compact ? 0.92 : 1
            }}
          >
            Tıklayın veya sürükleyin
          </Typography>
          <input type="file" accept="image/*" hidden onChange={onChange} onClick={(ev) => { ev.target.value = null; }} />
        </label>
      )}
    </Box>
  );
}

/** Trendyol toplu kategori arama — TrendyolProductInfoForm ile uyumlu yanıt düzleştirme */
const bulkTyToText = (value) => String(value ?? '').trim();
const bulkTyFormatCategoryId = (value) => bulkTyToText(value);
const bulkTyNormalizeSearchText = (value) =>
  bulkTyToText(value)
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const bulkTyDedupeCategories = (rows = []) => {
  const seen = new Set();
  return rows.filter((row) => {
    const key = `${bulkTyFormatCategoryId(row?.id)}::${bulkTyNormalizeSearchText(row?.path || row?.name)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const bulkTyBuildCategoryPath = (node, parentPath = '') => {
  const name = bulkTyToText(node?.name);
  const currentPath = bulkTyToText(node?.path || node?.categoryPath || node?.displayName);
  if (currentPath) return currentPath;
  return [parentPath, name].filter(Boolean).join(' > ');
};

const bulkTyFlattenCategories = (rows = [], parentPath = '') =>
  (Array.isArray(rows) ? rows : []).flatMap((node) => {
    const id = bulkTyFormatCategoryId(node?.id);
    const name = bulkTyToText(node?.name);
    const path = bulkTyBuildCategoryPath(node, parentPath);
    const children = Array.isArray(node?.subCategories) ? node.subCategories : [];
    const isLeaf = node?.isLeaf === true || children.length === 0;
    const item = id || name ? [{ id, name, path: path || name, isLeaf }] : [];
    return [...item, ...bulkTyFlattenCategories(children, path || name)];
  });

const bulkTyExtractCategoryRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];

  const candidates = [
    payload.categories,
    payload.data,
    payload.result,
    payload.items,
    payload.content,
    payload.raw?.categories,
    payload.raw?.data,
    payload.raw?.result,
    payload.raw?.items,
    payload.raw?.content
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
    if (candidate && typeof candidate === 'object') {
      const nested = bulkTyExtractCategoryRows(candidate);
      if (nested.length) return nested;
    }
  }

  return [];
};

const bulkTyNormalizeCategoryPayload = (payload) =>
  bulkTyDedupeCategories(bulkTyFlattenCategories(bulkTyExtractCategoryRows(payload)));

const bulkTyFilterCategoriesLocally = (rows = [], query = '') => {
  const normalizedQuery = bulkTyNormalizeSearchText(query);
  if (!normalizedQuery) return rows;
  return rows.filter((row) => {
    const haystack = bulkTyNormalizeSearchText([row?.name, row?.path, row?.id].filter(Boolean).join(' '));
    return haystack.includes(normalizedQuery);
  });
};

const bulkTyCategoryOptionLabel = (option) => {
  if (!option) return '';
  const name = bulkTyToText(option.name);
  const id = bulkTyToText(option.id);
  return id ? `${name} (${id})` : name;
};

const bulkTyReadJsonSafely = async (response) => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};

function UrunListesi() {
  const { prices } = usePriceContext();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const sube = queryParams.get('sube')?.trim() || 'Bilinmeyen Şube';

  const [urunler, setUrunler] = useState([]);
  const [formuller, setFormuller] = useState([]); 
  const [filteredUrunler, setFilteredUrunler] = useState([]);
  const [ikasCategories, setIkasCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTab, setSelectedTab] = useState('Tümü');
  /** İkas kök kategori + alt menü (anchorEl, rootCategoryId) */
  const [ikasCatMenu, setIkasCatMenu] = useState(null);
  const createInitialBulkForm = useCallback(
    () => ({
      iscilik: '',
      adetIscilik: '',
      stok: '',
      magazaListe: '',
      magazaIndirimli: '',
      webListe: '',
      webIndirimli: '',
      pazarListe: '',
      pazarIndirimli: '',
      bulkMagazaAuto: false,
      bulkWebAuto: false,
      bulkPazarAuto: false,
      kkKomisyonu: '',
      kdvOrani: String(KUYUMCU_KDV_ORANI),
      ekMasraf: '',
      kargoBedeli: '',
      ekIndirim: '',
      indirimYuzde: '',
      ikasCategoryIds: [],
      ikasCategoryNamesSnapshot: [],
      ikasCategoryId: '',
      urunKategorisi: '',
      bulkCategoryMerge: true,
      webIndirimliAutoDraft: false,
      tyKomisyonOrani: '',
      tyKargoUcreti: '',
      tyEkMasraf: '',
      tyKarMarji: '',
      tyKampanyaTipi: '',
      tyKampanyaDegeri: '',
      ty_vatRate: '',
      ty_dimensionalWeight: '',
      ty_cargoCompanyId: '',
      ty_deliveryDuration: ''
    }),
    []
  );
  
  // --- TOPLU İŞLEM STATELERİ ---
  const [selectedIds, setSelectedIds] = useState([]); 
  const [openBulkModal, setOpenBulkModal] = useState(false); 
  const [bulkForm, setBulkForm] = useState(createInitialBulkForm);
  /** Toplu TY gönderim: kategori seçildiyse tüm seçili ürünlere uygulanır; null = dokunma */
  const [bulkTrendyolCategoryLeaf, setBulkTrendyolCategoryLeaf] = useState(null);
  const [bulkTyCategoryInput, setBulkTyCategoryInput] = useState('');
  const [bulkTyCategoryOptions, setBulkTyCategoryOptions] = useState([]);
  const [bulkTyCategoryLoading, setBulkTyCategoryLoading] = useState(false);
  const [bulkTyCategoryError, setBulkTyCategoryError] = useState('');

  // --- UI STATELERİ ---
  const [openModal, setOpenModal] = useState(false); 
  const [openTrendyol, setOpenTrendyol] = useState(false); 
  const [openMagaza, setOpenMagaza] = useState(false); 
  const [openWebsite, setOpenWebsite] = useState(false);
  const [openIkasSettings, setOpenIkasSettings] = useState(false);
  const [openTrendyolTest, setOpenTrendyolTest] = useState(false);
  const [openIkasPreview, setOpenIkasPreview] = useState(false);
  const [ikasPreviewLoading, setIkasPreviewLoading] = useState(false);
  const [ikasPreviewPayload, setIkasPreviewPayload] = useState(null);
  const [trendyolPreviewLoading, setTrendyolPreviewLoading] = useState(false);
  const [trendyolPreviewResult, setTrendyolPreviewResult] = useState(null);
  const [trendyolPreviewError, setTrendyolPreviewError] = useState('');
  const [openTrendyolPayloadPreviewDialog, setOpenTrendyolPayloadPreviewDialog] = useState(false);
  const [trendyolSaveLoading, setTrendyolSaveLoading] = useState(false);
  const [trendyolSaveResult, setTrendyolSaveResult] = useState(null);
  const [trendyolSaveError, setTrendyolSaveError] = useState('');
  const [trendyolMerchantCfg, setTrendyolMerchantCfg] = useState(null);
  const [trendyolMerchantCfgLoading, setTrendyolMerchantCfgLoading] = useState(false);
  const [trendyolCategoryPortalHost, setTrendyolCategoryPortalHost] = useState(null);
  const [trendyolShipmentPortalHost, setTrendyolShipmentPortalHost] = useState(null);
  /** Liste satırı «Onayda» Trendyol durum yenileme (ürün id → yüklü mü) */
  const [trendyolListRowRefreshBusy, setTrendyolListRowRefreshBusy] = useState({});
  const [ikasConfig, setIkasConfig] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false); // Kopyalama bildirimi için
  const [snackbarMessage, setSnackbarMessage] = useState('');
  /** Web Site Ürün Detay: Firestore + İkas sonucu (üstte Alert) */
  const [websiteSaveBanner, setWebsiteSaveBanner] = useState(null);
  const websiteBannerTimerRef = useRef(null);
  /** Web Site Ürün Detay: eksik SEO/açıklama doldurma bildirimi */
  const [websiteSeoGenBanner, setWebsiteSeoGenBanner] = useState(null);
  const websiteSeoGenBannerTimerRef = useRef(null);
  const [webSeoFillLoading, setWebSeoFillLoading] = useState(false);
  const [ikasSendLoading, setIkasSendLoading] = useState(false);
  /** Toplu / yönetim İkas & SEO işlemleri özet penceresi */
  const [ikasJobDialogOpen, setIkasJobDialogOpen] = useState(false);
  const [ikasJobTitle, setIkasJobTitle] = useState('');
  const [ikasJobSummary, setIkasJobSummary] = useState('');
  const [ikasJobDetail, setIkasJobDetail] = useState([]);
  const [ikasJobRunning, setIkasJobRunning] = useState(false);
  const [ikasVerifyLoading, setIkasVerifyLoading] = useState(false);
  /** Ürün listesi: sayfa ziyaretinde sessiz İkas doğrulama. */
  const urunlerRef = useRef([]);

  const [selectedUrun, setSelectedUrun] = useState(null);
  const [editForm, setEditForm] = useState(null);
  
  const [canliHasKur, setCanliHasKur] = useState(0); 
  const [manuelKur, setManuelKur] = useState(0); 
  const [acilModAktif, setAcilModAktif] = useState(false); 
  const [guncelSistemKuru, setGuncelSistemKuru] = useState(0); 

  const [unitInfo, setUnitInfo] = useState({
      magazaBirim: 0,
      webBirim: 0,
      kaynak: 'Manuel'
  });

  const [trendyolForm, setTrendyolForm] = useState({
    hesaplamaYontemi: 'Manuel',
    hasAltinKuru: '',
    komisyonOrani: '',
    kargoUcreti: '',
    ekMasraf: '0',
    karMarji: '',
    trendyolSatisFiyati: '',
    trendyolIndirimliSatisFiyati: '',
    kampanyaTipi: 'yok',
    kampanyaDegeri: '0'
  });
  const [trendyolProductInfo, setTrendyolProductInfo] = useState({});

  const [websiteForm, setWebsiteForm] = useState({
    hasAltinKuru: 0,
    websiteSatisFiyati: '',
    kargoUcreti: '',
    ekMasraf: '0',
    kampanyaTipi: 'yok',
    kampanyaDegeri: '0'
  });

  const [magazaForm, setMagazaForm] = useState({
    satisFiyati: '',
    kkKomisyonu: '0',
    kdvOrani: '20',
    ekIndirim: '0',
    indirimYuzde: '0',
    ekMasraf: '0',
    kargoBedeli: '0'
  });
  
  const [preview1, setPreview1] = useState(null);
  const [preview2, setPreview2] = useState(null);
  const [preview3, setPreview3] = useState(null);
  const [preview4, setPreview4] = useState(null);
  const [preview5, setPreview5] = useState(null);
  const [tyPreview1, setTyPreview1] = useState(null);
  const [tyPreview2, setTyPreview2] = useState(null);
  const [tyPreview3, setTyPreview3] = useState(null);
  const [tyPreview4, setTyPreview4] = useState(null);
  const [tyPreview5, setTyPreview5] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // SAYFA BAŞINA 10 ÜRÜN
  const itemsPerPage = 10;

  const safeParseFloat = (val) => {
    if (!val) return 0;
    if (typeof val === 'number') return val;
    const cleanVal = val.toString().replace(',', '.').trim();
    return parseFloat(cleanVal) || 0;
  };

  /** Toplu fiyat: mağaza sekmesindeki formül satış (liste) — ürünün gram/kur/ürün tipüne göre. */
  const computeBulkFormulaMagazaStr = (urun) => {
    const gram = safeParseFloat(urun?.gram);
    const kur = safeParseFloat(urun?.hasAltinKuru);
    if (!(gram > 0 && kur > 0)) return null;
    const tip = String(urun?.urunTipi || '');
    let hedefFormul = null;
    if (Array.isArray(formuller) && formuller.length > 0) {
      hedefFormul = formuller.find((f) => f.ad === tip);
      if (!hedefFormul) {
        if (tip.includes('14 Ayar')) hedefFormul = formuller.find((f) => f.ad === '14 Ayar');
        else if (tip.includes('22 Ayar')) hedefFormul = formuller.find((f) => f.ad === '22 Ayar');
        else if (tip.includes('8 Ayar')) hedefFormul = formuller.find((f) => f.ad === '8 Ayar');
        else if (tip.includes('18 Ayar')) hedefFormul = formuller.find((f) => f.ad === '18 Ayar');
      }
    }
    if (!hedefFormul) return null;
    const satisCarpan = safeParseFloat(hedefFormul.satisCarpan);
    const satisIscilik = safeParseFloat(hedefFormul.satisIscilik);
    const baseUnitPrice = kur * satisCarpan + satisIscilik;
    const yeniMagazaFiyati = baseUnitPrice * gram;
    return Number.isFinite(yeniMagazaFiyati) ? formatAutoPrice(yeniMagazaFiyati) : null;
  };

  useEffect(() => {
    const unsubFormul = onSnapshot(collection(db, "formuller"), (snapshot) => {
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setFormuller(list);
    });
    return () => unsubFormul();
  }, []);

  useEffect(() => {
    const ayarDinle = onSnapshot(doc(db, "ayarlar", "genel_durum"), (docSnap) => {
        if (docSnap.exists()) {
            const veri = docSnap.data();
            const mod = veri.aktif || false;
            setAcilModAktif(mod);
            setManuelKur(safeParseFloat(veri.manuelHasSatis));
        }
    });
    return () => ayarDinle();
  }, []);

  useEffect(() => {
    if (prices && prices.length > 0) {
      const altin = prices.find(p => p.currencyCode === "ALTINTRY" || p.currencyCode === "HAS ALTIN" || p.currencyCode === "HAS");
      const xauusd = prices.find(p => p.currencyCode === "XAUUSD");
      const usdtry = prices.find(p => p.currencyCode === "USDTRY");

      let satisFiyati = 0;
      if (altin && altin.sell > 0) {
          satisFiyati = parseFloat(altin.sell);
      } else if (xauusd?.sell && usdtry?.sell) {
          satisFiyati = (xauusd.sell * usdtry.sell) / 31.1035;
      }
      setCanliHasKur(satisFiyati);
    }
  }, [prices]);

  useEffect(() => {
    if (acilModAktif) {
        setGuncelSistemKuru(manuelKur);
    } else {
        setGuncelSistemKuru(canliHasKur > 0 ? canliHasKur : manuelKur);
    }
  }, [acilModAktif, canliHasKur, manuelKur]);

  const getAutoCategory = (name) => {
    if (!name) return 'Diğer';
    const n = name.toLowerCase();
    if (n.includes('bebek') || n.includes('çocuk')) return 'Bebek & Çocuk';
    if (n.includes('kolye ucu')) return 'Kolye Ucu'; 
    if (n.includes('yüzük')) return 'Yüzük';
    if (n.includes('kolye')) return 'Kolye';
    if (n.includes('küpe')) return 'Küpe';
    if (n.includes('bileklik')) return 'Bileklik';
    if (n.includes('bilezik')) return 'Bilezik';
    if (n.includes('set')) return 'Set';
    if (n.includes('alyans')) return 'Alyans';
    return 'Diğer';
  };

  useEffect(() => {
    if (!sube || sube === 'Bilinmeyen Şube') return;
    const q = query(collection(db, `subeler/${sube}/urunler`));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const rawData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          realBarkod: doc.id 
      }));
      
      const uniqueData = Array.from(new Map(rawData.map(item => [item.id, item])).values());

      uniqueData.sort((a, b) => {
          const timeA = a.timestamp?.seconds || 0;
          const timeB = b.timestamp?.seconds || 0;
          if (timeB !== timeA) return timeB - timeA;
          return (a.isim || "").localeCompare(b.isim || "");
      });

      setUrunler(uniqueData);
      setFilteredUrunler(uniqueData); 
    }, (err) => setError(`Veri hatası: ${err.message}`));
    
    return () => unsubscribe();
  }, [sube]);

  useEffect(() => {
    urunlerRef.current = urunler;
  }, [urunler]);

  /** Web sekmesi açıkken Firestore’daki İkas senkron alanlarını canlı taşı. */
  useEffect(() => {
    if (!openWebsite || !selectedUrun) return;
    const key = String(selectedUrun.id || selectedUrun.barkod || '').trim();
    if (!key) return;
    const row = urunler.find(
      (u) => String(u.id || '').trim() === key || String(u.barkod || '').trim() === key
    );
    if (!row) return;
    setEditForm((prev) => {
      if (!prev) return prev;
      const nextIkas = {
        ikasSyncStatus: row.ikasSyncStatus,
        ikasLastError: row.ikasLastError,
        ikasProductId: row.ikasProductId,
        ikasVariantId: row.ikasVariantId,
        ikasLastSyncedAt: row.ikasLastSyncedAt,
        ikasLastCheckedAt: row.ikasLastCheckedAt,
        ikasStockSyncStatus: row.ikasStockSyncStatus,
        ikasStockLastError: row.ikasStockLastError,
        ikasPriceSyncStatus: row.ikasPriceSyncStatus,
        ikasPriceLastError: row.ikasPriceLastError,
        ikasPriceLastSyncedAt: row.ikasPriceLastSyncedAt
      };
      if (
        prev.ikasSyncStatus === nextIkas.ikasSyncStatus &&
        prev.ikasLastError === nextIkas.ikasLastError &&
        prev.ikasProductId === nextIkas.ikasProductId &&
        prev.ikasVariantId === nextIkas.ikasVariantId &&
        prev.ikasLastSyncedAt === nextIkas.ikasLastSyncedAt &&
        prev.ikasLastCheckedAt === nextIkas.ikasLastCheckedAt &&
        prev.ikasStockSyncStatus === nextIkas.ikasStockSyncStatus &&
        prev.ikasStockLastError === nextIkas.ikasStockLastError &&
        prev.ikasPriceSyncStatus === nextIkas.ikasPriceSyncStatus &&
        prev.ikasPriceLastError === nextIkas.ikasPriceLastError &&
        prev.ikasPriceLastSyncedAt === nextIkas.ikasPriceLastSyncedAt
      ) {
        return prev;
      }
      return { ...prev, ...nextIkas };
    });
  }, [urunler, openWebsite, selectedUrun]);

  /** Ürün modalı açıkken Firestore’daki Trendyol alanlarını canlı taşı. */
  useEffect(() => {
    if (!openModal || !selectedUrun) return;
    const key = String(selectedUrun.id || selectedUrun.barkod || '').trim();
    if (!key) return;
    const row = urunler.find(
      (u) => String(u.id || '').trim() === key || String(u.barkod || '').trim() === key
    );
    if (!row) return;
    setEditForm((prev) => {
      if (!prev) return prev;
      const nextTy = {
        trendyolSyncStatus: row.trendyolSyncStatus,
        trendyolLastError: row.trendyolLastError,
        trendyolBatchRequestId: row.trendyolBatchRequestId,
        trendyolLastSubmittedAt: row.trendyolLastSubmittedAt,
        trendyolLastBatchRequestId: row.trendyolLastBatchRequestId,
        trendyolLastProductId: row.trendyolLastProductId,
        trendyolLastListingId: row.trendyolLastListingId,
        ty_listingStatus: row.ty_listingStatus,
        ty_lastStatusError: row.ty_lastStatusError,
        ty_productUrl: row.ty_productUrl,
        ty_matchedProductUrl: row.ty_matchedProductUrl,
        ty_listingUrl: row.ty_listingUrl,
        ty_matchedUrl: row.ty_matchedUrl,
        ty_syncMode: row.ty_syncMode,
        ty_barcode: row.ty_barcode,
        ty_stockCode: row.ty_stockCode,
        ty_matchedBarcode: row.ty_matchedBarcode,
        ty_matchedStockCode: row.ty_matchedStockCode,
        ty_matchedProductId: row.ty_matchedProductId,
        ty_matchedProductName: row.ty_matchedProductName,
        ty_platformListingId: row.ty_platformListingId,
        ty_productMainId: row.ty_productMainId,
        ty_productCode: row.ty_productCode
      };
      if (
        prev.trendyolSyncStatus === nextTy.trendyolSyncStatus &&
        prev.trendyolLastError === nextTy.trendyolLastError &&
        prev.trendyolBatchRequestId === nextTy.trendyolBatchRequestId &&
        prev.trendyolLastSubmittedAt === nextTy.trendyolLastSubmittedAt &&
        prev.trendyolLastBatchRequestId === nextTy.trendyolLastBatchRequestId &&
        prev.trendyolLastProductId === nextTy.trendyolLastProductId &&
        prev.trendyolLastListingId === nextTy.trendyolLastListingId &&
        prev.ty_listingStatus === nextTy.ty_listingStatus &&
        prev.ty_lastStatusError === nextTy.ty_lastStatusError &&
        prev.ty_productUrl === nextTy.ty_productUrl &&
        prev.ty_matchedProductUrl === nextTy.ty_matchedProductUrl &&
        prev.ty_listingUrl === nextTy.ty_listingUrl &&
        prev.ty_matchedUrl === nextTy.ty_matchedUrl &&
        prev.ty_syncMode === nextTy.ty_syncMode &&
        prev.ty_barcode === nextTy.ty_barcode &&
        prev.ty_stockCode === nextTy.ty_stockCode &&
        prev.ty_matchedBarcode === nextTy.ty_matchedBarcode &&
        prev.ty_matchedStockCode === nextTy.ty_matchedStockCode &&
        prev.ty_matchedProductId === nextTy.ty_matchedProductId &&
        prev.ty_matchedProductName === nextTy.ty_matchedProductName &&
        prev.ty_platformListingId === nextTy.ty_platformListingId &&
        prev.ty_productMainId === nextTy.ty_productMainId &&
        prev.ty_productCode === nextTy.ty_productCode
      ) {
        return prev;
      }
      return { ...prev, ...nextTy };
    });
  }, [urunler, openModal, selectedUrun]);

  useEffect(() => {
    if (openWebsite) {
      setWebsiteSaveBanner(null);
      setWebsiteSeoGenBanner(null);
    }
  }, [openWebsite]);

  useEffect(() => {
    if (!websiteSaveBanner) return undefined;
    if (websiteBannerTimerRef.current) {
      clearTimeout(websiteBannerTimerRef.current);
    }
    websiteBannerTimerRef.current = setTimeout(() => {
      setWebsiteSaveBanner(null);
      websiteBannerTimerRef.current = null;
    }, 28000);
    return () => {
      if (websiteBannerTimerRef.current) {
        clearTimeout(websiteBannerTimerRef.current);
        websiteBannerTimerRef.current = null;
      }
    };
  }, [websiteSaveBanner]);

  useEffect(() => {
    if (!websiteSeoGenBanner) return undefined;
    if (websiteSeoGenBannerTimerRef.current) {
      clearTimeout(websiteSeoGenBannerTimerRef.current);
    }
    websiteSeoGenBannerTimerRef.current = setTimeout(() => {
      setWebsiteSeoGenBanner(null);
      websiteSeoGenBannerTimerRef.current = null;
    }, 7500);
    return () => {
      if (websiteSeoGenBannerTimerRef.current) {
        clearTimeout(websiteSeoGenBannerTimerRef.current);
        websiteSeoGenBannerTimerRef.current = null;
      }
    };
  }, [websiteSeoGenBanner]);

  useEffect(() => {
    let mounted = true;
    const loadIkasCategories = async () => {
      try {
        const response = await fetch(apiUrl('/api/ikas/categories'), {
          method: 'GET',
          headers: await getBackendAuthHeaders()
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data?.success) return;
        const rows = Array.isArray(data?.categories) ? data.categories : [];
        if (mounted) setIkasCategories(rows);
      } catch (e) {
        // Sessiz fallback: sekmeler sabit liste ile devam eder.
      }
    };
    loadIkasCategories();
    return () => {
      mounted = false;
    };
  }, []);

  const ikasCategoryIdToName = useMemo(() => {
    const map = {};
    ikasCategories.forEach((cat) => {
      const id = String(cat?.id || '').trim();
      if (!id) return;
      map[id] = String(cat?.name || '').trim();
    });
    return map;
  }, [ikasCategories]);

  const ikasCategoryChildrenMap = useMemo(() => {
    const m = {};
    ikasCategories.forEach((c) => {
      const id = String(c?.id || '').trim();
      const pid = String(c?.parentId || '').trim();
      if (!id || !pid) return;
      if (!m[pid]) m[pid] = [];
      m[pid].push(id);
    });
    return m;
  }, [ikasCategories]);

  const getIkasCategoryExpandedIds = useCallback(
    (rootId) => {
      const rid = String(rootId || '').trim();
      if (!rid) return new Set();
      const out = new Set([rid]);
      const stack = [...(ikasCategoryChildrenMap[rid] || [])];
      while (stack.length) {
        const id = stack.pop();
        if (out.has(id)) continue;
        out.add(id);
        (ikasCategoryChildrenMap[id] || []).forEach((ch) => stack.push(ch));
      }
      return out;
    },
    [ikasCategoryChildrenMap]
  );

  const ikasRootCategories = useMemo(() => {
    const byId = new Map(
      ikasCategories.map((c) => [String(c?.id || '').trim(), c]).filter(([id]) => !!id)
    );
    return ikasCategories.filter((c) => {
      const pid = String(c?.parentId || '').trim();
      return !pid || !byId.has(pid);
    });
  }, [ikasCategories]);

  const flattenDescendantCategoryRows = useCallback(
    (rootId) => {
      const rows = [];
      const walk = (parentId, depth) => {
        (ikasCategoryChildrenMap[parentId] || []).forEach((cid) => {
          rows.push({
            id: cid,
            depth,
            name: String(ikasCategoryIdToName[cid] || cid).trim()
          });
          walk(cid, depth + 1);
        });
      };
      walk(rootId, 0);
      return rows;
    },
    [ikasCategoryChildrenMap, ikasCategoryIdToName]
  );

  const categoryTabs = useMemo(() => {
    if (!ikasCategories.length) {
      return altKategoriler.map((name) => ({ value: name, label: name }));
    }
    return [
      { value: 'Tümü', label: 'Tümü' },
      ...ikasCategories
        .map((cat) => ({
          value: `IKAS:${String(cat?.id || '').trim()}`,
          label: String(cat?.name || '').trim()
        }))
        .filter((tab) => !!tab.label && !!tab.value.replace('IKAS:', ''))
    ];
  }, [ikasCategories]);

  useEffect(() => {
    if (!categoryTabs.some((tab) => tab.value === selectedTab)) {
      setSelectedTab('Tümü');
    }
  }, [categoryTabs, selectedTab]);

  const webKategoriEtiketleri = useMemo(() => {
    const resolveNamesFromIds = (ids) =>
      (Array.isArray(ids) ? ids : [])
        .map((id) => String(ikasCategoryIdToName[String(id || '').trim()] || '').trim())
        .filter(Boolean);

    const fromSelectedSnapshot = Array.isArray(selectedUrun?.ikasCategoryNamesSnapshot)
      ? selectedUrun.ikasCategoryNamesSnapshot.filter(Boolean)
      : [];
    if (fromSelectedSnapshot.length) return fromSelectedSnapshot;

    const fromEditSnapshot = Array.isArray(editForm?.ikasCategoryNamesSnapshot)
      ? editForm.ikasCategoryNamesSnapshot.filter(Boolean)
      : [];
    if (fromEditSnapshot.length) return fromEditSnapshot;

    const fromSelectedIds = resolveNamesFromIds(selectedUrun?.ikasCategoryIds);
    if (fromSelectedIds.length) return fromSelectedIds;

    const fromEditIds = resolveNamesFromIds(editForm?.ikasCategoryIds);
    if (fromEditIds.length) return fromEditIds;

    const fromSelectedLegacy = String(selectedUrun?.urunKategorisi || '').trim();
    if (fromSelectedLegacy) return [fromSelectedLegacy];

    const fromEditLegacy = String(editForm?.urunKategorisi || '').trim();
    if (fromEditLegacy) return [fromEditLegacy];

    return [];
  }, [selectedUrun, editForm, ikasCategoryIdToName]);

  useEffect(() => {
    if (!openWebsite) return;
    let mounted = true;
    const loadIkasConfig = async () => {
      try {
        const response = await fetch(apiUrl('/api/ikas/config'), {
          method: 'GET',
          headers: await getBackendAuthHeaders()
        });
        const data = await response.json();
        if (!mounted) return;
        if (response.ok && data?.success) {
          setIkasConfig(data.config || {});
        } else {
          setIkasConfig({});
        }
      } catch (e) {
        if (mounted) setIkasConfig({});
      }
    };
    loadIkasConfig();
    return () => {
      mounted = false;
    };
  }, [openWebsite]);

  const ikasPreflightMissing = useMemo(() => {
    const cfg = ikasConfig || {};
    const brandId = String(cfg?.brandId || cfg?.brandMap?.['UKBA GOLD'] || '').trim();
    const webSalesChannelId = String(cfg?.webSalesChannelId || cfg?.salesChannelMap?.web || '').trim();
    const mergedProduct = {
      ...(selectedUrun || {}),
      ...(editForm || {}),
      ws_websiteSatisFiyati: websiteForm?.websiteSatisFiyati ?? editForm?.ws_websiteSatisFiyati,
      siteFiyati: editForm?.siteFiyati ?? selectedUrun?.siteFiyati
    };
    const urunAdi = String(editForm?.isim || selectedUrun?.isim || '').trim();
    const barkodOrSku = String(editForm?.barkod || selectedUrun?.barkod || editForm?.stokKodu || selectedUrun?.stokKodu || '').trim();

    const missing = [];
    if (!brandId) missing.push('İkas entegrasyonu: Brand ID tanımlı değil');
    if (!webSalesChannelId) missing.push('İkas entegrasyonu: Web satış kanalı ID tanımlı değil');
    if (!urunAdi) missing.push('Ürün adı eksik');
    if (!barkodOrSku) missing.push('Barkod veya SKU eksik');

    const v = validateIkasReadyProduct(mergedProduct);
    if (!v.ok) {
      v.missingFields.forEach((x) => {
        if (!missing.includes(x)) missing.push(x);
      });
    }
    return missing;
  }, [ikasConfig, editForm, selectedUrun, websiteForm]);

  const validation = useMemo(() => validateIkasReadyProduct(editForm || {}), [editForm]);
  const uniqueMissingFields = useMemo(() => {
    const missingUiFields = validation.missingFields || [];
    const missingPreflightFields = ikasPreflightMissing || [];
    return [...new Set([...missingUiFields, ...missingPreflightFields])];
  }, [validation, ikasPreflightMissing]);
  const hasMissingFields = uniqueMissingFields.length > 0;
  const isIkasSendDisabled = hasMissingFields;

  const prevFilterKeyRef = useRef({ selectedTab: '', searchTerm: '' });

  useEffect(() => {
    let result = urunler;

    if (selectedTab !== 'Tümü') {
        if (selectedTab.startsWith('IKAS:')) {
            const ikasCategoryId = selectedTab.replace('IKAS:', '');
            const expandedIds = getIkasCategoryExpandedIds(ikasCategoryId);
            const ikasCategoryName = ikasCategoryIdToName[ikasCategoryId] || '';
            result = result.filter((u) => {
                const ids = Array.isArray(u?.ikasCategoryIds)
                  ? u.ikasCategoryIds.map((id) => String(id || '').trim()).filter(Boolean)
                  : [];
                if (ids.some((id) => expandedIds.has(id))) return true;
                const directId = String(u?.ikasCategoryId || '').trim();
                if (directId && expandedIds.has(directId)) return true;
                const ukbaCategory = normalizeCategoryText(u?.urunKategorisi || '');
                if (ukbaCategory) {
                  for (const cid of expandedIds) {
                    const nm = normalizeCategoryText(ikasCategoryIdToName[cid] || '');
                    if (nm && ukbaCategory === nm) return true;
                  }
                }
                return getAutoCategory(u?.isim) === ikasCategoryName;
            });
        } else {
            result = result.filter(u => getAutoCategory(u.isim) === selectedTab);
        }
    } else {
        result = result.filter(u => u.kategori !== 'Sarrafiye' && u.kategori !== 'Hurda' && u.kategori !== 'Döviz' && u.kategori !== 'Gram Altın');
    }

    if (searchTerm) {
        result = result.filter(u => matchesSearch(searchTerm, u.isim, u.barkod));
    }

    setFilteredUrunler(result);

    const prev = prevFilterKeyRef.current;
    const filterChanged = prev.selectedTab !== selectedTab || prev.searchTerm !== searchTerm;

    if (filterChanged) {
      setCurrentPage(1);
      setSelectedIds([]);
      prevFilterKeyRef.current = { selectedTab, searchTerm };
      return;
    }

    // Veri güncellendiyse mevcut sayfayı koru; yalnızca sayfa sayısı azaldıysa kırp.
    const pc = Math.max(1, Math.ceil(result.length / itemsPerPage));
    setCurrentPage((p) => (p > pc ? pc : p));
  }, [selectedTab, searchTerm, urunler, ikasCategoryIdToName, getIkasCategoryExpandedIds, itemsPerPage]);

  // --- SAYFALAMA HESAPLAMASI ---
  const pageCount = Math.ceil(filteredUrunler.length / itemsPerPage);
  const offset = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredUrunler.slice(offset, offset + itemsPerPage);

  // --- TOPLU İŞLEM FONKSİYONLARI ---

  const handleSelectAllClick = (event) => {
    const currentPageIds = currentItems.map(n => n.id);
    if (event.target.checked) {
      const newSelecteds = Array.from(new Set([...selectedIds, ...currentPageIds]));
      setSelectedIds(newSelecteds);
    } else {
      const newSelecteds = selectedIds.filter(id => !currentPageIds.includes(id));
      setSelectedIds(newSelecteds);
    }
  };

  const handleSelectAllFilteredProducts = () => {
    setSelectedIds(filteredUrunler.map((u) => u.id));
  };

  const handleClearProductSelection = () => {
    setSelectedIds([]);
  };

  const handleClick = (event, id) => {
    event.stopPropagation();
    const selectedIndex = selectedIds.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedIds, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedIds.slice(1));
    } else if (selectedIndex === selectedIds.length - 1) {
      newSelected = newSelected.concat(selectedIds.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedIds.slice(0, selectedIndex),
        selectedIds.slice(selectedIndex + 1),
      );
    }
    setSelectedIds(newSelected);
  };

  const numSelectedOnPage = currentItems.filter(n => selectedIds.includes(n.id)).length;
  const isPageAllSelected = currentItems.length > 0 && numSelectedOnPage === currentItems.length;
  const isPageIndeterminate = numSelectedOnPage > 0 && numSelectedOnPage < currentItems.length;

  const handleBulkInputChange = (e) => {
    const { name, value } = e.target;
    setBulkForm((prev) => {
      const next = {
        ...prev,
        [name]: value,
        ...(name === 'magazaListe' || name === 'magazaIndirimli' ? { bulkMagazaAuto: false } : {}),
        ...(name === 'webListe' || name === 'webIndirimli' ? { bulkWebAuto: false } : {}),
        ...(name === 'pazarListe' || name === 'pazarIndirimli' ? { bulkPazarAuto: false } : {}),
        ...(name === 'webIndirimli' ? { webIndirimliAutoDraft: false } : {})
      };
      if (name === 'iscilik' && String(value || '').trim()) next.adetIscilik = '';
      if (name === 'adetIscilik' && String(value || '').trim()) next.iscilik = '';

      // Toplu Web: liste manuel girilip indirimli boş bırakıldıysa, TL/% indirimden otomatik indirimli hesapla.
      // Kullanıcı indirimli alanına değer girdiyse üzerine yazma.
      const webListeNum = safeParseFloat(next.webListe);
      const webIndirimliRaw = String(next.webIndirimli || '').trim();
      const ekIndirimNum = safeParseFloat(next.ekIndirim);
      const indirimYuzdeNum = safeParseFloat(next.indirimYuzde);
      const hasAnyDiscount = ekIndirimNum > 0 || indirimYuzdeNum > 0;
      const isWebRelatedInput =
        name === 'webListe' || name === 'ekIndirim' || name === 'indirimYuzde';

      if (
        isWebRelatedInput &&
        (webIndirimliRaw === '' || next.webIndirimliAutoDraft) &&
        webListeNum > 0 &&
        hasAnyDiscount
      ) {
        const autoWebIndirimli = Math.max(0, webListeNum - ekIndirimNum - (webListeNum * indirimYuzdeNum) / 100);
        next.webIndirimli = formatAutoPrice(autoWebIndirimli);
        next.webIndirimliAutoDraft = true;
      } else if (
        isWebRelatedInput &&
        next.webIndirimliAutoDraft &&
        (!hasAnyDiscount || !(webListeNum > 0))
      ) {
        // Otomatik hesap kaynak girdileri temizlendiyse indirimli kutuyu da boşalt.
        next.webIndirimli = '';
        next.webIndirimliAutoDraft = false;
      }

      return next;
    });
  };

  const handleCloseBulkModal = () => {
    setOpenBulkModal(false);
    setBulkForm(createInitialBulkForm());
    setBulkTrendyolCategoryLeaf(null);
    setBulkTyCategoryInput('');
    setBulkTyCategoryOptions([]);
    setBulkTyCategoryError('');
    setBulkTyCategoryLoading(false);
  };

  const handleBulkSave = async () => {
    if (!window.confirm(`${selectedIds.length} adet ürünü güncellemek istediğinize emin misiniz?`)) return;

    try {
      const batch = writeBatch(db);

      const bulkCatIds = Array.isArray(bulkForm.ikasCategoryIds)
        ? bulkForm.ikasCategoryIds.map((id) => String(id || '').trim()).filter(Boolean)
        : [];

      const hasRawValue = (val) => String(val ?? '').trim() !== '';

      const shared = {};
      if (String(bulkForm.iscilik || '').trim()) {
        shared.iscilik = bulkForm.iscilik;
        shared.adetIscilik = null;
      } else if (String(bulkForm.adetIscilik || '').trim()) {
        shared.adetIscilik = bulkForm.adetIscilik;
        shared.iscilik = null;
      }
      if (String(bulkForm.stok || '').trim()) shared.stok = bulkForm.stok;
      if (!ikasCategories.length && String(bulkForm.urunKategorisi || '').trim()) {
        shared.urunKategorisi = String(bulkForm.urunKategorisi || '').trim();
      }

      const hasWebDetailWork =
        String(bulkForm.kkKomisyonu || '').trim() ||
        String(bulkForm.kdvOrani || '').trim() ||
        String(bulkForm.ekMasraf || '').trim() ||
        String(bulkForm.kargoBedeli || '').trim() ||
        String(bulkForm.ekIndirim || '').trim() ||
        String(bulkForm.indirimYuzde || '').trim();

      const hasTrendyolDetailWork =
        hasRawValue(bulkForm.tyKomisyonOrani) ||
        hasRawValue(bulkForm.tyKargoUcreti) ||
        hasRawValue(bulkForm.tyEkMasraf) ||
        hasRawValue(bulkForm.tyKarMarji) ||
        hasRawValue(bulkForm.tyKampanyaTipi);

      const hasTrendyolCatalogBulkWork =
        hasRawValue(bulkForm.ty_vatRate) ||
        hasRawValue(bulkForm.ty_dimensionalWeight) ||
        hasRawValue(bulkForm.ty_cargoCompanyId) ||
        hasRawValue(bulkForm.ty_deliveryDuration) ||
        Boolean(bulkTrendyolCategoryLeaf && bulkTyFormatCategoryId(bulkTrendyolCategoryLeaf.id));

      const hasPriceWork =
        bulkForm.bulkMagazaAuto ||
        bulkForm.bulkWebAuto ||
        bulkForm.bulkPazarAuto ||
        String(bulkForm.magazaListe || '').trim() ||
        String(bulkForm.magazaIndirimli || '').trim() ||
        String(bulkForm.webListe || '').trim() ||
        String(bulkForm.webIndirimli || '').trim() ||
        String(bulkForm.pazarListe || '').trim() ||
        String(bulkForm.pazarIndirimli || '').trim();

      if (
        Object.keys(shared).length === 0 &&
        !hasPriceWork &&
        !hasWebDetailWork &&
        !hasTrendyolDetailWork &&
        !hasTrendyolCatalogBulkWork &&
        !(ikasCategories.length > 0 && bulkCatIds.length > 0)
      ) {
        alert('Lütfen en az bir alanı doldurun veya otomatik fiyat seçin.');
        return;
      }

      const applyMagazaPatch = (urun, patch) => {
        if (bulkForm.bulkMagazaAuto) {
          const mStr = computeBulkFormulaMagazaStr(urun);
          if (mStr == null) return;
          const mm = safeParseFloat(mStr);
          patch.mg_satisFiyati = mStr;
          patch.magazaFiyati = mm;
          patch.mg_ekIndirim = '0';
          patch.mg_indirimYuzde = '0';
          patch.isManualMagaza = false;
          return;
        }
        const ml = String(bulkForm.magazaListe || '').trim();
        const mi = String(bulkForm.magazaIndirimli || '').trim();
        if (!ml && !mi) return;
        if (ml && mi) {
          const L = safeParseFloat(ml);
          const I = safeParseFloat(mi);
          patch.mg_satisFiyati = ml;
          patch.magazaFiyati = I;
          patch.mg_ekIndirim = String(Math.max(0, L - I));
          patch.mg_indirimYuzde = '0';
          patch.isManualMagaza = true;
        } else if (ml) {
          const L = safeParseFloat(ml);
          patch.mg_satisFiyati = ml;
          patch.magazaFiyati = L;
          patch.mg_ekIndirim = '0';
          patch.mg_indirimYuzde = '0';
          patch.isManualMagaza = true;
        } else if (mi) {
          patch.magazaFiyati = safeParseFloat(mi);
          patch.isManualMagaza = true;
        }
      };

      const refMagazaListeNum = (urun, patch) => {
        if (patch.mg_satisFiyati != null && String(patch.mg_satisFiyati).trim() !== '') {
          return safeParseFloat(patch.mg_satisFiyati);
        }
        if (bulkForm.bulkMagazaAuto) {
          const s = computeBulkFormulaMagazaStr(urun);
          return s != null ? safeParseFloat(s) : 0;
        }
        if (String(bulkForm.magazaListe || '').trim()) return safeParseFloat(bulkForm.magazaListe);
        return safeParseFloat(urun.mg_satisFiyati ?? urun.magazaFiyati);
      };

      const applyCategoryPatch = (urun, patch) => {
        if (!ikasCategories.length || !bulkCatIds.length) return;
        const existingIds = Array.isArray(urun?.ikasCategoryIds)
          ? urun.ikasCategoryIds.map((id) => String(id || '').trim()).filter(Boolean)
          : [];
        const idsForCompute = bulkForm.bulkCategoryMerge
          ? [...new Set([...existingIds, ...bulkCatIds])]
          : bulkCatIds;
        const resolved = computeExpandedIkasCategories(idsForCompute, ikasCategories);
        patch.ikasCategoryIds = resolved.ikasCategoryIds;
        patch.ikasCategoryNamesSnapshot = resolved.ikasCategoryNamesSnapshot;
        patch.ikasCategoryId = String(resolved.ikasCategoryId || '').trim();
        patch.urunKategorisi = String(resolved.urunKategorisi || urun?.urunKategorisi || '').trim();
      };

      const applyWebSalesDetailPatch = (patch) => {
        if (hasRawValue(bulkForm.kkKomisyonu)) patch.mg_kkKomisyonu = String(bulkForm.kkKomisyonu).trim();
        if (hasRawValue(bulkForm.kdvOrani)) patch.mg_kdvOrani = String(bulkForm.kdvOrani).trim();
        if (hasRawValue(bulkForm.ekMasraf)) {
          const val = String(bulkForm.ekMasraf).trim();
          patch.mg_ekMasraf = val;
          patch.ws_ekMasraf = val;
        }
        if (hasRawValue(bulkForm.kargoBedeli)) {
          const val = String(bulkForm.kargoBedeli).trim();
          patch.mg_kargoBedeli = val;
          patch.ws_kargoUcreti = val;
        }
        if (hasRawValue(bulkForm.ekIndirim)) patch.mg_ekIndirim = String(bulkForm.ekIndirim).trim();
        if (hasRawValue(bulkForm.indirimYuzde)) patch.mg_indirimYuzde = String(bulkForm.indirimYuzde).trim();
      };

      const applyTrendyolBulkDetailPatch = (patch) => {
        if (hasTrendyolDetailWork) {
          if (hasRawValue(bulkForm.tyKomisyonOrani)) patch.ty_komisyonOrani = String(bulkForm.tyKomisyonOrani).trim();
          if (hasRawValue(bulkForm.tyKargoUcreti)) patch.ty_kargoUcreti = String(bulkForm.tyKargoUcreti).trim();
          if (hasRawValue(bulkForm.tyEkMasraf)) patch.ty_ekMasraf = String(bulkForm.tyEkMasraf).trim();
          if (hasRawValue(bulkForm.tyKarMarji)) patch.ty_karMarji = String(bulkForm.tyKarMarji).trim();
          const kt = String(bulkForm.tyKampanyaTipi || '').trim();
          if (kt !== '') {
            patch.ty_kampanyaTipi = kt;
            const kd = String(bulkForm.tyKampanyaDegeri ?? '').trim();
            patch.ty_kampanyaDegeri = kt === 'yok' ? '0' : kd !== '' ? kd : '0';
          }
        }
        /** Trendyol ürün (katalog) alanları — tekli TrendyolProductInfoForm ile aynı Firestore anahtarları */
        if (hasRawValue(bulkForm.ty_vatRate)) patch.ty_vatRate = String(bulkForm.ty_vatRate).trim();
        if (hasRawValue(bulkForm.ty_dimensionalWeight)) {
          const dwRaw = String(bulkForm.ty_dimensionalWeight).trim().replace(',', '.');
          const n = Number(dwRaw);
          if (Number.isFinite(n)) patch.ty_dimensionalWeight = String(n);
        }
        if (hasRawValue(bulkForm.ty_cargoCompanyId)) {
          const raw = String(bulkForm.ty_cargoCompanyId).trim();
          patch.ty_cargoCompanyId = /^\d+$/.test(raw) ? String(Number(raw)) : raw;
        }
        if (hasRawValue(bulkForm.ty_deliveryDuration)) patch.ty_deliveryDuration = String(bulkForm.ty_deliveryDuration).trim();
        const tyCatLeaf = bulkTrendyolCategoryLeaf;
        if (tyCatLeaf?.isLeaf && bulkTyFormatCategoryId(tyCatLeaf.id)) {
          patch.ty_categoryId = bulkTyFormatCategoryId(tyCatLeaf.id);
          patch.ty_categoryName = bulkTyToText(tyCatLeaf.name);
          patch.ty_categoryPath = bulkTyToText(tyCatLeaf.path || tyCatLeaf.name || tyCatLeaf.id);
        }
      };

      const applyWebPatch = (urun, patch) => {
        if (bulkForm.bulkWebAuto) {
          patch.ws_kampanyaTipi = 'yok';
          patch.ws_kampanyaDegeri = '0';
          const br = refMagazaListeNum(urun, patch);
          if (!(br > 0)) return;
          const siteStr = formatAutoPrice(br * 1.15);
          patch.ws_websiteSatisFiyati = siteStr;
          patch.siteFiyati = safeParseFloat(siteStr);
          patch.isManualSite = false;
          patch.priceSource = 'auto';
          return;
        }
        const wl = String(bulkForm.webListe || '').trim();
        const wi = String(bulkForm.webIndirimli || '').trim();
        if (!wl && !wi) return;
        if (wl && wi) {
          const wln = safeParseFloat(wl);
          const win = safeParseFloat(wi);
          patch.ws_websiteSatisFiyati = wl;
          patch.siteFiyati = win;
          if (!hasRawValue(bulkForm.ekIndirim) && !hasRawValue(bulkForm.indirimYuzde)) {
            patch.mg_ekIndirim = String(Math.max(0, wln - win));
            patch.mg_indirimYuzde = '0';
          }
        } else if (wl) {
          const wn = safeParseFloat(wl);
          patch.ws_websiteSatisFiyati = wl;
          patch.siteFiyati = wn;
        } else if (wi) {
          patch.siteFiyati = safeParseFloat(wi);
        }
        patch.isManualSite = true;
        patch.priceSource = 'manual';
      };

      const applyPazarPatch = (urun, patch) => {
        if (bulkForm.bulkPazarAuto) {
          const br = refMagazaListeNum(urun, patch);
          if (!(br > 0)) return;
          const pzStr = formatAutoPrice(br * 1.35);
          patch.ty_trendyolSatisFiyati = pzStr;
          patch.pazaryeriFiyati = safeParseFloat(pzStr);
          patch.ty_kampanyaTipi = 'yok';
          patch.ty_kampanyaDegeri = '0';
          patch.isManualPazaryeri = false;
          return;
        }
        const pl = String(bulkForm.pazarListe || '').trim();
        const pi = String(bulkForm.pazarIndirimli || '').trim();
        if (!pl && !pi) return;
        if (pl && pi) {
          patch.ty_trendyolSatisFiyati = pl;
          patch.pazaryeriFiyati = safeParseFloat(pi);
        } else if (pl) {
          const pn = safeParseFloat(pl);
          patch.ty_trendyolSatisFiyati = pl;
          patch.pazaryeriFiyati = pn;
        } else if (pi) {
          patch.pazaryeriFiyati = safeParseFloat(pi);
        }
        patch.ty_kampanyaTipi = 'yok';
        patch.ty_kampanyaDegeri = '0';
        patch.isManualPazaryeri = true;
      };

      let updateCount = 0;
      selectedIds.forEach((id) => {
        const urun = urunler.find((u) => String(u.id) === String(id));
        if (!urun) return;
        const patch = { ...shared };
        applyCategoryPatch(urun, patch);
        applyWebSalesDetailPatch(patch);
        applyTrendyolBulkDetailPatch(patch);
        if (hasPriceWork) {
          applyMagazaPatch(urun, patch);
          applyWebPatch(urun, patch);
          applyPazarPatch(urun, patch);
        }
        const keys = Object.keys(patch);
        if (keys.length === 0) return;
        const docRef = doc(db, `subeler/${sube}/urunler`, id);
        batch.update(docRef, patch);
        updateCount += 1;
      });

      if (updateCount === 0) {
        alert('Seçili ürünlerde güncellenecek kayıt bulunamadı (fiyat otomatiği bu ürünlerde formül uygulanamıyor olabilir).');
        return;
      }

      await batch.commit();
      setSuccess(`${updateCount} ürün başarıyla güncellendi.`);
      handleCloseBulkModal();
      setSelectedIds([]);
    } catch (err) {
      setError('Toplu güncelleme hatası: ' + err.message);
    }
  };

  // --- KOPYALAMA İŞLEMİ (CLIPBOARD) ---
  const handleCopyToClipboard = (text, label) => {
      navigator.clipboard.writeText(text);
      setSnackbarMessage(`${label} kopyalandı: ${text}`);
      setSnackbarOpen(true);
  };

  const getBackendAuthHeaders = async () => {
    const headers = {};
    const sharedSecret = safeString(process.env.REACT_APP_CAMPAIGN_AI_SHARED_SECRET).trim();
    if (sharedSecret) headers['x-ukba-internal-key'] = sharedSecret;
    try {
      const token = await auth.currentUser?.getIdToken?.();
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch (error) {
      // Token alınamazsa backend yapılandırmasına göre istek reddedilebilir.
    }
    return headers;
  };

  useEffect(() => {
    if (!openBulkModal) return undefined;
    const query = bulkTyCategoryInput.trim();
    if (query.length < 2) {
      setBulkTyCategoryOptions([]);
      setBulkTyCategoryError('');
      setBulkTyCategoryLoading(false);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setBulkTyCategoryLoading(true);
      setBulkTyCategoryError('');
      try {
        const fetchCategoryOptions = async (requestPath) => {
          const response = await fetch(apiUrl(requestPath), {
            method: 'GET',
            headers: await getBackendAuthHeaders()
          });
          const json = await bulkTyReadJsonSafely(response);
          if (!response.ok || json?.success === false) {
            const error = new Error(json?.error || json?.message || 'Kategori bilgileri alınamadı.');
            error.status = json?.status || json?.trendyolStatus || response.status;
            throw error;
          }
          return bulkTyNormalizeCategoryPayload(json);
        };

        let flattened = await fetchCategoryOptions(`/api/trendyol/categories?name=${encodeURIComponent(query)}`);

        const asciiQuery = bulkTyNormalizeSearchText(query);
        if (!flattened.length && asciiQuery && asciiQuery !== query) {
          flattened = await fetchCategoryOptions(`/api/trendyol/categories?name=${encodeURIComponent(asciiQuery)}`);
        }

        if (!flattened.length) {
          const allCategories = await fetchCategoryOptions('/api/trendyol/categories');
          flattened = bulkTyFilterCategoriesLocally(allCategories, query);
        }

        if (!cancelled) setBulkTyCategoryOptions(flattened);
      } catch (error) {
        if (!cancelled) {
          setBulkTyCategoryOptions([]);
          const statusText = error?.status ? ` Backend ${error.status} döndü.` : '';
          setBulkTyCategoryError(`Kategori bilgileri alınamadı.${statusText}`);
        }
      } finally {
        if (!cancelled) setBulkTyCategoryLoading(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [openBulkModal, bulkTyCategoryInput]);

  useEffect(() => {
    if (!openTrendyol) return undefined;
    let cancelled = false;
    setTrendyolMerchantCfg(null);
    setTrendyolMerchantCfgLoading(true);
    (async () => {
      try {
        const response = await fetch(apiUrl('/api/trendyol/config'), {
          method: 'GET',
          headers: await getBackendAuthHeaders()
        });
        const data = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (response.ok && data?.success && data?.config && typeof data.config === 'object') {
          setTrendyolMerchantCfg(data.config);
        } else {
          setTrendyolMerchantCfg({});
        }
      } catch {
        if (!cancelled) setTrendyolMerchantCfg({});
      } finally {
        if (!cancelled) setTrendyolMerchantCfgLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [openTrendyol]);

  const resolvedTrendyolBrandForSendButton = useMemo(
    () => resolveTrendyolBrandIdForSend(trendyolProductInfo?.ty_brandId, trendyolMerchantCfg || {}),
    [trendyolProductInfo?.ty_brandId, trendyolMerchantCfg]
  );

  const trendyolMerchantCfgFetched = trendyolMerchantCfg !== null && !trendyolMerchantCfgLoading;

  const trendyolListingMatched = useMemo(() => isTrendyolProductMatched(editForm), [editForm]);

  const trendyolSendBlockedByBrand =
    !trendyolMerchantCfgFetched || !isTrendyolNumericBrandIdResolved(resolvedTrendyolBrandForSendButton);

  const postVerifyIkasRemoteProducts = async (productIds) => {
    const ids = (Array.isArray(productIds) ? productIds : [])
      .map((x) => String(x || '').trim())
      .filter(Boolean)
      .slice(0, 30);
    if (!sube || sube === 'Bilinmeyen Şube' || !ids.length) {
      return { res: { ok: false, status: 400 }, json: { success: false, message: 'Geçersiz istek' } };
    }
    const res = await fetch(apiUrl('/api/ikas/verify-remote-products'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await getBackendAuthHeaders())
      },
      body: JSON.stringify({ subeId: sube, productIds: ids })
    });
    let json = {};
    try {
      json = await res.json();
    } catch (e) {
      json = {};
    }
    return { res, json };
  };

  /** İkas ürün id’si olan kayıtlar; son kontrolden minIntervalMs geçtiyse veya hiç kontrol yoksa aday. */
  const pickProductIdsForAutoIkasRemoteVerify = (list, max = 30, minIntervalMs = 6 * 60 * 60 * 1000) => {
    const now = Date.now();
    const rows = (list || [])
      .map((u) => {
        const id = String(u?.id || u?.barkod || '').trim();
        const ikasPid = String(u?.ikasProductId || '').trim();
        if (!id || !ikasPid) return null;
        const ts = u?.ikasLastCheckedAt;
        let checkedMs = 0;
        if (ts) {
          const sec = ts.seconds != null ? ts.seconds : ts._seconds;
          if (sec != null) checkedMs = sec * 1000;
        }
        const neverChecked = !ts || (ts.seconds == null && ts._seconds == null);
        const due = neverChecked || now - checkedMs >= minIntervalMs;
        if (!due) return null;
        return { id, sortKey: neverChecked ? 0 : checkedMs };
      })
      .filter(Boolean);
    rows.sort((a, b) => a.sortKey - b.sortKey);
    return rows.slice(0, max).map((r) => r.id);
  };

  /** Şube / sayfa değişince: İkas’ta silinmiş bağlantıları arka planda düzelt (aynı ürün en sık 6 saatte bir). */
  useEffect(() => {
    if (!sube || sube === 'Bilinmeyen Şube') return undefined;
    let cancelled = false;
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    const run = async () => {
      await sleep(2800);
      if (cancelled) return;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        if (cancelled) return;
        if (!auth.currentUser) return;
        const list = urunlerRef.current || [];
        if (!list.length) {
          await sleep(2200);
          continue;
        }
        const ids = pickProductIdsForAutoIkasRemoteVerify(list, 30, 6 * 60 * 60 * 1000);
        if (!ids.length) return;
        try {
          const { res, json } = await postVerifyIkasRemoteProducts(ids);
          if (cancelled) return;
          if (!res.ok || json.success === false) return;
          const results = Array.isArray(json.results) ? json.results : [];
          const cleared = results.filter((r) => r.action === 'cleared_stale_link').length;
          if (cleared > 0) {
            setSnackbarMessage(
              `${cleared} ürün İkas’ta bulunamadı; bağlantı bilgisi güncellendi. (Liste açılırken otomatik kontrol)`
            );
            setSnackbarOpen(true);
          }
        } catch {
          // sessiz
        }
        return;
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [sube, location.key]);

  const handleOpenIkasPayloadPreview = async () => {
    const productId = String(selectedUrun?.barkod || selectedUrun?.id || '').trim();
    const subeId = String(selectedUrun?.sube || selectedUrun?.subeId || sube || '').trim();
    console.log('selectedUrun:', selectedUrun);
    console.log('subeId:', subeId);
    console.log('productId:', productId);
    if (hasMissingFields) {
      const lines = uniqueMissingFields.map((m) => `• ${m}`).join('\n');
      setSnackbarMessage(
        `İkas gönderimi için eksik alanlar var. Ürünü kaydedebilirsiniz ancak eksikler tamamlanmadan İkas’a gönderilemez.\n${lines}`
      );
      setSnackbarOpen(true);
      return;
    }
    if (!productId) {
      setSnackbarMessage('Ürün kimliği bulunamadı. Önizleme açılamadı.');
      setSnackbarOpen(true);
      return;
    }
    if (!subeId || subeId === 'Bilinmeyen Şube') {
      setSnackbarMessage('Şube bilgisi bulunamadı. Önizleme açılamadı.');
      setSnackbarOpen(true);
      return;
    }

    setIkasPreviewLoading(true);
    setIkasPreviewPayload(null);
    setOpenIkasPreview(true);
    try {
      if (!subeId || !productId) return;
      const url = apiUrl(
        `/api/ikas/products/${encodeURIComponent(subeId)}/${encodeURIComponent(productId)}/payload-preview`
      );
      console.log('IKAS PREVIEW URL:', url);
      const response = await fetch(url, {
        method: 'GET',
        headers: await getBackendAuthHeaders()
      });

      const responseText = await response.text();
      let parsed = {};
      try {
        parsed = responseText ? JSON.parse(responseText) : {};
      } catch (parseError) {
        throw new Error('Sunucudan geçersiz yanıt alındı.');
      }

      if (!response.ok || parsed?.success !== true) {
        throw new Error(parsed?.message || 'İkas paket önizlemesi alınamadı.');
      }

      setIkasPreviewPayload(parsed.payload || {});
    } catch (err) {
      const message = err?.message || 'Önizleme sırasında hata oluştu.';
      setIkasPreviewPayload({ error: message });
      setSnackbarMessage(message);
      setSnackbarOpen(true);
    } finally {
      setIkasPreviewLoading(false);
    }
  };

  const handleOpenTrendyolPayloadPreview = async () => {
    setOpenTrendyolPayloadPreviewDialog(true);
    const productId = String(selectedUrun?.barkod || selectedUrun?.id || '').trim();
    const subeId = String(selectedUrun?.sube || selectedUrun?.subeId || sube || '').trim();
    if (!productId) {
      setTrendyolPreviewError('Ürün kimliği bulunamadı. Paket önizleme açılamadı.');
      setTrendyolPreviewResult(null);
      return;
    }
    if (!subeId || subeId === 'Bilinmeyen Şube') {
      setTrendyolPreviewError('Şube bilgisi bulunamadı. Paket önizleme açılamadı.');
      setTrendyolPreviewResult(null);
      return;
    }

    setTrendyolPreviewLoading(true);
    setTrendyolPreviewError('');
    setTrendyolPreviewResult(null);
    try {
      const baseSource = { ...(selectedUrun || {}), ...(editForm || {}) };
      const tyPatchForPreview = buildTrendyolProductFirestorePatch(trendyolProductInfo, baseSource);
      const resolvedBrandPreview = resolveTrendyolBrandIdForSend(
        trendyolProductInfo?.ty_brandId,
        trendyolMerchantCfg || {}
      );
      const productOverlay = Object.fromEntries(
        Object.entries({
          ...tyPatchForPreview,
          ...(isTrendyolNumericBrandIdResolved(resolvedBrandPreview)
            ? { ty_brandId: String(Math.trunc(Number(resolvedBrandPreview))) }
            : {})
        }).filter(([, v]) => v !== undefined && v !== null)
      );
      const response = await fetch(apiUrl('/api/trendyol/product-payload-preview'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await getBackendAuthHeaders())
        },
        body: JSON.stringify({
          subeId,
          productId,
          ...(Object.keys(productOverlay).length ? { productOverlay } : {})
        })
      });
      const responseText = await response.text();
      let parsed = {};
      try {
        parsed = responseText ? JSON.parse(responseText) : {};
      } catch (parseError) {
        throw new Error('Sunucudan geçersiz yanıt alındı.');
      }
      if (!response.ok || parsed?.success !== true) {
        throw new Error(parsed?.message || parsed?.error || 'Trendyol paket önizlemesi alınamadı.');
      }
      setTrendyolPreviewResult(parsed);
    } catch (err) {
      setTrendyolPreviewError(err?.message || 'Trendyol paket önizlemesi alınamadı.');
    } finally {
      setTrendyolPreviewLoading(false);
    }
  };

  const handleTrendyolSaveProduct = async () => {
    const productId = String(selectedUrun?.barkod || selectedUrun?.id || '').trim();
    const subeIdLocal = String(selectedUrun?.sube || selectedUrun?.subeId || sube || '').trim();
    const matchedTy = String(editForm?.ty_syncMode || '').trim().toLowerCase() === 'matched';
    if (!productId) {
      setTrendyolSaveError('Ürün kimliği bulunamadı.');
      setTrendyolSaveResult(null);
      return;
    }
    if (!subeIdLocal || subeIdLocal === 'Bilinmeyen Şube') {
      setTrendyolSaveError('Şube bilgisi bulunamadı.');
      setTrendyolSaveResult(null);
      return;
    }

    const resolvedTyBrandSend = resolveTrendyolBrandIdForSend(trendyolProductInfo?.ty_brandId, trendyolMerchantCfg || {});
    if (
      !matchedTy &&
      (!trendyolMerchantCfgFetched || !isTrendyolNumericBrandIdResolved(resolvedTyBrandSend))
    ) {
      setTrendyolSaveError('Geçerli Trendyol Marka ID yok.');
      setTrendyolSaveResult(null);
      return;
    }

    setTrendyolSaveLoading(true);
    setTrendyolSaveError('');
    setTrendyolSaveResult(null);
    try {
      const { res, json: parsed } = await postTrendyolSaveProductRequest(productId, subeIdLocal);
      if (!res.ok) {
        const fallback = parsed?.message || parsed?.error || `İstek başarısız (${res.status})`;
        throw new Error(fallback);
      }
      setTrendyolSaveResult(parsed);
    } catch (err) {
      setTrendyolSaveError(err?.message || 'Trendyol gönderimi sırasında hata oluştu.');
      setTrendyolSaveResult(null);
    } finally {
      setTrendyolSaveLoading(false);
    }
  };

  const findIkasCategoryByName = (categoryName) => {
    const target = normalizeCategoryText(categoryName);
    if (!target) return null;
    return ikasCategories.find((cat) => normalizeCategoryText(cat?.name) === target) || null;
  };

  /** Tek ürün + toplu düzenleme: İkas kategori seçimini üst kategori zinciriyle genişletir. */
  const computeExpandedIkasCategories = (categoryIdsInput, categoriesList) => {
    const normalizeId = (v) => String(v || '').trim();
    const normalizeName = (v) =>
      String(v || '')
        .trim()
        .toLocaleLowerCase('tr-TR')
        .replace(/\s+/g, ' ');
    const list = Array.isArray(categoriesList) ? categoriesList : [];
    const byId = new Map(
      list.map((cat) => [
        normalizeId(cat?.id),
        {
          id: normalizeId(cat?.id),
          name: String(cat?.name || '').trim(),
          parentId: normalizeId(cat?.parentId)
        }
      ])
    );
    const seedIds = Array.isArray(categoryIdsInput)
      ? categoryIdsInput.map(normalizeId)
      : [normalizeId(categoryIdsInput)];
    const selectedSet = new Set(seedIds.filter(Boolean));

    const addParents = (id) => {
      const visited = new Set();
      let currentId = id;
      while (currentId && !visited.has(currentId)) {
        visited.add(currentId);
        const row = byId.get(currentId);
        const parentId = normalizeId(row?.parentId);
        if (!parentId || !byId.has(parentId)) break;
        selectedSet.add(parentId);
        currentId = parentId;
      }
    };

    [...selectedSet].forEach(addParents);

    const manualParentByChildName = {
      'bebek & çocuk küpe': 'bebek & çocuk',
      'bebek & çocuk künye': 'bebek & çocuk',
      'kolye ucu': 'kolye',
      zincir: 'kolye',
      'harf kolye': 'kolye',
      'tuğrali kolye': 'kolye',
      'tuğralı kolye': 'kolye',
      'külçe kolye': 'kolye',
      'tasarim kolye': 'kolye',
      'tasarım kolye': 'kolye',
      'minimal yüzük': 'yüzük',
      'tasarim yüzük': 'yüzük',
      'tasarım yüzük': 'yüzük',
      'minimal bileklik': 'bileklik',
      'top küpe': 'küpe'
    };
    const idByName = new Map(
      [...byId.values()].map((row) => [normalizeName(row?.name), row?.id]).filter(([name, id]) => !!name && !!id)
    );
    [...selectedSet].forEach((id) => {
      const childName = normalizeName(byId.get(id)?.name);
      const parentName = manualParentByChildName[childName];
      const parentId = parentName ? idByName.get(parentName) : '';
      if (parentId) selectedSet.add(parentId);
    });
    [...selectedSet].forEach(addParents);

    const finalIds = [...selectedSet];
    const finalNames = finalIds
      .map((id) => byId.get(id)?.name || '')
      .map((x) => String(x || '').trim())
      .filter(Boolean);

    return {
      ikasCategoryIds: finalIds,
      ikasCategoryNamesSnapshot: finalNames,
      ikasCategoryId: finalIds[0] || '',
      urunKategorisi: finalNames[0] || ''
    };
  };

  const handleEditCategorySelect = (categoryIdsInput) => {
    const resolved = computeExpandedIkasCategories(categoryIdsInput, ikasCategories);
    setEditForm((prev) => ({
      ...prev,
      ikasCategoryIds: resolved.ikasCategoryIds,
      ikasCategoryNamesSnapshot: resolved.ikasCategoryNamesSnapshot,
      ikasCategoryId: resolved.ikasCategoryId,
      urunKategorisi: resolved.urunKategorisi || prev?.urunKategorisi || ''
    }));
  };

  const handleBulkCategorySelect = (categoryIdsInput) => {
    const resolved = computeExpandedIkasCategories(categoryIdsInput, ikasCategories);
    setBulkForm((prev) => ({
      ...prev,
      ikasCategoryIds: resolved.ikasCategoryIds,
      ikasCategoryNamesSnapshot: resolved.ikasCategoryNamesSnapshot,
      ikasCategoryId: resolved.ikasCategoryId,
      urunKategorisi: resolved.urunKategorisi
    }));
  };

  const handleOpenEdit = (urun) => {
    setSelectedUrun(urun);
    const kullanilacakKur = guncelSistemKuru > 0 ? guncelSistemKuru : (safeParseFloat(urun.hasAltinKuru) || 0);
    const existingIds = Array.isArray(urun?.ikasCategoryIds)
      ? urun.ikasCategoryIds.map((id) => String(id || '').trim()).filter(Boolean)
      : [];
    const fallbackId = String(urun?.ikasCategoryId || '').trim();
    const fallbackByNameId = String(findIkasCategoryByName(urun?.urunKategorisi)?.id || '').trim();
    const resolvedIds = existingIds.length
      ? existingIds
      : fallbackId
        ? [fallbackId]
        : fallbackByNameId
          ? [fallbackByNameId]
          : [];
    const resolvedNames = resolvedIds
      .map((id) => String(ikasCategoryIdToName[id] || '').trim())
      .filter(Boolean);

    setEditForm({
      ...urun,
      ikasCategoryIds: resolvedIds,
      ikasCategoryNamesSnapshot: resolvedNames.length
        ? resolvedNames
        : Array.isArray(urun?.ikasCategoryNamesSnapshot)
          ? urun.ikasCategoryNamesSnapshot
          : [],
      ikasCategoryId: resolvedIds[0] || '',
      stokKodu: urun.stokKodu != null && urun.stokKodu !== '' ? urun.stokKodu : urun.barkod || '',
      hasAltinKuru: kullanilacakKur, 
      maliyetFiyati: urun.maliyetFiyati || '',
      magazaFiyati: urun.magazaFiyati || '',
      siteFiyati: urun.siteFiyati || '',
      pazaryeriFiyati: urun.pazaryeriFiyati || '',
      siteLinki: String(urun?.ikasProductId || '').trim() ? resolveUkbagoldProductUrl(urun) : '',
      isManualPazaryeri: urun.isManualPazaryeri || false,
      isManualSite: urun.isManualSite || false,
      isManualMagaza: urun.isManualMagaza || false,
      priceSource: urun.priceSource ?? (urun.isManualSite ? 'manual' : 'auto'),
      gorsel1: null,
      gorsel2: null,
      gorsel3: null,
      gorsel4: null,
      gorsel5: null,
      ty_gorsel1: null,
      ty_gorsel2: null,
      ty_gorsel3: null,
      ty_gorsel4: null,
      ty_gorsel5: null
    });
    
    setTrendyolForm({
        hasAltinKuru: kullanilacakKur, 
        hesaplamaYontemi: urun.ty_hesaplamaYontemi || 'Manuel',
        komisyonOrani: urun.ty_komisyonOrani || '',
        kargoUcreti: urun.ty_kargoUcreti || '',
        karMarji: urun.ty_karMarji || '',
        trendyolSatisFiyati: String(urun.ty_trendyolSatisFiyati ?? '').trim() !== ''
          ? String(urun.ty_trendyolSatisFiyati)
          : urun.pazaryeriFiyati != null && urun.pazaryeriFiyati !== ''
            ? String(urun.pazaryeriFiyati)
            : '',
        trendyolIndirimliSatisFiyati:
          urun.pazaryeriFiyati != null && urun.pazaryeriFiyati !== '' ? String(urun.pazaryeriFiyati) : '',
        ekMasraf: urun.ty_ekMasraf || '0',
        kampanyaTipi: urun.ty_kampanyaTipi || 'yok',
        kampanyaDegeri: urun.ty_kampanyaDegeri || '0'
    });
    setTrendyolProductInfo(normalizeTrendyolProductInfo(urun, urun));

    setWebsiteForm({
        hasAltinKuru: kullanilacakKur,
        websiteSatisFiyati: urun.ws_websiteSatisFiyati || '',
        kargoUcreti: urun.ws_kargoUcreti || '',
        ekMasraf: urun.ws_ekMasraf || '0',
        kampanyaTipi: urun.ws_kampanyaTipi || 'yok',
        kampanyaDegeri: urun.ws_kampanyaDegeri || '0'
    });

    setMagazaForm({
        satisFiyati: urun.mg_satisFiyati ?? urun.magazaFiyati ?? '',
        kkKomisyonu: urun.mg_kkKomisyonu ?? '0',
        kdvOrani: urun.mg_kdvOrani ?? '20',
        ekIndirim: urun.mg_ekIndirim ?? '0',
        indirimYuzde: urun.mg_indirimYuzde ?? '0',
        ekMasraf: urun.mg_ekMasraf ?? '0',
        kargoBedeli: urun.mg_kargoBedeli ?? '0'
    });

    setPreview1(urun.gorselURL1 || null);
    setPreview2(urun.gorselURL2 || null);
    setPreview3(urun.gorselURL3 || null);
    setPreview4(urun.gorselURL4 || null);
    setPreview5(urun.gorselURL5 || null);
    setTyPreview1(urun.ty_gorselURL1 || null);
    setTyPreview2(urun.ty_gorselURL2 || null);
    setTyPreview3(urun.ty_gorselURL3 || null);
    setTyPreview4(urun.ty_gorselURL4 || null);
    setTyPreview5(urun.ty_gorselURL5 || null);
    setError('');
    setSuccess('');
    setOpenModal(true);
  };

  const handleCloseModal = () => { setOpenModal(false); setSelectedUrun(null); };
  
  const handleEditChange = (e) => {
      const { name, value } = e.target;
      setEditForm((prev) => {
        const next = { ...prev, [name]: value };
        if (name === 'iscilik' && String(value || '').trim()) {
          next.adetIscilik = '';
        }
        if (name === 'adetIscilik' && String(value || '').trim()) {
          next.iscilik = '';
        }
        return next;
      });
  };

  const renderEditFormUrunTipiSelect = (label = 'Ürün tipi') => {
    if (!editForm) return null;
    const v = String(editForm.urunTipi || '').trim();
    const orphan = Boolean(v && !TAKI_URUN_TIPLERI.includes(v));
    return (
      <TextField
        select
        fullWidth
        label={label}
        name="urunTipi"
        value={editForm.urunTipi || ''}
        onChange={handleEditChange}
        size="small"
        sx={modalInputSx}
        SelectProps={{ displayEmpty: true }}
      >
        <MenuItem value="">
          <em>Seçiniz</em>
        </MenuItem>
        {TAKI_URUN_TIPLERI.map((t) => (
          <MenuItem key={t} value={t}>
            {t}
          </MenuItem>
        ))}
        {orphan ? (
          <MenuItem value={v}>{v} (kayıtlı)</MenuItem>
        ) : null}
      </TextField>
    );
  };

  const handleManualPriceChange = (e, type) => {
      const val = e.target.value;
      setEditForm((prev) => ({
        ...prev,
        [e.target.name]: val,
        ...(type !== undefined ? { [type]: true } : {}),
        ...(type === 'isManualSite' ? { priceSource: 'manual' } : {})
      }));

      if (e.target.name === 'pazaryeriFiyati') {
          setTrendyolForm(prev => ({ ...prev, trendyolIndirimliSatisFiyati: val }));
      }
  };

  const handleRevertAuto = (type) => {
      setEditForm((prev) => {
        if (type === 'isManualSite') {
          return { ...prev, isManualSite: false, priceSource: 'auto' };
        }
        return { ...prev, [type]: false };
      });
      if (type === 'isManualMagaza') {
          setMagazaForm(prev => ({
              ...prev,
              satisFiyati: editForm?.magazaFiyati != null ? String(editForm.magazaFiyati) : prev.satisFiyati
          }));
      }
  };

  const handleTrendyolChange = (e) => { 
      const { name, value } = e.target;
      setTrendyolForm(prev => ({ ...prev, [name]: value })); 
      if (name === 'trendyolIndirimliSatisFiyati') {
          setEditForm(prev => ({ ...prev, pazaryeriFiyati: value, isManualPazaryeri: true }));
          return;
      }
      if (name === 'trendyolSatisFiyati') {
          setEditForm(prev => ({ ...prev, isManualPazaryeri: true }));
      }
  };

  const handleMagazaChange = (e) => {
      const { name, value } = e.target;
      setMagazaForm(prev => ({ ...prev, [name]: value }));
      if (name === 'satisFiyati') {
          setEditForm(prev => ({ ...prev, isManualMagaza: true }));
      }
  };

  /** Web Site Ürün Detay sol panel — Web Satış Fiyatı (`ws_websiteSatisFiyati`) + varsa Satış (indirimli) (`siteFiyati`). */
  const handleWebsiteTabWebSatisChange = (e) => {
      const val = e.target.value;
      setWebsiteForm((prev) => ({ ...prev, websiteSatisFiyati: val }));
      setEditForm((prev) => ({
        ...prev,
        ws_websiteSatisFiyati: val,
        isManualSite: true,
        priceSource: 'manual'
      }));
  };

  const WEB_FILLABLE_FIELDS = [
    'aciklama',
    'ws_slug',
    'ws_seoBaslik',
    'ws_seoAciklama',
    'ws_seoAnahtarKelimeler',
    'ws_canonicalUrl'
  ];

  const isWebContentFieldEmpty = (form, key) => {
    const v = form?.[key];
    return v == null || String(v).trim() === '';
  };

  const handleFillMissingWebSeo = async () => {
    if (!editForm || webSeoFillLoading) return;
    const barkod = String(editForm.barkod || selectedUrun?.barkod || '').trim();
    if (!barkod || !sube || sube === 'Bilinmeyen Şube') {
      setWebsiteSeoGenBanner({
        severity: 'error',
        message: 'Şube veya barkod eksik; kayıt yapılamadı.'
      });
      return;
    }
    const emptyKeys = WEB_FILLABLE_FIELDS.filter((k) => isWebContentFieldEmpty(editForm, k));
    if (emptyKeys.length === 0) {
      setWebsiteSeoGenBanner({
        severity: 'info',
        message: 'Tüm açıklama ve SEO alanları zaten dolu.'
      });
      return;
    }
    const autoContent = buildAutoProductContent(editForm);
    const map = {
      aciklama: autoContent.description,
      ws_slug: autoContent.slug,
      ws_seoBaslik: autoContent.seoTitle,
      ws_seoAciklama: autoContent.seoDescription,
      ws_seoAnahtarKelimeler: autoContent.seoKeywords,
      ws_canonicalUrl: autoContent.canonical
    };
    const patch = {};
    emptyKeys.forEach((k) => {
      patch[k] = map[k];
    });
    setWebSeoFillLoading(true);
    try {
      await setDoc(doc(db, `subeler/${sube}/urunler`, barkod), patch, { merge: true });
      setEditForm((prev) => (prev ? { ...prev, ...patch } : prev));
      setSelectedUrun((prev) => (prev ? { ...prev, ...patch } : prev));
      setWebsiteSeoGenBanner({
        severity: 'success',
        message: 'Eksik açıklama ve SEO alanları oluşturuldu.'
      });
    } catch (err) {
      setWebsiteSeoGenBanner({
        severity: 'error',
        message: err?.message ? `Kayıt hatası: ${err.message}` : 'Kayıt sırasında hata oluştu.'
      });
    } finally {
      setWebSeoFillLoading(false);
    }
  };

  const applyImageFile = (file, key) => {
    if (!file || !/^image\//.test(file.type)) return;
    if (file.size > 2 * 1024 * 1024) {
      setError('HATA: Görsel boyutu 2MB’ı geçemez. Lütfen daha küçük bir dosya seçin.');
      return;
    }
    setError('');
    setEditForm((prev) => ({ ...prev, [key]: file }));
    const objectUrl = URL.createObjectURL(file);
    const bumpPreview = (setter) =>
      setter((prev) => {
        if (prev && String(prev).startsWith('blob:')) URL.revokeObjectURL(prev);
        return objectUrl;
      });
    if (key === 'gorsel1') bumpPreview(setPreview1);
    if (key === 'gorsel2') bumpPreview(setPreview2);
    if (key === 'gorsel3') bumpPreview(setPreview3);
    if (key === 'gorsel4') bumpPreview(setPreview4);
    if (key === 'gorsel5') bumpPreview(setPreview5);
    if (key === 'ty_gorsel1') bumpPreview(setTyPreview1);
    if (key === 'ty_gorsel2') bumpPreview(setTyPreview2);
    if (key === 'ty_gorsel3') bumpPreview(setTyPreview3);
    if (key === 'ty_gorsel4') bumpPreview(setTyPreview4);
    if (key === 'ty_gorsel5') bumpPreview(setTyPreview5);
  };

  const handleFileChange = (e, key) => {
    const file = e.target.files?.[0];
    if (file) applyImageFile(file, key);
  };
  
  const handleRemoveImage = (key) => { 
      setEditForm(prev => ({ ...prev, [key]: null })); 
      if (key === 'gorsel1') setPreview1(null); 
      if (key === 'gorsel2') setPreview2(null); 
      if (key === 'gorsel3') setPreview3(null);
      if (key === 'gorsel4') setPreview4(null);
      if (key === 'gorsel5') setPreview5(null);
      if (key === 'ty_gorsel1') setTyPreview1(null);
      if (key === 'ty_gorsel2') setTyPreview2(null);
      if (key === 'ty_gorsel3') setTyPreview3(null);
      if (key === 'ty_gorsel4') setTyPreview4(null);
      if (key === 'ty_gorsel5') setTyPreview5(null);
  };

  useEffect(() => {
    if (editForm) {
        const gram = safeParseFloat(editForm.gram);
        const milyem = safeParseFloat(editForm.urunMilyem);
        const iscilik = safeParseFloat(editForm.iscilik);
        const adetIscilik = safeParseFloat(editForm.adetIscilik);
        const kur = safeParseFloat(editForm.hasAltinKuru);
        const tip = editForm.urunTipi || "";

        if (gram > 0 && kur > 0) {
            const yeniMaliyet = calculateUrunMaliyetTry(
              { gram, urunMilyem: milyem, iscilik, adetIscilik },
              kur
            ).toFixed(2);
            
            let hedefFormul = null;
            if(formuller.length > 0) {
                hedefFormul = formuller.find(f => f.ad === tip);
                if (!hedefFormul) {
                    if (tip.includes("14 Ayar")) hedefFormul = formuller.find(f => f.ad === "14 Ayar");
                    else if (tip.includes("22 Ayar")) hedefFormul = formuller.find(f => f.ad === "22 Ayar");
                    else if (tip.includes("8 Ayar")) hedefFormul = formuller.find(f => f.ad === "8 Ayar");
                    else if (tip.includes("18 Ayar")) hedefFormul = formuller.find(f => f.ad === "18 Ayar");
                }
            }

            let yeniMagazaFiyati = editForm.magazaFiyati;
            let baseUnitPrice = 0; 
            let source = 'Manuel';

            if (hedefFormul) {
                const satisCarpan = safeParseFloat(hedefFormul.satisCarpan);
                const satisIscilik = safeParseFloat(hedefFormul.satisIscilik);
                baseUnitPrice = (kur * satisCarpan) + satisIscilik;
                source = `Formül: ${hedefFormul.ad}`;
                yeniMagazaFiyati = formatAutoPrice(baseUnitPrice * gram);
            } else {
                baseUnitPrice = gram > 0 ? (safeParseFloat(editForm.magazaFiyati) / gram) : 0;
            }

            const pazaryeriFiyatDegeri = safeParseFloat(yeniMagazaFiyati);
            const otoPazaryeriFiyati = formatAutoPrice(pazaryeriFiyatDegeri * 1.35);
            
            const webUnitPrice = baseUnitPrice * 1.15; 
            const otoSiteFiyati = formatAutoPrice(webUnitPrice * gram); 

            setUnitInfo({
                magazaBirim: baseUnitPrice,
                webBirim: webUnitPrice,
                kaynak: source
            });

            setEditForm(prev => {
                if (
                    prev.maliyetFiyati === yeniMaliyet &&
                    (!hedefFormul || prev.magazaFiyati === yeniMagazaFiyati) &&
                    (prev.isManualPazaryeri || prev.pazaryeriFiyati === otoPazaryeriFiyati) &&
                    true
                ) {
                    return prev;
                }

                return { 
                    ...prev, 
                    maliyetFiyati: yeniMaliyet,
                    magazaFiyati: hedefFormul ? yeniMagazaFiyati : prev.magazaFiyati,
                    pazaryeriFiyati: prev.isManualPazaryeri ? prev.pazaryeriFiyati : otoPazaryeriFiyati
                };
            });

            setTrendyolForm((prev) => ({
              ...prev,
              ...(editForm.isManualPazaryeri
                ? {}
                : {
                    trendyolSatisFiyati: otoPazaryeriFiyati,
                    trendyolIndirimliSatisFiyati: otoPazaryeriFiyati
                  })
            }));

            setWebsiteForm((prev) => ({
                ...prev,
                websiteSatisFiyati: editForm.isManualSite
                  ? String(editForm.ws_websiteSatisFiyati ?? '').trim() !== ''
                    ? String(editForm.ws_websiteSatisFiyati)
                    : prev.websiteSatisFiyati || otoSiteFiyati
                  : otoSiteFiyati
            }));
            
            setMagazaForm(prev => ({
                ...prev,
                satisFiyati: (hedefFormul && !editForm.isManualMagaza) ? yeniMagazaFiyati : prev.satisFiyati
            }));
        }
    }
  }, [editForm?.gram, editForm?.urunMilyem, editForm?.iscilik, editForm?.adetIscilik, editForm?.hasAltinKuru, editForm?.urunTipi, editForm?.magazaFiyati, editForm?.isManualPazaryeri, editForm?.isManualSite, editForm?.isManualMagaza, editForm?.pazaryeriFiyati, editForm?.siteFiyati, editForm?.ws_websiteSatisFiyati, formuller]);

  /**
   * Trendyol sek. “Sonuç analizi”: Web sekmesi gibi liste + indirimli satış; komisyon/KDV/indirimli satıştan hesaplanır.
   * Kampanya simülasyonu ve minimum kuyumcu fiyatı hâlâ liste fiyatı üzerinden.
   */
  const trendyolDetailMetrics = useMemo(() => {
    if (!selectedUrun || !editForm) return null;

    const kur =
      parseNumLoose(editForm.hasAltinKuru) > 0
        ? parseNumLoose(editForm.hasAltinKuru)
        : parseNumLoose(trendyolForm.hasAltinKuru);
    if (!(kur > 0)) return null;

    const hasAltinDegeri = computeKuyumcuHasAltinDegeri(
      selectedUrun.gram,
      kur,
      editForm.urunTipi,
      selectedUrun.urunMilyem
    );
    const laborProduct = {
      gram: selectedUrun.gram,
      urunMilyem: editForm.urunMilyem ?? selectedUrun.urunMilyem,
      iscilik: editForm.iscilik ?? selectedUrun.iscilik,
      adetIscilik: editForm.adetIscilik ?? selectedUrun.adetIscilik
    };
    const iscilikTutari = calculateUrunLaborTry(laborProduct, kur);
    const toplamMaliyet = hasAltinDegeri + iscilikTutari;

    const maliyet = safeParseFloat(editForm.maliyetFiyati);

    const satisListe = Math.max(
      0,
      trendyolForm.trendyolSatisFiyati !== '' && trendyolForm.trendyolSatisFiyati != null
        ? safeParseFloat(trendyolForm.trendyolSatisFiyati)
        : 0
    );

    const indirimRaw = trendyolForm.trendyolIndirimliSatisFiyati;
    const indirimStr = String(indirimRaw ?? '').trim();
    const efektifSatis =
      indirimStr !== '' && indirimRaw != null ? Math.max(0, safeParseFloat(indirimRaw)) : satisListe;
    const toplamIndirim = Math.max(0, satisListe - efektifSatis);

    const komisyonOrani = safeParseFloat(trendyolForm.komisyonOrani);
    const kargo = safeParseFloat(trendyolForm.kargoUcreti);
    const ekMasraf = safeParseFloat(trendyolForm.ekMasraf);

    const kdvMatrahi = Math.max(0, efektifSatis - hasAltinDegeri);
    const kdvTutari = kdvMatrahi * (KUYUMCU_KDV_ORANI / 100);
    const komisyonTutari = efektifSatis * (komisyonOrani / 100);

    const netKalan = efektifSatis - komisyonTutari - kdvTutari - ekMasraf - kargo;
    const netKar = netKalan - maliyet;

    let kampanyaFiyati = satisListe;
    const kDeger = safeParseFloat(trendyolForm.kampanyaDegeri);
    if (trendyolForm.kampanyaTipi === 'yuzde') kampanyaFiyati = Math.max(0, satisListe - satisListe * (kDeger / 100));
    else if (trendyolForm.kampanyaTipi === 'tutar') kampanyaFiyati = Math.max(0, satisListe - kDeger);

    const kdvMatrahiKamp = Math.max(0, kampanyaFiyati - hasAltinDegeri);
    const kdvTutariKamp = kdvMatrahiKamp * (KUYUMCU_KDV_ORANI / 100);
    const komisyonTutariKamp = kampanyaFiyati * (komisyonOrani / 100);
    const netKalanKamp = kampanyaFiyati - komisyonTutariKamp - kdvTutariKamp - ekMasraf - kargo;
    const netKarKamp = netKalanKamp - maliyet;

    const pay = toplamMaliyet + kargo + ekMasraf - hasAltinDegeri * (KUYUMCU_KDV_ORANI / 100);
    const payda = 1 - KUYUMCU_KDV_ORANI / 100 - komisyonOrani / 100;
    const minimumSatisFiyati = payda > 0 ? pay / payda : 0;

    return {
      hasAltinDegeri,
      iscilikTutari,
      toplamMaliyet,
      maliyet,
      satisListe,
      toplamIndirim,
      efektifSatis,
      komisyonOrani,
      komisyonTutari,
      kdvTutari,
      kdvMatrahi,
      ekMasraf,
      kargo,
      netKalan,
      netKar,
      kampanyaFiyati,
      netKarKamp,
      kdvTutariKamp,
      komisyonTutariKamp,
      minimumSatisFiyati
    };
  }, [trendyolForm, selectedUrun, editForm]);

  const magazaMetrics = useMemo(() => {
      if (!selectedUrun || !editForm) return null;

      const hasAltinDegeri = computeKuyumcuHasAltinDegeri(
        selectedUrun.gram,
        editForm.hasAltinKuru,
        editForm.urunTipi,
        selectedUrun.urunMilyem
      );

      const maliyet = safeParseFloat(editForm.maliyetFiyati);
      const satisBrut = safeParseFloat(magazaForm.satisFiyati);
      const indirimTL = safeParseFloat(magazaForm.ekIndirim);
      const indirimYuzde = safeParseFloat(magazaForm.indirimYuzde);
      const indirimOranTutari = satisBrut * (indirimYuzde / 100);
      const toplamIndirim = indirimTL + indirimOranTutari;
      const efektifSatis = toplamIndirim > 0
        ? roundAutoPriceUpToTen(Math.max(0, satisBrut - toplamIndirim))
        : satisBrut;

      const kkKomisyon = safeParseFloat(magazaForm.kkKomisyonu);
      const bankaKesintisi = efektifSatis * (kkKomisyon / 100);

      const kdvMatrahi = Math.max(0, efektifSatis - hasAltinDegeri);
      const kdvTutari = kdvMatrahi * (KUYUMCU_KDV_ORANI / 100);

      const ekMasraf = safeParseFloat(magazaForm.ekMasraf);
      const kargo = safeParseFloat(magazaForm.kargoBedeli);

      const netKalan = efektifSatis - bankaKesintisi - kdvTutari - ekMasraf - kargo;
      const netKar = netKalan - maliyet;
      const kkOranSafe = Math.min(Math.max(kkKomisyon, 0), 99.99);
      const minFiyat = (maliyet + ekMasraf + kargo) / (1 - kkOranSafe / 100);

      return {
        maliyet,
        satisBrut,
        toplamIndirim,
        efektifSatis,
        bankaKesintisi,
        hasAltinDegeri,
        kdvMatrahi,
        kdvTutari,
        ekMasraf,
        kargo,
        netKalan,
        netKar,
        minFiyat
      };
  }, [magazaForm, editForm, selectedUrun]);

  /** Web sekmesi “Sonuç analizi”: liste = Web Satış Fiyatı, indirim = Web liste üzerinden (mg_ekIndirim / mg_indirimYuzde). */
  const webSiteMetrics = useMemo(() => {
      if (!selectedUrun || !editForm) return null;

      const hasAltinDegeri = computeKuyumcuHasAltinDegeri(
        selectedUrun.gram,
        editForm.hasAltinKuru,
        editForm.urunTipi,
        selectedUrun.urunMilyem
      );

      const maliyet = safeParseFloat(editForm.maliyetFiyati);
      const webListeRaw =
        websiteForm.websiteSatisFiyati !== '' && websiteForm.websiteSatisFiyati != null
          ? websiteForm.websiteSatisFiyati
          : String(editForm.ws_websiteSatisFiyati ?? '').trim() !== ''
            ? editForm.ws_websiteSatisFiyati
            : editForm.siteFiyati;
      const satisBrut = safeParseFloat(webListeRaw);
      const indirimTL = safeParseFloat(magazaForm.ekIndirim);
      const indirimYuzde = safeParseFloat(magazaForm.indirimYuzde);
      const indirimOranTutari = satisBrut * (indirimYuzde / 100);
      const toplamIndirim = indirimTL + indirimOranTutari;
      const efektifSatis = toplamIndirim > 0
        ? roundAutoPriceUpToTen(Math.max(0, satisBrut - toplamIndirim))
        : satisBrut;

      const kkKomisyon = safeParseFloat(magazaForm.kkKomisyonu);
      const bankaKesintisi = efektifSatis * (kkKomisyon / 100);

      const kdvMatrahi = Math.max(0, efektifSatis - hasAltinDegeri);
      const kdvTutari = kdvMatrahi * (KUYUMCU_KDV_ORANI / 100);

      const ekMasraf = safeParseFloat(magazaForm.ekMasraf);
      const kargo = safeParseFloat(magazaForm.kargoBedeli);

      const netKalan = efektifSatis - bankaKesintisi - kdvTutari - ekMasraf - kargo;
      const netKar = netKalan - maliyet;
      const kkOranSafe = Math.min(Math.max(kkKomisyon, 0), 99.99);
      const minFiyat = (maliyet + ekMasraf + kargo) / (1 - kkOranSafe / 100);

      return {
        maliyet,
        satisBrut,
        toplamIndirim,
        efektifSatis,
        bankaKesintisi,
        hasAltinDegeri,
        kdvMatrahi,
        kdvTutari,
        ekMasraf,
        kargo,
        netKalan,
        netKar,
        minFiyat
      };
  }, [websiteForm, magazaForm, editForm, selectedUrun]);


  const handleSave = async () => {
    setError('');
    setSuccess('');
    try {
      const barkod = selectedUrun.barkod; 
      let gorselURL1 = selectedUrun.gorselURL1;
      let gorselURL2 = selectedUrun.gorselURL2;
      let gorselURL3 = selectedUrun.gorselURL3;
      let gorselURL4 = selectedUrun.gorselURL4;
      let gorselURL5 = selectedUrun.gorselURL5;
      let ty_gorselURL1 = selectedUrun.ty_gorselURL1;
      let ty_gorselURL2 = selectedUrun.ty_gorselURL2;
      let ty_gorselURL3 = selectedUrun.ty_gorselURL3;
      let ty_gorselURL4 = selectedUrun.ty_gorselURL4;
      let ty_gorselURL5 = selectedUrun.ty_gorselURL5;

      if (editForm.gorsel1) {
        const storageRef1 = ref(storage, `subeler/${sube}/urunler/${barkod}/gorsel1`);
        await uploadBytes(storageRef1, editForm.gorsel1);
        gorselURL1 = await getDownloadURL(storageRef1);
      }
      if (editForm.gorsel2) {
        const storageRef2 = ref(storage, `subeler/${sube}/urunler/${barkod}/gorsel2`);
        await uploadBytes(storageRef2, editForm.gorsel2);
        gorselURL2 = await getDownloadURL(storageRef2);
      }
      if (editForm.gorsel3) {
        const storageRef3 = ref(storage, `subeler/${sube}/urunler/${barkod}/gorsel3`);
        await uploadBytes(storageRef3, editForm.gorsel3);
        gorselURL3 = await getDownloadURL(storageRef3);
      }
      if (editForm.gorsel4) {
        const storageRef4 = ref(storage, `subeler/${sube}/urunler/${barkod}/gorsel4`);
        await uploadBytes(storageRef4, editForm.gorsel4);
        gorselURL4 = await getDownloadURL(storageRef4);
      }
      if (editForm.gorsel5) {
        const storageRef5 = ref(storage, `subeler/${sube}/urunler/${barkod}/gorsel5`);
        await uploadBytes(storageRef5, editForm.gorsel5);
        gorselURL5 = await getDownloadURL(storageRef5);
      }
      if (editForm.ty_gorsel1) {
        const ref1 = ref(storage, `subeler/${sube}/urunler/${barkod}/ty_gorsel1`);
        await uploadBytes(ref1, editForm.ty_gorsel1);
        ty_gorselURL1 = await getDownloadURL(ref1);
      }
      if (editForm.ty_gorsel2) {
        const ref2 = ref(storage, `subeler/${sube}/urunler/${barkod}/ty_gorsel2`);
        await uploadBytes(ref2, editForm.ty_gorsel2);
        ty_gorselURL2 = await getDownloadURL(ref2);
      }
      if (editForm.ty_gorsel3) {
        const ref3 = ref(storage, `subeler/${sube}/urunler/${barkod}/ty_gorsel3`);
        await uploadBytes(ref3, editForm.ty_gorsel3);
        ty_gorselURL3 = await getDownloadURL(ref3);
      }
      if (editForm.ty_gorsel4) {
        const ref4 = ref(storage, `subeler/${sube}/urunler/${barkod}/ty_gorsel4`);
        await uploadBytes(ref4, editForm.ty_gorsel4);
        ty_gorselURL4 = await getDownloadURL(ref4);
      }
      if (editForm.ty_gorsel5) {
        const ref5 = ref(storage, `subeler/${sube}/urunler/${barkod}/ty_gorsel5`);
        await uploadBytes(ref5, editForm.ty_gorsel5);
        ty_gorselURL5 = await getDownloadURL(ref5);
      }

      const cleanForm = { ...editForm };
      delete cleanForm.gorsel1;
      delete cleanForm.gorsel2;
      delete cleanForm.gorsel3;
      delete cleanForm.gorsel4;
      delete cleanForm.gorsel5;
      delete cleanForm.ty_gorsel1;
      delete cleanForm.ty_gorsel2;
      delete cleanForm.ty_gorsel3;
      delete cleanForm.ty_gorsel4;
      delete cleanForm.ty_gorsel5;

      // Sekme içindeki güncel fiyatları ana forma ve tabloya da taşı.
      const rawFinalMagaza = safeParseFloat(
        magazaForm.satisFiyati !== '' && magazaForm.satisFiyati != null
          ? magazaForm.satisFiyati
          : editForm.magazaFiyati
      );
      const finalMagazaFiyati = editForm.isManualMagaza
        ? rawFinalMagaza
        : roundAutoPriceUpToTen(rawFinalMagaza);
      const tyListeForFinalRaw =
        trendyolForm.trendyolSatisFiyati !== '' && trendyolForm.trendyolSatisFiyati != null
          ? trendyolForm.trendyolSatisFiyati
          : '';
      const tyIndirimForFinalRaw =
        trendyolForm.trendyolIndirimliSatisFiyati !== '' && trendyolForm.trendyolIndirimliSatisFiyati != null
          ? trendyolForm.trendyolIndirimliSatisFiyati
          : '';

      const resolvedTyListeRaw =
        tyListeForFinalRaw !== ''
          ? tyListeForFinalRaw
          : String(editForm.ty_trendyolSatisFiyati ?? '').trim() !== ''
            ? editForm.ty_trendyolSatisFiyati
            : String(selectedUrun.ty_trendyolSatisFiyati ?? '').trim() !== ''
              ? selectedUrun.ty_trendyolSatisFiyati
              : editForm.pazaryeriFiyati;

      const rawFinalPazaryeri = safeParseFloat(
        String(tyIndirimForFinalRaw).trim() !== ''
          ? tyIndirimForFinalRaw
          : resolvedTyListeRaw !== '' && resolvedTyListeRaw != null
            ? resolvedTyListeRaw
            : editForm.pazaryeriFiyati
      );
      const finalPazaryeriFiyati = editForm.isManualPazaryeri
        ? rawFinalPazaryeri
        : roundAutoPriceUpToTen(rawFinalPazaryeri);
      const rawSaveWebListe = safeParseFloat(
        websiteForm.websiteSatisFiyati !== '' && websiteForm.websiteSatisFiyati != null
          ? websiteForm.websiteSatisFiyati
          : String(editForm.ws_websiteSatisFiyati ?? '').trim() !== ''
            ? editForm.ws_websiteSatisFiyati
            : selectedUrun.ws_websiteSatisFiyati
      );
      const saveWebListe = editForm.isManualSite
        ? rawSaveWebListe
        : roundAutoPriceUpToTen(rawSaveWebListe);
      const saveEk = safeParseFloat(magazaForm.ekIndirim);
      const saveY = safeParseFloat(magazaForm.indirimYuzde);
      const saveHasDiscount = saveEk > 0 || saveY > 0;
      const finalSiteFiyati = saveHasDiscount && saveWebListe > 0
        ? roundAutoPriceUpToTen(Math.max(0, saveWebListe - saveEk - (saveWebListe * saveY) / 100))
        : 0;
      const saveWebListeStr = editForm.isManualSite
        ? String(websiteForm.websiteSatisFiyati ?? rawSaveWebListe)
        : formatAutoPrice(saveWebListe);
      const saveMagazaListeStr = editForm.isManualMagaza
        ? String(magazaForm.satisFiyati ?? rawFinalMagaza)
        : formatAutoPrice(finalMagazaFiyati);
      const saveTyListeStr = editForm.isManualPazaryeri
        ? String(resolvedTyListeRaw ?? '')
        : formatAutoPrice(safeParseFloat(resolvedTyListeRaw));

      let tyFirestorePatch = {};
      if (openTrendyol) {
        tyFirestorePatch = buildTrendyolProductFirestorePatch(trendyolProductInfo, {
          ...selectedUrun,
          ...cleanForm
        });
        const tyCfg = trendyolMerchantCfg && typeof trendyolMerchantCfg === 'object' ? trendyolMerchantCfg : null;
        if (tyCfg) {
          const rb = resolveTrendyolBrandIdForSend(
            trendyolProductInfo?.ty_brandId ?? tyFirestorePatch.ty_brandId,
            tyCfg
          );
          if (isTrendyolNumericBrandIdResolved(rb)) {
            tyFirestorePatch = {
              ...tyFirestorePatch,
              ty_brandId: String(Math.trunc(Number(rb)))
            };
          }
        }
      }

      const updatedData = {
        ...selectedUrun,
        ...cleanForm,
        ...(openTrendyol ? tyFirestorePatch : {}),
        ikasCategoryIds: Array.isArray(cleanForm.ikasCategoryIds) ? cleanForm.ikasCategoryIds : [],
        ikasCategoryNamesSnapshot: Array.isArray(cleanForm.ikasCategoryNamesSnapshot) ? cleanForm.ikasCategoryNamesSnapshot : [],
        ikasCategoryId:
          (Array.isArray(cleanForm.ikasCategoryIds) ? cleanForm.ikasCategoryIds[0] : '') ||
          cleanForm.ikasCategoryId ||
          '',
        urunKategorisi:
          (Array.isArray(cleanForm.ikasCategoryNamesSnapshot) ? cleanForm.ikasCategoryNamesSnapshot[0] : '') ||
          cleanForm.urunKategorisi ||
          '',
        stokKodu:
          cleanForm.stokKodu != null && String(cleanForm.stokKodu).trim() !== '' ? String(cleanForm.stokKodu).trim() : barkod,
        ty_hasAltinKuru: trendyolForm.hasAltinKuru,
        ty_trendyolSatisFiyati:
          tyListeForFinalRaw !== ''
            ? (editForm.isManualPazaryeri ? trendyolForm.trendyolSatisFiyati : formatAutoPrice(trendyolForm.trendyolSatisFiyati))
            : String(resolvedTyListeRaw ?? '') !== ''
              ? saveTyListeStr
              : (editForm.isManualPazaryeri ? trendyolForm.trendyolSatisFiyati : formatAutoPrice(trendyolForm.trendyolSatisFiyati)),
        ty_komisyonOrani: trendyolForm.komisyonOrani,
        ty_kargoUcreti: trendyolForm.kargoUcreti,
        ty_ekMasraf: trendyolForm.ekMasraf,
        ty_hesaplamaYontemi: trendyolForm.hesaplamaYontemi,
        ty_karMarji: trendyolForm.karMarji,
        ty_kampanyaTipi: trendyolForm.kampanyaTipi,
        ty_kampanyaDegeri: trendyolForm.kampanyaDegeri,
        
        ws_hasAltinKuru: websiteForm.hasAltinKuru,
        ws_websiteSatisFiyati: saveWebListeStr,
        ws_kargoUcreti: websiteForm.kargoUcreti,
        ws_ekMasraf: websiteForm.ekMasraf,
        ws_kampanyaTipi: websiteForm.kampanyaTipi,
        ws_kampanyaDegeri: websiteForm.kampanyaDegeri,
        mg_satisFiyati: saveMagazaListeStr,
        mg_kkKomisyonu: magazaForm.kkKomisyonu,
        mg_kdvOrani: String(KUYUMCU_KDV_ORANI),
        mg_ekIndirim: magazaForm.ekIndirim,
        mg_indirimYuzde: magazaForm.indirimYuzde,
        mg_ekMasraf: magazaForm.ekMasraf,
        mg_kargoBedeli: magazaForm.kargoBedeli,

        maliyetFiyati: safeParseFloat(editForm.maliyetFiyati),
        hasAltinKuru: safeParseFloat(editForm.hasAltinKuru),
        iscilik: normalizeIscilikForSave(editForm.iscilik),
        adetIscilik: normalizeAdetIscilikForSave(editForm.adetIscilik),
        magazaFiyati: finalMagazaFiyati,
        siteFiyati: finalSiteFiyati,
        pazaryeriFiyati: finalPazaryeriFiyati,
        isManualPazaryeri: editForm.isManualPazaryeri,
        isManualSite: editForm.isManualSite,
        priceSource: editForm.priceSource ?? (editForm.isManualSite ? 'manual' : 'auto'),
        isManualMagaza: editForm.isManualMagaza,
        gorselURL1: gorselURL1 || selectedUrun.gorselURL1,
        gorselURL2: gorselURL2 || selectedUrun.gorselURL2,
        gorselURL3: gorselURL3 || selectedUrun.gorselURL3,
        gorselURL4: gorselURL4 || selectedUrun.gorselURL4,
        gorselURL5: gorselURL5 || selectedUrun.gorselURL5,
        ty_gorselURL1: ty_gorselURL1 || selectedUrun.ty_gorselURL1,
        ty_gorselURL2: ty_gorselURL2 || selectedUrun.ty_gorselURL2,
        ty_gorselURL3: ty_gorselURL3 || selectedUrun.ty_gorselURL3,
        ty_gorselURL4: ty_gorselURL4 || selectedUrun.ty_gorselURL4,
        ty_gorselURL5: ty_gorselURL5 || selectedUrun.ty_gorselURL5,
        uretici: editForm.uretici ?? editForm.mensei ?? selectedUrun.uretici ?? selectedUrun.mensei ?? null,
        urunRengi: editForm.urunRengi ?? editForm.renk ?? selectedUrun.urunRengi ?? selectedUrun.renk ?? null,
        renk: editForm.urunRengi ?? editForm.renk ?? selectedUrun.urunRengi ?? selectedUrun.renk ?? null,
        timestamp: serverTimestamp() 
      };

      // Firestore undefined kabul etmez: undefined olan tüm alanları temizle.
      const sanitizedData = Object.fromEntries(
        Object.entries(updatedData).filter(([, value]) => value !== undefined)
      );
      // Görsel URL alanlarında undefined kalmaması için güvenli default.
      [
        'gorselURL1',
        'gorselURL2',
        'gorselURL3',
        'gorselURL4',
        'gorselURL5',
        'ty_gorselURL1',
        'ty_gorselURL2',
        'ty_gorselURL3',
        'ty_gorselURL4',
        'ty_gorselURL5'
      ].forEach((key) => {
        if (sanitizedData[key] === undefined) sanitizedData[key] = '';
      });

      const docRef = doc(db, `subeler/${sube}/urunler`, barkod);
      await setDoc(docRef, sanitizedData);

      // Modal açıkken de güncel değerleri anında göster.
      setSelectedUrun((prev) => (prev ? { ...prev, ...sanitizedData } : prev));
      if (openTrendyol) {
        setTrendyolProductInfo(normalizeTrendyolProductInfo(sanitizedData, sanitizedData));
      }
      const bumpPrevFromSanitized = (setter, url) =>
        setter((p) => {
          if (p && String(p).startsWith('blob:')) URL.revokeObjectURL(p);
          return url || null;
        });

      bumpPrevFromSanitized(setPreview1, sanitizedData.gorselURL1);
      bumpPrevFromSanitized(setPreview2, sanitizedData.gorselURL2);
      bumpPrevFromSanitized(setPreview3, sanitizedData.gorselURL3);
      bumpPrevFromSanitized(setPreview4, sanitizedData.gorselURL4);
      bumpPrevFromSanitized(setPreview5, sanitizedData.gorselURL5);
      bumpPrevFromSanitized(setTyPreview1, sanitizedData.ty_gorselURL1);
      bumpPrevFromSanitized(setTyPreview2, sanitizedData.ty_gorselURL2);
      bumpPrevFromSanitized(setTyPreview3, sanitizedData.ty_gorselURL3);
      bumpPrevFromSanitized(setTyPreview4, sanitizedData.ty_gorselURL4);
      bumpPrevFromSanitized(setTyPreview5, sanitizedData.ty_gorselURL5);

      setEditForm((prev) => {
        if (!prev) return prev;
        const tyFromDoc = {};
        TRENDYOL_PRODUCT_FIELDS.forEach((key) => {
          if (sanitizedData[key] !== undefined) tyFromDoc[key] = sanitizedData[key];
        });
        if (sanitizedData.ty_attributes !== undefined) tyFromDoc.ty_attributes = sanitizedData.ty_attributes;
        return {
          ...prev,
          ...tyFromDoc,
          gorsel1: null,
          gorsel2: null,
          gorsel3: null,
          gorsel4: null,
          gorsel5: null,
          ty_gorsel1: null,
          ty_gorsel2: null,
          ty_gorsel3: null,
          ty_gorsel4: null,
          ty_gorsel5: null,
          gorselURL1: sanitizedData.gorselURL1 ?? prev.gorselURL1,
          gorselURL2: sanitizedData.gorselURL2 ?? prev.gorselURL2,
          gorselURL3: sanitizedData.gorselURL3 ?? prev.gorselURL3,
          gorselURL4: sanitizedData.gorselURL4 ?? prev.gorselURL4,
          gorselURL5: sanitizedData.gorselURL5 ?? prev.gorselURL5,
          ty_gorselURL1: sanitizedData.ty_gorselURL1 ?? prev.ty_gorselURL1,
          ty_gorselURL2: sanitizedData.ty_gorselURL2 ?? prev.ty_gorselURL2,
          ty_gorselURL3: sanitizedData.ty_gorselURL3 ?? prev.ty_gorselURL3,
          ty_gorselURL4: sanitizedData.ty_gorselURL4 ?? prev.ty_gorselURL4,
          ty_gorselURL5: sanitizedData.ty_gorselURL5 ?? prev.ty_gorselURL5,
          magazaFiyati: String(finalMagazaFiyati || ''),
          pazaryeriFiyati: String(finalPazaryeriFiyati || ''),
          siteFiyati: String(finalSiteFiyati || '')
        };
      });

      if (openWebsite) {
        setWebsiteSaveBanner({
          severity: 'success',
          message: "Ürün UKBA-KASA'ya kaydedildi."
        });
        return;
      }

      setSuccess('Başarıyla kaydedildi.');
      if (openTrendyol) setOpenTrendyol(false);
      setTimeout(() => {
        if (!openTrendyol) handleCloseModal();
      }, 1500);
    } catch (err) {
      if (openWebsite) {
        setWebsiteSaveBanner({
          severity: 'error',
          message: 'Ürün kaydedilemedi.',
          detail: err?.message || String(err)
        });
      }
      setError('Hata: ' + err.message);
    }
  };

  const postIkasSaveProductRequest = async (productId) => {
    const res = await fetch(apiUrl('/api/ikas/save-product'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await getBackendAuthHeaders())
      },
      body: JSON.stringify({ subeId: sube, productId })
    });
    let json = {};
    try {
      json = await res.json();
    } catch (e) {
      json = {};
    }
    return { res, json };
  };

  const postTrendyolSaveProductRequest = async (productId, subeIdLocalParam = sube) => {
    const pid = String(productId || '').trim();
    const sid = String(subeIdLocalParam || sube || '').trim();
    const res = await fetch(apiUrl('/api/trendyol/save-product'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await getBackendAuthHeaders())
      },
      body: JSON.stringify({ subeId: sid, productId: pid })
    });
    const responseText = await res.text();
    let json = {};
    try {
      json = responseText ? JSON.parse(responseText) : {};
    } catch (parseError) {
      json = { message: 'Sunucudan geçersiz JSON yanıtı alındı.' };
    }
    return { res, json };
  };

  const postTrendyolRefreshStatusRequest = async (productId, subeIdLocalParam = sube) => {
    const pid = String(productId || '').trim();
    const sid = String(subeIdLocalParam || sube || '').trim();
    const res = await fetch(apiUrl('/api/trendyol/products/refresh-status'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await getBackendAuthHeaders())
      },
      body: JSON.stringify({ subeId: sid, productId: pid })
    });
    const responseText = await res.text();
    let json = {};
    try {
      json = responseText ? JSON.parse(responseText) : {};
    } catch (parseError) {
      json = { message: 'Sunucudan geçersiz JSON yanıtı alındı.' };
    }
    return { res, json };
  };

  const handleTrendyolListProductRowRefresh = async (urunRow) => {
    const pid = String(urunRow?.realBarkod ?? urunRow?.id ?? urunRow?.barkod ?? '').trim();
    if (!pid || !sube || sube === 'Bilinmeyen Şube') {
      setSnackbarMessage('Şube veya ürün kimliği eksik; Trendyol durumu güncellenemedi.');
      setSnackbarOpen(true);
      return;
    }
    setTrendyolListRowRefreshBusy((prev) => ({ ...prev, [pid]: true }));
    try {
      const { res, json } = await postTrendyolRefreshStatusRequest(pid);
      let msg = String(json?.message || json?.error || '').trim();
      if (!msg && !res.ok) msg = `HTTP ${res.status}`;
      if (!msg) msg = json?.success !== false ? 'Trendyol durumu güncellendi.' : 'Trendyol durum güncellemesi reddedildi.';
      setSnackbarMessage(msg);
      setSnackbarOpen(true);
    } catch (e) {
      setSnackbarMessage(e?.message || String(e));
      setSnackbarOpen(true);
    } finally {
      setTrendyolListRowRefreshBusy((prev) => {
        const next = { ...prev };
        delete next[pid];
        return next;
      });
    }
  };

  const handleVerifyIkasRemoteForSelection = async () => {
    if (!sube || sube === 'Bilinmeyen Şube' || !selectedIds.length) return;
    setIkasVerifyLoading(true);
    try {
      const { res, json } = await postVerifyIkasRemoteProducts(selectedIds);
      if (!res.ok || json.success === false) {
        setSnackbarMessage(String(json.message || `HTTP ${res.status}`));
        setSnackbarOpen(true);
        return;
      }
      const results = Array.isArray(json.results) ? json.results : [];
      const cleared = results.filter((r) => r.action === 'cleared_stale_link').length;
      const still = results.filter((r) => r.action === 'still_on_ikas').length;
      const skip = results.filter((r) => r.action === 'skipped_no_link').length;
      const errN = results.filter((r) => r.action === 'error').length;
      setSnackbarMessage(
        `İkas kontrolü: ${still} ürün panelde mevcut, ${cleared} üründe eski İkas bağlantısı silindi, ${skip} bağlantısız atlandı${errN ? `, ${errN} hata` : ''}.`
      );
      setSnackbarOpen(true);
    } catch (e) {
      setSnackbarMessage(e?.message || String(e));
      setSnackbarOpen(true);
    } finally {
      setIkasVerifyLoading(false);
    }
  };

  /** Web modal: Firestore’a yazılmış ürünü İkas’a gönderir (modal kapanmaz). */
  const handleSendProductToIkas = async () => {
    const barkod = String(selectedUrun?.barkod || selectedUrun?.id || '').trim();
    if (!barkod || !sube || sube === 'Bilinmeyen Şube') {
      setWebsiteSaveBanner({
        severity: 'warning',
        message: 'Şube veya barkod eksik; İkas’a gönderilemedi.'
      });
      return;
    }
    setIkasSendLoading(true);
    try {
      const { res, json } = await postIkasSaveProductRequest(barkod);
      if (json.syncStatus === 'skipped_missing_fields') {
        const extra = Array.isArray(json.missingFields) ? json.missingFields.join(', ') : '';
        setWebsiteSaveBanner({
          severity: 'warning',
          message: 'Ürün İkas’a gönderilmedi. Eksik alanlar var.',
          detail: extra || json.message || ''
        });
        return;
      }
      if (json.syncStatus === 'skipped_unchanged') {
        setWebsiteSaveBanner({
          severity: 'info',
          message: 'Üründe değişiklik yok. İkas’a tekrar gönderilmedi.'
        });
        return;
      }
      if (json.dryRun === true) {
        setWebsiteSaveBanner({
          severity: 'warning',
          message: 'Canlı İkas kaydı kapalı (dry-run). İkas panelinde ürün oluşmadı.',
          detail:
            'Sunucu ortamında IKAS_LIVE_SAVE_ENABLED=true ve İkas API bilgileri tanımlı olmalı.'
        });
        return;
      }
      if (!res.ok || json.success === false) {
        const errDetail =
          String(json.ikasLastError || json.message || json.error || '').trim() || `HTTP ${res.status}`;
        setWebsiteSaveBanner({
          severity: 'error',
          message: `İkas gönderim hatası: ${errDetail}`
        });
        return;
      }
      setWebsiteSaveBanner({
        severity: 'success',
        message: 'Ürün başarıyla İkas’a gönderildi.'
      });
    } catch (e) {
      setWebsiteSaveBanner({
        severity: 'error',
        message: `İkas gönderim hatası: ${e?.message || e}`
      });
    } finally {
      setIkasSendLoading(false);
    }
  };

  const runBulkIkasSendBatch = async (targets) => {
    let ok = 0;
    let skipped = 0;
    let bad = 0;
    const lines = [];
    const n = targets.length;
    for (let i = 0; i < n; i += 1) {
      const p = targets[i];
      const barkod = String(p.barkod || p.id || '').trim();
      setIkasJobSummary(
        `Gönderiliyor: ${i + 1} / ${n}\nBaşarılı: ${ok}\nAtlandı: ${skipped}\nHatalı: ${bad}`
      );
      if (!barkod) {
        bad += 1;
        lines.push(`— | ${p.isim || '-'} | geçersiz barkod`);
        continue;
      }
      const localCheck = validateIkasReadyProduct(p);
      if (!localCheck.ok) {
        skipped += 1;
        lines.push(
          `${barkod} | ${p.isim || '-'} | skipped_missing_fields: ${localCheck.missingFields.join(', ')}`
        );
        continue;
      }
      try {
        const { res, json } = await postIkasSaveProductRequest(barkod);
        if (json.syncStatus === 'skipped_missing_fields') {
          skipped += 1;
          lines.push(
            `${barkod} | ${p.isim || '-'} | skipped_missing_fields: ${(json.missingFields || []).join(', ')}`
          );
          continue;
        }
        if (json.syncStatus === 'skipped_unchanged') {
          skipped += 1;
          lines.push(`${barkod} | ${p.isim || '-'} | skipped_unchanged`);
          continue;
        }
        if (json.dryRun === true) {
          skipped += 1;
          lines.push(
            `${barkod} | ${p.isim || '-'} | dry_run: IKAS_LIVE_SAVE_ENABLED kapalı — canlı kayıt yok`
          );
          continue;
        }
        if (!res.ok || json.success === false) {
          bad += 1;
          const errDetail = String(json.ikasLastError || json.message || json.error || '').trim() || `HTTP ${res.status}`;
          lines.push(`${barkod} | ${p.isim || '-'} | ${errDetail}`);
          continue;
        }
        ok += 1;
      } catch (e) {
        bad += 1;
        lines.push(`${barkod} | ${p.isim || '-'} | ${e?.message || e}`);
      }
    }
    setIkasJobSummary(
      `Tamamlandı.\nBaşarılı: ${ok}\nAtlandı: ${skipped}\nHatalı: ${bad}`
    );
    setIkasJobDetail(lines);
    setIkasJobRunning(false);
  };

  const handleClickBulkIkasSend = () => {
    if (!sube || sube === 'Bilinmeyen Şube') return;
    let targets = urunler.filter((u) => selectedIds.includes(u.id));
    if (!selectedIds.length) {
      if (
        !window.confirm(
          'Seçili ürün yok. Tüm ürünleri İkas’a göndermek istiyor musunuz?'
        )
      ) {
        return;
      }
      targets = [...urunler];
    }
    setIkasJobTitle('İkas’a toplu gönderim');
    setIkasJobSummary('Başlatılıyor…');
    setIkasJobDetail([]);
    setIkasJobDialogOpen(true);
    setIkasJobRunning(true);
    void runBulkIkasSendBatch(targets);
  };

  const runBulkTrendyolSendBatch = async (targets) => {
    let ok = 0;
    let bad = 0;
    let eksik = 0;
    let atlanan = 0;
    const lines = [];
    const n = targets.length;
    let tyCfg = {};
    try {
      const cfgRes = await fetch(apiUrl('/api/trendyol/config'), {
        method: 'GET',
        headers: await getBackendAuthHeaders()
      });
      const cfgJson = await cfgRes.json().catch(() => ({}));
      if (cfgRes.ok && cfgJson?.success && cfgJson?.config && typeof cfgJson.config === 'object') {
        tyCfg = cfgJson.config;
      }
    } catch {
      tyCfg = {};
    }
    if (!tyCfg || typeof tyCfg !== 'object') tyCfg = {};

    const progressLines = () =>
      `Trendyol: gönderiliyor…\nToplam: ${n}\nBaşarılı: ${ok}\nHatalı: ${bad}\nEksik bilgi: ${eksik}\nAtlanan: ${atlanan}`;

    for (let i = 0; i < n; i += 1) {
      const p = targets[i];
      const barkod = String(p.barkod || p.id || '').trim();
      const subeIdLocal = String(p?.sube || p?.subeId || sube || '').trim();
      setIkasJobSummary(progressLines());

      if (!barkod) {
        bad += 1;
        lines.push(`— | ${p.isim || '-'} | geçersiz barkod`);
        continue;
      }
      if (!subeIdLocal || subeIdLocal === 'Bilinmeyen Şube') {
        bad += 1;
        lines.push(`${barkod} | ${p.isim || '-'} | şube bilgisi yok`);
        continue;
      }
      const matchedBulk = String(p.ty_syncMode || '').trim().toLowerCase() === 'matched';
      const normTy = normalizeTrendyolProductInfo(p, p);
      const resolvedBrand = resolveTrendyolBrandIdForSend(normTy?.ty_brandId, tyCfg);
      if (!matchedBulk && !isTrendyolNumericBrandIdResolved(resolvedBrand)) {
        atlanan += 1;
        lines.push(`${barkod} | ${p.isim || '-'} | atlandı: marka ID yok`);
        continue;
      }

      try {
        const { res, json } = await postTrendyolSaveProductRequest(barkod, subeIdLocal);
        if (!res.ok) {
          bad += 1;
          lines.push(`${barkod} | ${p.isim || '-'} | HTTP ${res.status}`);
          continue;
        }
        const st = String(json.trendyolSyncStatus || '').trim();
        if (json.success === false) {
          if (st === 'skipped_missing_fields') {
            eksik += 1;
            const mf = Array.isArray(json.validation?.missingFields)
              ? json.validation.missingFields.join(', ')
              : '';
            lines.push(`${barkod} | ${p.isim || '-'} | eksik bilgi${mf ? `: ${mf}` : ''}`);
            continue;
          }
          if (st === 'skipped_matched_listing') {
            atlanan += 1;
            lines.push(`${barkod} | ${p.isim || '-'} | atlandı: eşleşmiş listeleme`);
            continue;
          }
          bad += 1;
          const errDetail =
            String(json.message || json.error || '').trim() ||
            st ||
            'Trendyol gönderim hatası';
          lines.push(`${barkod} | ${p.isim || '-'} | ${errDetail}`);
          continue;
        }
        if (st === 'skipped_unchanged' || json.dryRun === true) {
          atlanan += 1;
          lines.push(
            `${barkod} | ${p.isim || '-'} | atlandı: ${json.dryRun ? 'dry_run' : 'değişiklik yok'}`
          );
          continue;
        }
        ok += 1;
      } catch (e) {
        bad += 1;
        console.error('[Trendyol Sync Error]', e);
        lines.push(`${barkod} | ${p.isim || '-'} | ${e?.message || e}`);
      }
    }
    setIkasJobSummary(
      `Toplu Trendyol Gönderim Özeti\nToplam: ${n}\nBaşarılı: ${ok}\nHatalı: ${bad}\nEksik bilgi: ${eksik}\nAtlanan: ${atlanan}`
    );
    setIkasJobDetail(lines);
    setIkasJobRunning(false);
  };

  const handleClickBulkTrendyolSend = () => {
    if (!sube || sube === 'Bilinmeyen Şube') return;
    let targets = urunler.filter((u) => selectedIds.includes(u.id));
    if (!selectedIds.length) {
      if (!window.confirm(`Seçili ürün yok. Tüm ürünleri Trendyol'a göndermek istiyor musunuz?`)) {
        return;
      }
      targets = [...urunler];
    }
    setIkasJobTitle("Trendyol'a toplu gönderim");
    setIkasJobSummary('Başlatılıyor…');
    setIkasJobDetail([]);
    setIkasJobDialogOpen(true);
    setIkasJobRunning(true);
    void runBulkTrendyolSendBatch(targets);
  };

  const handleDelete = async () => {
    if (window.confirm('Emin misiniz?')) {
      try {
        await deleteDoc(doc(db, `subeler/${sube}/urunler`, selectedUrun.id));
        setOpenModal(false);
      } catch (err) { setError(err.message); }
    }
  };

  return (
    <Box
      className="ul-page"
      sx={{
        flex: 1,
        width: '100%',
        minHeight: 0,
        maxHeight: '100%',
        backgroundColor: '#f3f4f6',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'stretch',
        pt: 0.5,
        pb: 0,
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <Card
        className="ul-card"
        sx={{
          width: '100%',
          maxWidth: '1800px',
          flex: '1 1 auto',
          minHeight: 0,
          mx: '1%',
          borderRadius: 3,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: '1px solid #e8edf4',
        }}
      >
        <Box className="ul-header" sx={{ background: 'linear-gradient(90deg, #0f2240 0%, #1f2e4e 42%, #7a3f0d 78%, #9d5317 100%)', px: 2.2, py: 1.1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'white', flexShrink: 0, flexWrap: 'wrap', gap: 1 }}>
           <Box className="ul-header-title-wrap" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
             <DiamondIcon fontSize="medium" />
             <Box>
                <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1 }}>Takı Ürünleri</Typography>
                <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '12px', fontWeight: 500 }}>Toplam {filteredUrunler.length} ürün listeleniyor</Typography>
             </Box>
           </Box>
           
           {/* TOPLU İŞLEM BUTONU */}
           <Box className="ul-header-mid">
           {selectedIds.length > 0 ? (
               <Stack className="ul-header-selection" direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                 <Button
                   variant="contained"
                   color="warning"
                   startIcon={<PlaylistAddCheckIcon />}
                  onClick={() => {
                    setBulkForm(createInitialBulkForm());
                    setOpenBulkModal(true);
                  }}
                   sx={{ fontWeight: 'bold', boxShadow: '0 4px 12px rgba(245, 158, 11, 0.4)' }}
                 >
                   {selectedIds.length} Ürün Seçildi - Düzenle
                 </Button>
                 <Tooltip title="Seçili ürünleri şimdi kontrol eder (en fazla 30). Liste açılınca arka planda da otomatik kontrol çalışır (aynı ürün en sık 6 saatte bir).">
                   <Button
                     variant="outlined"
                     size="small"
                     disabled={ikasVerifyLoading}
                     onClick={() => void handleVerifyIkasRemoteForSelection()}
                     sx={{
                       color: '#fff',
                       borderColor: 'rgba(255,255,255,0.55)',
                       textTransform: 'none',
                       fontWeight: 700,
                       fontSize: '0.75rem',
                       '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' }
                     }}
                   >
                     {ikasVerifyLoading ? 'İkas kontrol…' : 'İkas’ta kontrol et'}
                   </Button>
                 </Tooltip>
               </Stack>
           ) : (
                <TextField className="ul-header-search" variant="outlined" size="small" placeholder="Ara..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#f2e9de', fontSize: 20 }} /></InputAdornment>, sx: { color: '#fdfaf6', backgroundColor: 'rgba(202, 120, 48, 0.5)', borderRadius: 1.6, height: '32px', width: '188px', fontSize: '0.9rem', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.18)' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.42)' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#f8d7b2' } } }} />
           )}
           </Box>
           <Stack className="ul-header-tools" direction="row" spacing={0.75} useFlexGap flexWrap="wrap" justifyContent="flex-end" sx={{ alignItems: 'center' }}>
           <Button
             variant="outlined"
             onClick={() => setOpenIkasSettings(true)}
             sx={{
               color: '#fff',
               borderColor: 'rgba(255,255,255,0.45)',
               textTransform: 'none',
               fontWeight: 700,
               fontSize: '0.72rem',
               py: 0.35,
               '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' }
             }}
           >
             İkas Ayarları
           </Button>
           <Button
             variant="outlined"
             onClick={() => setOpenTrendyolTest(true)}
             sx={{
               color: '#fff',
               borderColor: 'rgba(255,255,255,0.45)',
               textTransform: 'none',
               fontWeight: 700,
               fontSize: '0.72rem',
               py: 0.35,
               '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' }
             }}
           >
             Trendyol Ayarları / Test
           </Button>
           <Button
             variant="contained"
             size="small"
             color="warning"
             onClick={handleClickBulkIkasSend}
             sx={{
               textTransform: 'none',
               fontWeight: 800,
               fontSize: '0.72rem',
               boxShadow: 'none'
             }}
           >
             İkas’a Toplu Gönder
           </Button>
           <Button
             variant="contained"
             size="small"
             onClick={handleClickBulkTrendyolSend}
             sx={{
               textTransform: 'none',
               fontWeight: 800,
               fontSize: '0.72rem',
               boxShadow: 'none',
               bgcolor: '#ea580c',
               '&:hover': { bgcolor: '#c2410c' }
             }}
           >
             Trendyol'a Toplu Gönder
           </Button>
           </Stack>
        </Box>
        <CardContent className="ul-content" sx={{ p: 0, flexGrow: 1, flexShrink: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#fffdf9' }}>
            <Box
              className="ul-tabs"
              sx={{
                borderBottom: '1px solid #ece8df',
                bgcolor: '#fffdf9',
                px: 1.2,
                py: 0.55,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 0.55,
                rowGap: 0.55
              }}
            >
              <Button
                size="small"
                variant={selectedTab === 'Tümü' ? 'contained' : 'outlined'}
                onClick={() => {
                  setSelectedTab('Tümü');
                  setIkasCatMenu(null);
                }}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.78rem',
                  minHeight: 30,
                  px: 1.1,
                  ...(selectedTab === 'Tümü'
                    ? { bgcolor: '#bb6a21', color: '#fff', '&:hover': { bgcolor: '#a85d1c' } }
                    : { color: '#475569', borderColor: '#e2e8f0' })
                }}
              >
                Tümü
              </Button>
              {!ikasCategories.length
                ? altKategoriler
                    .filter((n) => n !== 'Tümü')
                    .map((name) => (
                      <Button
                        key={name}
                        size="small"
                        variant={selectedTab === name ? 'contained' : 'outlined'}
                        onClick={() => setSelectedTab(name)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          fontSize: '0.78rem',
                          minHeight: 30,
                          px: 1.1,
                          ...(selectedTab === name
                            ? { bgcolor: '#bb6a21', color: '#fff', '&:hover': { bgcolor: '#a85d1c' } }
                            : { color: '#475569', borderColor: '#e2e8f0' })
                        }}
                      >
                        {name}
                      </Button>
                    ))
                : ikasRootCategories.map((root) => {
                    const rid = String(root?.id || '').trim();
                    const rname = String(root?.name || '').trim();
                    if (!rid) return null;
                    const kids = ikasCategoryChildrenMap[rid] || [];
                    const branchSelected =
                      selectedTab.startsWith('IKAS:') &&
                      getIkasCategoryExpandedIds(rid).has(selectedTab.replace('IKAS:', ''));
                    if (!kids.length) {
                      return (
                        <Button
                          key={rid}
                          size="small"
                          variant={branchSelected ? 'contained' : 'outlined'}
                          onClick={() => {
                            setSelectedTab(`IKAS:${rid}`);
                            setIkasCatMenu(null);
                          }}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.78rem',
                            minHeight: 30,
                            px: 1.1,
                            maxWidth: 220,
                            ...(branchSelected
                              ? { bgcolor: '#bb6a21', color: '#fff', '&:hover': { bgcolor: '#a85d1c' } }
                              : { color: '#475569', borderColor: '#e2e8f0' })
                          }}
                        >
                          {rname}
                        </Button>
                      );
                    }
                    return (
                      <React.Fragment key={rid}>
                        <Button
                          size="small"
                          variant={branchSelected ? 'contained' : 'outlined'}
                          endIcon={<ArrowDropDownIcon sx={{ fontSize: 20 }} />}
                          onClick={(e) => {
                            setIkasCatMenu((prev) =>
                              prev?.rootId === rid && prev.anchorEl === e.currentTarget
                                ? null
                                : { anchorEl: e.currentTarget, rootId: rid }
                            );
                          }}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '0.78rem',
                            minHeight: 30,
                            px: 1.1,
                            maxWidth: 240,
                            ...(branchSelected
                              ? { bgcolor: '#bb6a21', color: '#fff', '&:hover': { bgcolor: '#a85d1c' } }
                              : { color: '#475569', borderColor: '#e2e8f0' })
                          }}
                        >
                          {rname}
                        </Button>
                        <Menu
                          anchorEl={ikasCatMenu?.rootId === rid ? ikasCatMenu.anchorEl : null}
                          open={ikasCatMenu?.rootId === rid && Boolean(ikasCatMenu?.anchorEl)}
                          onClose={() => setIkasCatMenu(null)}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                          slotProps={{ paper: { sx: { maxHeight: 380 } } }}
                        >
                          <MenuItem
                            dense
                            onClick={() => {
                              setSelectedTab(`IKAS:${rid}`);
                              setIkasCatMenu(null);
                            }}
                            sx={{ fontWeight: 700, fontSize: '0.82rem' }}
                          >
                            {rname} — tümü (altlar dahil)
                          </MenuItem>
                          <Divider sx={{ my: 0.35 }} />
                          {flattenDescendantCategoryRows(rid).map((row) => (
                            <MenuItem
                              key={row.id}
                              dense
                              onClick={() => {
                                setSelectedTab(`IKAS:${row.id}`);
                                setIkasCatMenu(null);
                              }}
                              sx={{ pl: 1.25 + row.depth * 1.15, fontSize: '0.8rem' }}
                            >
                              {row.name}
                            </MenuItem>
                          ))}
                        </Menu>
                      </React.Fragment>
                    );
                  })}
            </Box>
            <TableContainer className="ul-table-wrap" sx={{ flexGrow: 1, flexShrink: 1, minHeight: 0, overflowX: 'auto', overflowY: 'auto', width: '100%', minWidth: 0, WebkitOverflowScrolling: 'touch' }}>
                <Box className="ul-mobile-list-head" aria-hidden="true">
                  <span>Ürün</span>
                  <span>Fiyat</span>
                  <span>Detay</span>
                </Box>
                <Table
                  className="ul-table"
                  stickyHeader
                  size="small"
                  sx={{
                    tableLayout: 'fixed',
                    width: '100%',
                    /** Sabit kolonlar + ürün adı min 260px; dar ekranda yatay kaydırma */
                    minWidth: 1186,
                    '& .MuiTableCell-root': { verticalAlign: 'middle', py: 0.28, px: 0.45, boxSizing: 'border-box' },
                  }}
                >
                    <colgroup>
                      <col style={{ width: 36 }} />
                      <col style={{ width: 48 }} />
                      {/* Ürün adı: 260–360px aralığı */}
                      <col style={{ width: 360, minWidth: 260 }} />
                      <col style={{ width: 135 }} />
                      <col style={{ width: 150 }} />
                      <col style={{ width: 70 }} />
                      <col style={{ width: 105 }} />
                      <col style={{ width: 105 }} />
                      <col style={{ width: 105 }} />
                      <col style={{ width: 76 }} />
                      <col style={{ width: 96 }} />
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            {/* SEÇİM KUTUSU BAŞLIK (Sadece mevcut sayfa kontrolü) */}
                            <TableCell padding="checkbox" sx={{ width: 36, maxWidth: 36, minWidth: 36, px: 0.25, bgcolor: '#fffdf8', borderBottom: '1px solid #eee7da' }}>
                                <Checkbox
                                    color="primary"
                                    indeterminate={isPageIndeterminate}
                                    checked={isPageAllSelected}
                                    onChange={handleSelectAllClick}
                                    size="small"
                                />
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 800, bgcolor: '#fffdf8', color: '#374151', borderBottom: '1px solid #eee7da', fontSize: '13px', px: 0.35, minWidth: 48 }}>Görsel</TableCell>
                            <TableCell sx={{ fontWeight: 800, bgcolor: '#fffdf8', color: '#374151', borderBottom: '1px solid #eee7da', py: 0.45, fontSize: '13.5px', minWidth: 260, maxWidth: 360, width: '1%', overflow: 'hidden' }}>Ürün adı</TableCell>
                            <TableCell sx={{ px: 0.25, fontWeight: 800, bgcolor: '#fffdf8', color: '#374151', borderBottom: '1px solid #eee7da', fontSize: '13px' }}>Barkod</TableCell>
                            <TableCell sx={{ px: 0.25, fontWeight: 800, bgcolor: '#fffdf8', color: '#374151', borderBottom: '1px solid #eee7da', fontSize: '13px' }}>Kategori</TableCell>
                            <TableCell align="center" sx={{ px: 0.2, width: 70, maxWidth: 70, fontWeight: 800, bgcolor: '#fffdf8', color: '#374151', borderBottom: '1px solid #eee7da', fontSize: '12.5px', whiteSpace: 'nowrap' }}>Gram</TableCell>
                            <TableCell align="right" sx={{ px: 0.2, width: 105, maxWidth: 105, fontWeight: 800, bgcolor: '#fffdf8', color: '#374151', borderBottom: '1px solid #eee7da', fontSize: '12px', whiteSpace: 'nowrap' }}>Mağaza</TableCell>
                            <TableCell align="right" sx={{ px: 0.2, width: 105, maxWidth: 105, fontWeight: 800, bgcolor: '#fffdf8', color: '#374151', borderBottom: '1px solid #eee7da', fontSize: '12px', whiteSpace: 'nowrap' }}>Web</TableCell>
                            <TableCell align="right" sx={{ px: 0.2, width: 105, maxWidth: 105, fontWeight: 800, bgcolor: '#fffdf8', color: '#374151', borderBottom: '1px solid #eee7da', fontSize: '12px', whiteSpace: 'nowrap' }}>Pazaryeri</TableCell>
                            <TableCell align="center" sx={{ px: 0.22, width: 76, maxWidth: 76, fontWeight: 800, bgcolor: '#fffdf8', color: '#374151', borderBottom: '1px solid #eee7da', fontSize: '12.5px', whiteSpace: 'nowrap' }}>İkas</TableCell>
                            <TableCell align="center" sx={{ px: 0.22, width: 96, maxWidth: 96, fontWeight: 800, bgcolor: '#fffdf8', color: '#374151', borderBottom: '1px solid #eee7da', fontSize: '12.5px', whiteSpace: 'nowrap' }}>Trendyol</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>{currentItems.map((urun) => {
                            const isSelected = selectedIds.indexOf(urun.id) !== -1;
                            const autoCat = getAutoCategory(urun.isim);
                            const kategoriEtiketleri = Array.isArray(urun?.ikasCategoryNamesSnapshot) && urun.ikasCategoryNamesSnapshot.length > 0
                              ? urun.ikasCategoryNamesSnapshot.filter(Boolean)
                              : (String(urun?.urunKategorisi || '').trim()
                                ? [String(urun.urunKategorisi).trim()]
                                : [autoCat]);
                            return (<TableRow 
                                        key={urun.id}
                                        className="ul-data-row"
                                        hover 
                                        onClick={(event) => handleClick(event, urun.id)}
                                        selected={isSelected}
                                        sx={{
                                          cursor: 'pointer',
                                          height: 51,
                                          maxHeight: 51,
                                          boxSizing: 'border-box',
                                          '&:hover': { backgroundColor: '#fff8ef' },
                                          '& .MuiTableCell-root': {
                                            borderBottom: '1px solid #f1ece3',
                                            height: 51,
                                            maxHeight: 51,
                                            py: 0.12,
                                            fontSize: '13.5px',
                                            verticalAlign: 'middle',
                                            boxSizing: 'border-box',
                                            overflow: 'hidden',
                                          },
                                        }}
                                    >
                                    {/* SEÇİM KUTUSU SATIR */}
                                    <TableCell className="ul-cell-select" data-label="Seç" padding="checkbox" sx={{ width: 36, maxWidth: 36, minWidth: 36, px: 0.25 }}>
                                        <Checkbox color="primary" checked={isSelected} />
                                    </TableCell>

                                    <TableCell className="ul-cell-image" data-label="Görsel" align="center" onClick={(e) => { e.stopPropagation(); handleOpenEdit(urun); }} sx={{ px: 0.35, verticalAlign: 'middle' }}>
                                        <Tooltip
                                          title={
                                            urun.gorselURL1 ? (
                                              <Box
                                                component="img"
                                                alt=""
                                                src={urun.gorselURL1}
                                                sx={{
                                                  display: 'block',
                                                  maxWidth: 200,
                                                  maxHeight: 200,
                                                  width: 'auto',
                                                  height: 'auto',
                                                  objectFit: 'contain'
                                                }}
                                              />
                                            ) : (
                                              'Görsel yok'
                                            )
                                          }
                                          enterDelay={300}
                                          slotProps={{
                                            tooltip: {
                                              sx: {
                                                bgcolor: 'background.paper',
                                                border: '1px solid #e2e8f0',
                                                boxShadow: 3,
                                                p: 0.5,
                                                maxWidth: 220
                                              }
                                            }
                                          }}
                                        >
                                          <Avatar
                                            src={urun.gorselURL1}
                                            variant="rounded"
                                            sx={{
                                              width: 24,
                                              height: 24,
                                              bgcolor: '#f2f4f8',
                                              color: '#1e3a8a',
                                              border: '1px solid #e2e8f0'
                                            }}
                                          >
                                            {!urun.gorselURL1 && <ImageIcon fontSize="small" />}
                                          </Avatar>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell className="ul-cell-name" data-label="Ürün" onClick={(e) => { e.stopPropagation(); handleOpenEdit(urun); }} sx={{ overflow: 'hidden', minWidth: 260, maxWidth: 360, width: '1%', px: 0.4 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.22, minWidth: 0, minHeight: 28 }}>
                                            <Typography
                                              variant="subtitle2"
                                              fontWeight={700}
                                              sx={{
                                                fontSize: '13px',
                                                color: '#111827',
                                                lineHeight: 1.1,
                                                minWidth: 0,
                                                minHeight: 28,
                                                flex: '1 1 auto',
                                                overflow: 'hidden',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                wordBreak: 'break-word',
                                                alignSelf: 'center',
                                              }}
                                            >
                                              {urun.isim}
                                            </Typography>
                                            <Tooltip title="İsmi Kopyala">
                                                <IconButton size="small" sx={{ flexShrink: 0, p: 0.25, alignSelf: 'center' }} onClick={(e) => { e.stopPropagation(); handleCopyToClipboard(urun.isim, 'İsim'); }}>
                                                    <ContentCopyIcon sx={{ fontSize: 15, color: '#94a3b8' }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                    <TableCell className="ul-cell-barcode" data-label="Barkod" sx={{ minWidth: 0, overflow: 'hidden', px: 0.25 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2, minWidth: 0 }}>
                                            <Typography sx={{ fontFamily: 'monospace', fontSize: '12.5px', color: '#7b8798', fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{urun.barkod}</Typography>
                                            <Tooltip title="Barkodu Kopyala">
                                                <IconButton size="small" sx={{ flexShrink: 0 }} onClick={(e) => { e.stopPropagation(); handleCopyToClipboard(urun.barkod, 'Barkod'); }}>
                                                    <ContentCopyIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                    <TableCell className="ul-cell-category" data-label="Kategori" sx={{ minWidth: 0, overflow: 'hidden', px: 0.25 }}>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', alignContent: 'center', justifyContent: 'flex-start', gap: 0.22, rowGap: 0.18, height: 34, minHeight: 34, maxHeight: 34, overflow: 'hidden', boxSizing: 'border-box' }}>
                                        {kategoriEtiketleri.slice(0, 2).map((etiket, idx) => (
                                          <Chip
                                            key={`${urun.id}-cat-${idx}-${etiket}`}
                                            label={etiket}
                                            size="small"
                                            sx={{
                                              height: 16,
                                              maxWidth: '100%',
                                              fontSize: '10px',
                                              bgcolor: '#fff6e5',
                                              color: '#8a5b22',
                                              border: '1px solid #f7dfb5',
                                              fontWeight: 700,
                                              '& .MuiChip-label': { px: 0.45, lineHeight: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
                                            }}
                                          />
                                        ))}
                                        {kategoriEtiketleri.length > 2 && (
                                          <Chip
                                            label={`+${kategoriEtiketleri.length - 2}`}
                                            size="small"
                                            sx={{
                                              height: 16,
                                              minWidth: 26,
                                              fontSize: '9px',
                                              bgcolor: '#f8fafc',
                                              color: '#64748b',
                                              border: '1px solid #e2e8f0',
                                              fontWeight: 700,
                                              flexShrink: 0,
                                              '& .MuiChip-label': { px: 0.35, lineHeight: 1 }
                                            }}
                                          />
                                        )}
                                      </Box>
                                    </TableCell>

                                    <TableCell className="ul-cell-gram" data-label="Gram" align="center" sx={{ width: 70, maxWidth: 70, px: 0.18, verticalAlign: 'middle', fontWeight: 700, fontSize: '13px', whiteSpace: 'nowrap' }}>{urun.gram ? `${urun.gram} gr` : '-'}</TableCell>

                                    <TableCell className="ul-cell-store" data-label="Mağaza" align="right" sx={{ overflow: 'hidden', px: 0.2, verticalAlign: 'middle' }}>
                                      <Typography variant="body2" fontWeight={700} noWrap sx={{ color: '#111827', fontSize: '12.5px' }}>{displayMagazaPriceNum(urun) > 0 ? `${displayMagazaPriceNum(urun).toLocaleString('tr-TR', { maximumFractionDigits: 0 })} ₺` : '-'}</Typography>
                                    </TableCell>
                                    <TableCell className="ul-cell-web" data-label="Web" align="right" sx={{ overflow: 'hidden', px: 0.2, verticalAlign: 'middle', height: '100%' }}>
                                      {webHasListAndDiscountedPair(urun) ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', gap: 0, lineHeight: 1.03, minWidth: 0, overflow: 'hidden', minHeight: 26, maxHeight: 26 }}>
                                          <Typography variant="caption" noWrap sx={{ color: '#64748b', fontWeight: 600, fontSize: '9px', textDecoration: 'line-through', width: '100%', textAlign: 'right' }}>
                                            {webListeNum(urun).toLocaleString('tr-TR', { maximumFractionDigits: 0 })} ₺
                                          </Typography>
                                          <Typography variant="body2" fontWeight={800} noWrap sx={{ color: '#0f766e', fontSize: '10.75px', width: '100%', textAlign: 'right' }}>
                                            {webIndirimliNum(urun).toLocaleString('tr-TR', { maximumFractionDigits: 0 })} ₺
                                          </Typography>
                                        </Box>
                                      ) : (
                                        <Typography variant="body2" fontWeight={700} noWrap sx={{ color: '#0f766e', fontSize: '12.5px' }}>
                                          {webListeNum(urun) > 0
                                            ? `${webListeNum(urun).toLocaleString('tr-TR', { maximumFractionDigits: 0 })} ₺`
                                              : '-'}
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell className="ul-cell-marketplace" data-label="Pazaryeri" align="right" sx={{ overflow: 'hidden', px: 0.2, verticalAlign: 'middle' }}>
                                      <Typography variant="body2" fontWeight={700} noWrap sx={{ color: '#b45309', fontSize: '12.5px' }}>{displayPazaryeriPriceNum(urun) > 0 ? `${displayPazaryeriPriceNum(urun).toLocaleString('tr-TR', { maximumFractionDigits: 0 })} ₺` : '-'}</Typography>
                                    </TableCell>
                                    <TableCell className="ul-cell-ikas" data-label="İkas" align="center" sx={{ px: 0.18, py: 0.1, verticalAlign: 'middle', '& > span': { display: 'inline-flex', justifyContent: 'center', width: '100%' } }} onClick={(e) => e.stopPropagation()}>
                                      {(() => {
                                        const ik = computeIkasMarketplaceTableButton(urun);
                                        return (
                                          <Tooltip title={ik.tooltip}>
                                            <span>
                                              <Button
                                                size="small"
                                                variant="contained"
                                                disableElevation
                                                disabled={ik.disabled}
                                                onClick={(ev) => {
                                                  ev.stopPropagation();
                                                  if (ik.href) window.open(ik.href, '_blank', 'noopener,noreferrer');
                                                }}
                                                sx={marketplaceTableBtnSx(ik.greenLook)}
                                              >
                                                İkas
                                              </Button>
                                            </span>
                                          </Tooltip>
                                        );
                                      })()}
                                    </TableCell>
                                    <TableCell className="ul-cell-trendyol" data-label="Trendyol" align="center" sx={{ px: 0.18, py: 0.1, verticalAlign: 'middle', '& > span': { display: 'inline-flex', justifyContent: 'center', width: '100%' } }} onClick={(e) => e.stopPropagation()}>
                                      {(() => {
                                        const ty = computeTrendyolMarketplaceTableButton(urun);
                                        const tyPid = String(urun?.realBarkod ?? urun?.id ?? urun?.barkod ?? '').trim();
                                        const tyBusy = tyPid ? Boolean(trendyolListRowRefreshBusy[tyPid]) : false;
                                        const btnDisabled =
                                          ty.tableVariant === 'error' ||
                                          (ty.tableVariant === 'pending' && tyBusy) ||
                                          (ty.tableVariant === 'sale' && ty.disabled) ||
                                          (ty.tableVariant === 'closed' && ty.disabled);
                                        const tableVariantSafe =
                                          ty.tableVariant === 'sale' ||
                                          ty.tableVariant === 'pending' ||
                                          ty.tableVariant === 'error' ||
                                          ty.tableVariant === 'closed'
                                            ? ty.tableVariant
                                            : 'closed';
                                        return (
                                          <Tooltip title={ty.tooltip}>
                                            <span>
                                              <Button
                                                size="small"
                                                variant="contained"
                                                disableElevation
                                                disabled={btnDisabled}
                                                onClick={(ev) => {
                                                  ev.stopPropagation();
                                                  if (ty.isPendingRefresh && !tyBusy) {
                                                    void handleTrendyolListProductRowRefresh(urun);
                                                    return;
                                                  }
                                                  if (ty.tableVariant === 'sale' && ty.href)
                                                    window.open(ty.href, '_blank', 'noopener,noreferrer');
                                                }}
                                                sx={trendyolMarketplaceTableBtnSx(tableVariantSafe)}
                                              >
                                                {tyBusy && ty.isPendingRefresh ? (
                                                  <CircularProgress size={13} sx={{ color: '#fff' }} thickness={6} />
                                                ) : (
                                                  ty.label
                                                )}
                                              </Button>
                                            </span>
                                          </Tooltip>
                                        );
                                      })()}
                                    </TableCell>
                                </TableRow>); })}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box
              className="ul-footer"
              sx={{
                flexShrink: 0,
                p: 1.1,
                borderTop: '1px solid #ece8df',
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) auto minmax(0, 1fr)' },
                alignItems: 'center',
                gap: { xs: 1.25, sm: 1 },
                bgcolor: '#fffdf9'
              }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifySelf: { xs: 'stretch', sm: 'start' }, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleSelectAllFilteredProducts}
                  disabled={!filteredUrunler.length}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    borderColor: '#e2e8f0',
                    color: '#475569',
                    py: 0.35,
                    px: 1.25,
                    '&:hover': { borderColor: '#bb6a21', color: '#0f2240', bgcolor: 'rgba(187,106,33,0.06)' }
                  }}
                >
                  Tümünü seç
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleClearProductSelection}
                  disabled={!selectedIds.length}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    borderColor: '#e2e8f0',
                    color: '#475569',
                    py: 0.35,
                    px: 1.25,
                    '&:hover': { borderColor: '#64748b', color: '#0f172a', bgcolor: 'rgba(100,116,139,0.08)' },
                    '&.Mui-disabled': { borderColor: '#f1f5f9', color: '#cbd5e1' }
                  }}
                >
                  Seçimi kaldır
                </Button>
              </Box>
              <Pagination
                count={pageCount}
                page={currentPage}
                onChange={(e, val) => setCurrentPage(val)}
                color="primary"
                shape="rounded"
                size="small"
                sx={{
                  justifySelf: 'center',
                  gridColumn: { xs: '1', sm: '2' },
                  '& .MuiPaginationItem-root': { fontSize: '13px', minWidth: 30, height: 30 },
                  '& .Mui-selected': { bgcolor: '#b66a1f !important', color: '#fff' }
                }}
              />
              <Box sx={{ display: { xs: 'none', sm: 'block' }, minWidth: 0 }} aria-hidden />
            </Box>
        </CardContent>
      </Card>

      {/* --- TOPLU DÜZENLEME MODALI --- */}
      <Dialog className="ul-bulk-dialog" open={openBulkModal} onClose={handleCloseBulkModal} maxWidth="md" fullWidth PaperProps={{ className: 'ul-bulk-dialog-paper' }}>
          <DialogTitle sx={{ bgcolor: '#f59e0b', color: 'white' }}>Toplu Ürün Düzenle ({selectedIds.length} Ürün)</DialogTitle>
          <DialogContent dividers sx={{ pt: 2.5, pb: 2 }}>
              <Alert severity="info" sx={{ mb: 2.5 }}>
                Sadece doldurduğunuz alanlar güncellenir; boş alanlar aynı kalır. Fiyat satırları mağaza / web / Trendyol sekmelerindeki liste ve indirimli satışla aynıdır.
              </Alert>
              <Stack spacing={2.75}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 700, color: 'text.secondary' }}>
                      Ürün kategorisi (İkas)
                    </Typography>
                    <Box sx={{ width: '100%' }}>
                      {ikasCategories.length > 0 ? (
                        <>
                          <TextField
                            fullWidth
                            size="small"
                            select
                            label="Seçili ürünlerde uygulanacak kategoriler"
                            value={Array.isArray(bulkForm.ikasCategoryIds) ? bulkForm.ikasCategoryIds : []}
                            onChange={(e) => handleBulkCategorySelect(e.target.value)}
                            sx={modalInputSx}
                            SelectProps={{
                              multiple: true,
                              MenuProps: {
                                PaperProps: {
                                  sx: {
                                    maxHeight: 320,
                                    maxWidth: 'min(100vw - 32px, 420px)'
                                  }
                                },
                                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                transformOrigin: { vertical: 'top', horizontal: 'left' }
                              }
                            }}
                            helperText="Üst kategoriler ürün detayındaki gibi otomatik eklenir."
                          >
                            <MenuItem value="">Seçiniz</MenuItem>
                            {ikasCategories.map((cat) => (
                              <MenuItem key={cat.id} value={cat.id} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                {cat.name}
                              </MenuItem>
                            ))}
                          </TextField>
                          <FormControlLabel
                            sx={{ mt: 0.5 }}
                            control={
                              <Checkbox
                                checked={!!bulkForm.bulkCategoryMerge}
                                onChange={(e) =>
                                  setBulkForm((prev) => ({ ...prev, bulkCategoryMerge: e.target.checked }))
                                }
                              />
                            }
                            label="Mevcut ürün kategorilerini koru ve seçtiklerimi üzerine ekle"
                          />
                        </>
                      ) : (
                        <TextField
                          fullWidth
                          size="small"
                          label="Ürün kategorisi (metin)"
                          name="urunKategorisi"
                          value={bulkForm.urunKategorisi || ''}
                          onChange={handleBulkInputChange}
                          sx={modalInputSx}
                          helperText="İkas kategorileri yüklenmediyse metin alanı; doldurursanız seçili tüm ürünlere yazılır."
                        />
                      )}
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="İşçilik milyemi"
                      name="iscilik"
                      value={bulkForm.iscilik}
                      onChange={handleBulkInputChange}
                      sx={modalInputSx}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Adet işçiliği"
                      name="adetIscilik"
                      value={bulkForm.adetIscilik}
                      onChange={handleBulkInputChange}
                      sx={modalInputSx}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Stok adedi"
                      name="stok"
                      type="number"
                      value={bulkForm.stok}
                      onChange={handleBulkInputChange}
                      sx={modalInputSx}
                    />
                  </Grid>
                </Grid>

                <Divider />

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: '#e5e7eb', bgcolor: '#fafafa' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                    Kanal fiyatları
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1.45 }}>
                    Web satış fiyatı = web fiyat alanı; indirim varsa “Satış (indirimli)” alanında görünür. «Otomatik fiyata dön» yalnızca o satırı formül / çarpanlara döndürür (indirim sıfırlanır).
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, height: '100%' }}>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.9rem' }}>Mağaza</Typography>
                        <Typography variant="caption" color="text.secondary">Mağaza sekmesi</Typography>
                        <Stack spacing={1.1} sx={{ mt: 1.1 }}>
                          <TextField fullWidth size="small" label="Satış fiyatı (liste)" name="magazaListe" type="number" value={bulkForm.magazaListe} onChange={handleBulkInputChange} disabled={bulkForm.bulkMagazaAuto} sx={modalInputSx} />
                          <TextField fullWidth size="small" label="İndirimli satış" name="magazaIndirimli" type="number" value={bulkForm.magazaIndirimli} onChange={handleBulkInputChange} disabled={bulkForm.bulkMagazaAuto} sx={modalInputSx} />
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            startIcon={<RestartAltIcon />}
                            disabled={bulkForm.bulkMagazaAuto}
                            onClick={() => setBulkForm((prev) => ({ ...prev, bulkMagazaAuto: true, magazaListe: '', magazaIndirimli: '' }))}
                            sx={{ textTransform: 'none', fontWeight: 700 }}
                          >
                            Otomatik fiyata dön
                          </Button>
                        </Stack>
                      </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, height: '100%' }}>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#0f766e' }}>Web</Typography>
                        <Typography variant="caption" color="text.secondary">Web sekmesi</Typography>
                        <Stack spacing={1.1} sx={{ mt: 1.1 }}>
                          <TextField fullWidth size="small" label="Satış fiyatı (liste)" name="webListe" type="number" value={bulkForm.webListe} onChange={handleBulkInputChange} disabled={bulkForm.bulkWebAuto} sx={modalInputSx} />
                          <TextField fullWidth size="small" label="İndirimli satış" name="webIndirimli" type="number" value={bulkForm.webIndirimli} onChange={handleBulkInputChange} disabled={bulkForm.bulkWebAuto} sx={modalInputSx} />
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            startIcon={<RestartAltIcon />}
                            disabled={bulkForm.bulkWebAuto}
                            onClick={() => setBulkForm((prev) => ({ ...prev, bulkWebAuto: true, webListe: '', webIndirimli: '' }))}
                            sx={{ textTransform: 'none', fontWeight: 700 }}
                          >
                            Otomatik fiyata dön
                          </Button>
                        </Stack>
                      </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, height: '100%' }}>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#c2410c' }}>Trendyol</Typography>
                        <Typography variant="caption" color="text.secondary">Pazaryeri sekmesi</Typography>
                        <Stack spacing={1.1} sx={{ mt: 1.1 }}>
                          <TextField fullWidth size="small" label="Satış fiyatı (liste)" name="pazarListe" type="number" value={bulkForm.pazarListe} onChange={handleBulkInputChange} disabled={bulkForm.bulkPazarAuto} sx={modalInputSx} />
                          <TextField fullWidth size="small" label="İndirimli satış" name="pazarIndirimli" type="number" value={bulkForm.pazarIndirimli} onChange={handleBulkInputChange} disabled={bulkForm.bulkPazarAuto} sx={modalInputSx} />
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            startIcon={<RestartAltIcon />}
                            disabled={bulkForm.bulkPazarAuto}
                            onClick={() => setBulkForm((prev) => ({ ...prev, bulkPazarAuto: true, pazarListe: '', pazarIndirimli: '' }))}
                            sx={{ textTransform: 'none', fontWeight: 700 }}
                          >
                            Otomatik fiyata dön
                          </Button>
                        </Stack>
                      </Paper>
                    </Grid>
                  </Grid>
                  {bulkForm.bulkMagazaAuto || bulkForm.bulkWebAuto || bulkForm.bulkPazarAuto ? (
                    <Alert severity="info" sx={{ mt: 2, py: 0.65, '& .MuiAlert-message': { width: '100%' } }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                        Otomatik seçili kanallar
                      </Typography>
                      <Typography variant="caption" component="div" sx={{ mt: 0.35, lineHeight: 1.45 }}>
                        {bulkForm.bulkMagazaAuto ? 'Mağaza: gram × kur × çarpan; yukarı 10 TL yuvarlama; indirim sıfır. ' : ''}
                        {bulkForm.bulkWebAuto ? 'Web: mağaza liste × 1,15; yukarı 10 TL yuvarlama; web indirim/kampanya sıfır. ' : ''}
                        {bulkForm.bulkPazarAuto ? 'Trendyol: mağaza liste × 1,35; yukarı 10 TL yuvarlama; kampanya sıfır.' : ''}
                      </Typography>
                    </Alert>
                  ) : null}
                </Paper>

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: '#dbeafe', bgcolor: '#f8fbff' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
                    Web satış detayları (toplu)
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1.45 }}>
                    Ürün detayı &gt; Web sekmesindeki Satış detayları ile aynıdır. Boş bırakılan alanlar değişmez.
                  </Typography>
                  <Grid container spacing={1.2}>
                    <Grid size={{ xs: 6, md: 4 }}>
                      <TextField fullWidth size="small" label="Kredi Kartı Komisyonu" name="kkKomisyonu" type="number" value={bulkForm.kkKomisyonu} onChange={handleBulkInputChange} sx={modalInputSx} InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
                    </Grid>
                    <Grid size={{ xs: 6, md: 4 }}>
                      <TextField fullWidth size="small" label="KDV Oranı" name="kdvOrani" type="number" value={bulkForm.kdvOrani} onChange={handleBulkInputChange} sx={modalInputSx} InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} helperText="Varsayılan: %20" />
                    </Grid>
                    <Grid size={{ xs: 6, md: 4 }}>
                      <TextField fullWidth size="small" label="Ek Masraf" name="ekMasraf" type="number" value={bulkForm.ekMasraf} onChange={handleBulkInputChange} sx={modalInputSx} InputProps={{ endAdornment: <InputAdornment position="end">TL</InputAdornment> }} />
                    </Grid>
                    <Grid size={{ xs: 6, md: 4 }}>
                      <TextField fullWidth size="small" label="Kargo Bedeli" name="kargoBedeli" type="number" value={bulkForm.kargoBedeli} onChange={handleBulkInputChange} sx={modalInputSx} InputProps={{ endAdornment: <InputAdornment position="end">TL</InputAdornment> }} />
                    </Grid>
                    <Grid size={{ xs: 6, md: 4 }}>
                      <TextField fullWidth size="small" label="Müşteriye yapılan indirim" name="ekIndirim" type="number" value={bulkForm.ekIndirim} onChange={handleBulkInputChange} sx={modalInputSx} InputProps={{ endAdornment: <InputAdornment position="end">TL</InputAdornment> }} />
                    </Grid>
                    <Grid size={{ xs: 6, md: 4 }}>
                      <TextField fullWidth size="small" label="Müşteriye yapılan % indirim" name="indirimYuzde" type="number" value={bulkForm.indirimYuzde} onChange={handleBulkInputChange} sx={modalInputSx} InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
                    </Grid>
                  </Grid>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: '#fed7aa', bgcolor: '#fffdfb' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5, color: '#9a3412' }}>
                    Trendyol satış detayları (toplu)
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1.45 }}>
                    Ürün detayı &gt; Trendyol sekmesindeki Satış detayları ile aynı alanları topluca günceller. Boş bırakılan alanlar değişmez. Kampanya tipi için «Değiştirme» seçiliyse kampanya alanlarına dokunulmaz.
                  </Typography>
                  <Grid container spacing={1.2}>
                    <Grid size={{ xs: 6, md: 4 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Komisyon"
                        name="tyKomisyonOrani"
                        type="number"
                        value={bulkForm.tyKomisyonOrani}
                        onChange={handleBulkInputChange}
                        sx={modalInputSx}
                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, md: 4 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Kargo"
                        name="tyKargoUcreti"
                        type="number"
                        value={bulkForm.tyKargoUcreti}
                        onChange={handleBulkInputChange}
                        sx={modalInputSx}
                        InputProps={{ endAdornment: <InputAdornment position="end">TL</InputAdornment> }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, md: 4 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Ek masraf"
                        name="tyEkMasraf"
                        type="number"
                        value={bulkForm.tyEkMasraf}
                        onChange={handleBulkInputChange}
                        sx={modalInputSx}
                        InputProps={{ endAdornment: <InputAdornment position="end">TL</InputAdornment> }}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, md: 4 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Kar marjı (not)"
                        name="tyKarMarji"
                        type="number"
                        value={bulkForm.tyKarMarji}
                        onChange={handleBulkInputChange}
                        sx={modalInputSx}
                        helperText="Kayıtta ty_karMarji olarak saklanır"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <FormControl fullWidth size="small" sx={modalInputSx}>
                        <InputLabel id="bulk-ty-kampanya-tip">Kampanya tipi</InputLabel>
                        <Select
                          labelId="bulk-ty-kampanya-tip"
                          name="tyKampanyaTipi"
                          value={bulkForm.tyKampanyaTipi || ''}
                          label="Kampanya tipi"
                          onChange={handleBulkInputChange}
                        >
                          <MenuItem value="">
                            <em>Değiştirme</em>
                          </MenuItem>
                          <MenuItem value="yok">Kampanya yok</MenuItem>
                          <MenuItem value="yuzde">Yüzde indirim (%)</MenuItem>
                          <MenuItem value="tutar">Tutar indirimi (TL)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Kampanya / indirim değeri"
                        name="tyKampanyaDegeri"
                        type="number"
                        value={bulkForm.tyKampanyaDegeri}
                        onChange={handleBulkInputChange}
                        disabled={!String(bulkForm.tyKampanyaTipi || '').trim() || bulkForm.tyKampanyaTipi === 'yok'}
                        sx={modalInputSx}
                        helperText="Kampanya tipi seçilirse uygulanır"
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, mb: 0.5 }}>
                        Gönderim: Trendyol ürün detayındaki KDV / desi / kargo / teslimat / kategori alanları (Firestore: ty_*). Boş bırakılan satırlarda mevcut değerler korunur.
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, md: 4 }}>
                      <FormControl fullWidth size="small" sx={modalInputSx}>
                        <InputLabel id="bulk-ty-vat">KDV (%)</InputLabel>
                        <Select
                          labelId="bulk-ty-vat"
                          name="ty_vatRate"
                          value={bulkForm.ty_vatRate ?? ''}
                          label="KDV (%)"
                          onChange={handleBulkInputChange}
                          renderValue={(selected) =>
                            selected !== '' && selected != null ? `%${String(selected)}` : ''
                          }
                        >
                          <MenuItem value="">
                            <em>Değiştirme</em>
                          </MenuItem>
                          {TRENDYOL_VAT_RATE_OPTIONS.map((opt) => (
                            <MenuItem key={`bulk-tyvat-${opt}`} value={opt}>
                              %{opt}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 6, md: 4 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Desi"
                        name="ty_dimensionalWeight"
                        type="number"
                        value={bulkForm.ty_dimensionalWeight}
                        onChange={handleBulkInputChange}
                        sx={modalInputSx}
                        inputProps={{ step: 'any', min: 0 }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <FormControl fullWidth size="small" sx={modalInputSx}>
                        <InputLabel id="bulk-ty-cargo-co">Kargo firması</InputLabel>
                        <Select
                          labelId="bulk-ty-cargo-co"
                          name="ty_cargoCompanyId"
                          value={bulkForm.ty_cargoCompanyId ?? ''}
                          label="Kargo firması"
                          onChange={handleBulkInputChange}
                          renderValue={(selected) => {
                            const raw = selected !== '' && selected != null ? String(selected).trim() : '';
                            const key = /^\d+$/.test(raw) ? String(Number(raw)) : raw;
                            return key ? TRENDYOL_CARGO_COMPANY_LABEL_BY_ID[key] || `ID ${key}` : '';
                          }}
                        >
                          <MenuItem value="">
                            <em>Değiştirme</em>
                          </MenuItem>
                          {TRENDYOL_CARGO_COMPANY_SELECT_IDS.map((cid) => (
                            <MenuItem key={`bulk-tycargo-${cid}`} value={cid}>
                              {TRENDYOL_CARGO_COMPANY_LABEL_BY_ID[cid] || cid}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 6, md: 4 }}>
                      <FormControl fullWidth size="small" sx={modalInputSx}>
                        <InputLabel id="bulk-ty-delivery">Teslimat</InputLabel>
                        <Select
                          labelId="bulk-ty-delivery"
                          name="ty_deliveryDuration"
                          value={bulkForm.ty_deliveryDuration ?? ''}
                          label="Teslimat"
                          onChange={handleBulkInputChange}
                          renderValue={(selected) =>
                            selected !== '' && selected != null ? `${selected} gün` : ''
                          }
                        >
                          <MenuItem value="">
                            <em>Değiştirme</em>
                          </MenuItem>
                          {TRENDYOL_DELIVERY_DURATION_OPTIONS.map((d) => (
                            <MenuItem key={`bulk-ty-del-${d}`} value={d}>
                              {d} gün
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, md: 8 }}>
                      <Stack direction="column" spacing={0.85} sx={{ minWidth: 0 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.85} alignItems={{ sm: 'center' }}>
                          <Autocomplete
                            fullWidth
                            options={bulkTyCategoryOptions}
                            value={bulkTrendyolCategoryLeaf}
                            inputValue={bulkTyCategoryInput}
                            loading={bulkTyCategoryLoading}
                            filterOptions={(options) => options}
                            getOptionLabel={bulkTyCategoryOptionLabel}
                            isOptionEqualToValue={(option, selected) =>
                              bulkTyFormatCategoryId(option?.id) === bulkTyFormatCategoryId(selected?.id)
                            }
                            getOptionDisabled={(option) => option?.isLeaf === false}
                            onInputChange={(event, nextInput, reason) => {
                              if (reason !== 'reset') setBulkTyCategoryInput(nextInput);
                            }}
                            onChange={(event, nextValue) => {
                              setBulkTrendyolCategoryLeaf(nextValue);
                              if (nextValue?.name) setBulkTyCategoryInput(bulkTyToText(nextValue.name));
                            }}
                            noOptionsText={
                              bulkTyCategoryError
                                ? 'Arama yapılamadı'
                                : bulkTyCategoryInput.trim().length < 2
                                  ? 'Aramak için en az 2 karakter yazın.'
                                  : 'Kategori bulunamadı.'
                            }
                            loadingText="Kategoriler aranıyor..."
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Trendyol kategorisi"
                                placeholder="Örn: bilezik, kolye, yüzük"
                                size="small"
                                sx={modalInputSx}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {bulkTyCategoryLoading ? (
                                        <CircularProgress color="inherit" size={18} />
                                      ) : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  )
                                }}
                              />
                            )}
                            renderOption={(props, option) => {
                              const { key, ...optionProps } = props;
                              return (
                                <Box component="li" {...optionProps} key={key || `${option.id}-${option.path}`}>
                                  <Stack spacing={0.25} sx={{ minWidth: 0, py: 0.5 }}>
                                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                                      <Typography sx={{ fontWeight: 850, overflowWrap: 'anywhere' }}>
                                        {option.name || '-'}
                                      </Typography>
                                      <Chip size="small" label={`ID: ${option.id || '-'}`} />
                                      {!option.isLeaf ? (
                                        <Chip size="small" color="warning" label="Üst kategori" />
                                      ) : null}
                                    </Stack>
                                    <Typography sx={{ color: '#64748b', fontSize: '0.8rem', overflowWrap: 'anywhere' }}>
                                      {option.path || option.name || '-'}
                                    </Typography>
                                  </Stack>
                                </Box>
                              );
                            }}
                          />
                          {bulkTrendyolCategoryLeaf ? (
                            <Button
                              size="small"
                              variant="text"
                              color="secondary"
                              onClick={() => {
                                setBulkTrendyolCategoryLeaf(null);
                                setBulkTyCategoryInput('');
                              }}
                              sx={{ textTransform: 'none', flexShrink: 0, fontWeight: 700 }}
                            >
                              Seçimi kaldır
                            </Button>
                          ) : null}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          Seçim yapmazsanız ürünlerdeki Trendyol kategori bilgisi değişmez.
                        </Typography>
                        {bulkTyCategoryError ? (
                          <Alert severity="warning" sx={{ py: 0.35 }}>
                            {bulkTyCategoryError}
                          </Alert>
                        ) : null}
                      </Stack>
                    </Grid>
                  </Grid>
                </Paper>
              </Stack>
          </DialogContent>
          <DialogActions>
              <Button onClick={handleCloseBulkModal}>İptal</Button>
              <Button onClick={handleBulkSave} variant="contained" color="warning">GÜNCELLE</Button>
          </DialogActions>
      </Dialog>

      <Dialog
        className="ul-edit-dialog"
        open={openModal}
        onClose={handleCloseModal}
        maxWidth={false}
        fullWidth
        scroll="paper"
        sx={{ '& .MuiDialog-paper': { width: '100%', maxWidth: '1280px', borderRadius: '14px', overflow: 'hidden', m: 2 } }}
      >
        <DialogTitle
          component="div"
          sx={{
            background: 'linear-gradient(90deg, #c45f18 0%, #a34a12 38%, #7a3f0d 100%)',
            color: '#fff',
            py: 1.75,
            px: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: '1 1 280px' }}>
            <EditIcon sx={{ fontSize: 26, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.2, letterSpacing: '0.02em' }}>Ürün detayı</Typography>
              {editForm?.isim && (
                <Typography noWrap title={editForm.isim} sx={{ fontWeight: 400, fontSize: '0.9rem', opacity: 0.92, maxWidth: { xs: 220, sm: 420, md: 560 } }}>
                  {editForm.isim}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, flexWrap: 'wrap' }}>
            <Tooltip title="Mağaza fiyat ve kar hesaplama">
              <Button
                onClick={() => setOpenMagaza(true)}
                size="small"
                startIcon={<StoreIcon sx={{ fontSize: 18 }} />}
                sx={{
                  borderRadius: 999,
                  bgcolor: '#6d28d9',
                  color: '#fff',
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 1.75,
                  py: 0.5,
                  minHeight: 34,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  '&:hover': { bgcolor: '#5b21b6' }
                }}
              >
                Mağaza
              </Button>
            </Tooltip>
            <Tooltip title="Web sitesi fiyat ve kar hesaplama">
              <Button
                onClick={() => {
                  if (editForm) {
                    setMagazaForm((prev) => ({
                      ...prev,
                      satisFiyati:
                        !editForm.isManualMagaza && editForm.magazaFiyati != null && editForm.magazaFiyati !== ''
                          ? String(editForm.magazaFiyati)
                          : prev.satisFiyati
                    }));
                  }
                  setOpenWebsite(true);
                }}
                size="small"
                startIcon={<LanguageIcon sx={{ fontSize: 18 }} />}
                sx={{
                  borderRadius: 999,
                  bgcolor: '#0d9488',
                  color: '#fff',
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 1.75,
                  py: 0.5,
                  minHeight: 34,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                  '&:hover': { bgcolor: '#0f766e' }
                }}
              >
                Web
              </Button>
            </Tooltip>
            <Tooltip title="Trendyol fiyat ve kar hesaplama">
              <Button
                onClick={() => {
                  if (editForm) {
                    setTrendyolForm((prev) => {
                      const tyListePref = String(editForm.ty_trendyolSatisFiyati ?? '').trim();
                      const tyDiskPref =
                        editForm.pazaryeriFiyati != null && editForm.pazaryeriFiyati !== ''
                          ? String(editForm.pazaryeriFiyati)
                          : '';
                      return {
                        ...prev,
                        trendyolSatisFiyati:
                          tyListePref !== ''
                            ? tyListePref
                            : tyDiskPref !== ''
                              ? tyDiskPref
                              : prev.trendyolSatisFiyati,
                        trendyolIndirimliSatisFiyati:
                          tyDiskPref !== ''
                            ? tyDiskPref
                            : tyListePref !== ''
                              ? tyListePref
                              : prev.trendyolIndirimliSatisFiyati || prev.trendyolSatisFiyati,
                        hasAltinKuru:
                          editForm.hasAltinKuru != null && editForm.hasAltinKuru !== ''
                            ? editForm.hasAltinKuru
                            : prev.hasAltinKuru
                      };
                    });
                  }
                  setTrendyolProductInfo((prev) =>
                    normalizeTrendyolProductInfo(prev, {
                      ...(selectedUrun || {}),
                      ...(editForm || {})
                    })
                  );
                  setTrendyolPreviewError('');
                  setTrendyolPreviewResult(null);
                  setOpenTrendyolPayloadPreviewDialog(false);
                  setTrendyolSaveError('');
                  setTrendyolSaveResult(null);
                  setOpenTrendyol(true);
                }}
                size="small"
                startIcon={<img src="https://cdn.dsmcdn.com/web/production/ty-web.svg" alt="" style={{ width: 18, height: 18 }} />}
                sx={{
                  borderRadius: 999,
                  bgcolor: '#ea580c',
                  color: '#fff',
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 1.75,
                  py: 0.5,
                  minHeight: 34,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                  '&:hover': { bgcolor: '#c2410c' }
                }}
              >
                Trendyol
              </Button>
            </Tooltip>
            <IconButton onClick={handleCloseModal} size="small" sx={{ color: '#fff', ml: 0.5 }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0, bgcolor: '#fafafa' }}>
          {error && (
            <Alert severity="error" sx={{ m: 2, mb: 0 }}>
              {error}
            </Alert>
          )}
          {editForm && (
            <Box sx={{ p: 2.5, pt: error ? 2 : 2.5 }}>
              {/* Genel bilgiler */}
              <Box sx={{ mb: 2.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      bgcolor: '#fff4e6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <InfoIcon sx={{ color: '#e67e22', fontSize: 22 }} />
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#111827' }}>Genel bilgiler</Typography>
                </Box>
                <Grid container spacing={1.75}>
                  <Grid size={{ xs: 6, sm: 3, md: 1.1 }}>
                    <TextField fullWidth label="Ana Kategori" value="Takı Ürün" disabled size="small" sx={{ ...modalInputSx, '& .MuiInputBase-root': { bgcolor: '#f9fafb' } }} />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 4, md: 1.35 }}>{renderEditFormUrunTipiSelect('Ürün tipi')}</Grid>
                  <Grid size={{ xs: 12, md: 4.75 }}>
                    <TextField fullWidth label="Ürün Adı" name="isim" value={editForm.isim || ''} onChange={handleEditChange} size="small" sx={modalInputSx} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 1.8 }}>
                    <TextField fullWidth label="Barkod" value={editForm.barkod || ''} disabled size="small" sx={{ ...modalInputSx, '& .MuiInputBase-root': { bgcolor: '#f3f4f6' } }} />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 6, md: 1.8 }}>
                    <TextField
                      fullWidth
                      label="Stok Kodu"
                      name="stokKodu"
                      value={editForm.barkod || ''}
                      disabled
                      size="small"
                      sx={{ ...modalInputSx, '& .MuiInputBase-root': { bgcolor: '#f3f4f6' } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 4, md: 1.2 }}>
                    <TextField
                      fullWidth
                      label="Stok"
                      name="stok"
                      type="number"
                      value={editForm.stok}
                      onChange={handleEditChange}
                      size="small"
                      sx={modalInputSx}
                      InputProps={{ endAdornment: <InputAdornment position="end">Adet</InputAdornment> }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Ürün detayları */}
              <Box sx={{ mb: 2.25, pt: 1.25, borderTop: '1px solid #ececec' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      bgcolor: '#fff4e6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <FavoriteIcon sx={{ color: '#e67e22', fontSize: 22 }} />
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#111827' }}>Ürün detayları</Typography>
                </Box>
                <Grid container spacing={1.75}>
                  <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    {ikasCategories.length > 0 ? (
                      <TextField
                        fullWidth
                        select
                        label="Ürün kategorisi"
                        value={Array.isArray(editForm.ikasCategoryIds) ? editForm.ikasCategoryIds : []}
                        onChange={(e) => handleEditCategorySelect(e.target.value)}
                        SelectProps={{ multiple: true }}
                        size="small"
                        sx={{ ...modalInputSx, maxWidth: { md: 220 } }}
                      >
                        <MenuItem value="">Seçiniz</MenuItem>
                        {ikasCategories.map((cat) => (
                          <MenuItem key={cat.id} value={cat.id}>
                            {cat.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : (
                      <TextField
                        fullWidth
                        label="Ürün kategorisi"
                        name="urunKategorisi"
                        value={editForm.urunKategorisi || ''}
                        onChange={handleEditChange}
                        size="small"
                        sx={{ ...modalInputSx, maxWidth: { md: 200 } }}
                      />
                    )}
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                    <TextField
                      fullWidth
                      label="Üretici"
                      name="uretici"
                      value={editForm.uretici ?? editForm.mensei ?? ''}
                      onChange={handleEditChange}
                      size="small"
                      sx={{ ...modalInputSx, maxWidth: { md: 175 } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 4, sm: 3, md: 1 }}>
                    <TextField
                      fullWidth
                      label="Gram"
                      name="gram"
                      type="number"
                      value={editForm.gram}
                      onChange={handleEditChange}
                      size="small"
                      sx={modalInputSx}
                      InputProps={{ endAdornment: <InputAdornment position="end">gr</InputAdornment> }}
                    />
                  </Grid>
                  <Grid size={{ xs: 4, sm: 3, md: 1 }}>
                    <TextField fullWidth label="Milyem" name="urunMilyem" value={editForm.urunMilyem || ''} onChange={handleEditChange} size="small" sx={modalInputSx} />
                  </Grid>
                  <Grid size={{ xs: 4, sm: 3, md: 1 }}>
                    <TextField fullWidth label="İşçilik" name="iscilik" value={editForm.iscilik || ''} onChange={handleEditChange} size="small" sx={modalInputSx} />
                  </Grid>
                  <Grid size={{ xs: 4, sm: 3, md: 1 }}>
                    <TextField fullWidth label="Adet İşçiliği" name="adetIscilik" value={editForm.adetIscilik || ''} onChange={handleEditChange} size="small" sx={modalInputSx} />
                  </Grid>
                  <Grid size={{ xs: 4, sm: 3, md: 1 }}>
                    <TextField fullWidth label="Ürün Çapı" name="urunCapi" value={editForm.urunCapi || ''} onChange={handleEditChange} size="small" sx={modalInputSx} />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 4, md: 1 }}>
                    <TextField
                      fullWidth
                      label="Taş Özelliği"
                      name="tasOzelligi"
                      value={editForm.tasOzelligi || ''}
                      onChange={handleEditChange}
                      size="small"
                      sx={{ ...modalInputSx, maxWidth: { md: 160 } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 4, md: 1 }}>
                    <TextField
                      fullWidth
                      label="Zincir Uzunluğu"
                      name="zincirUzunlugu"
                      value={editForm.zincirUzunlugu || ''}
                      onChange={handleEditChange}
                      size="small"
                      sx={{ ...modalInputSx, maxWidth: { md: 140 } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 4, sm: 3, md: 1 }}>
                    <TextField fullWidth label="Renk" name="urunRengi" value={editForm.urunRengi ?? editForm.renk ?? ''} onChange={handleEditChange} size="small" sx={modalInputSx} />
                  </Grid>
                </Grid>
              </Box>

              {/* Fiyat bilgileri — kart görünümü */}
              <Box sx={{ mb: 2.25, pt: 1.25, borderTop: '1px solid #ececec' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      bgcolor: '#fff4e6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <AttachMoneyIcon sx={{ color: '#e67e22', fontSize: 22 }} />
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#111827' }}>Fiyat bilgileri</Typography>
                </Box>
                <Box
                  sx={{
                    display: 'grid',
                    width: '100%',
                    gridTemplateColumns: {
                      xs: 'minmax(0, 1fr)',
                      sm: 'repeat(2, minmax(0, 1fr))',
                      md: 'repeat(5, minmax(0, 1fr))'
                    },
                    gap: 1,
                    alignItems: 'stretch'
                  }}
                >
                  <Paper elevation={0} sx={modalPriceCardSx}>
                    <TextField
                      fullWidth
                      margin="dense"
                      label="Güncel Has Satış"
                      name="hasAltinKuru"
                      type="number"
                      value={editForm.hasAltinKuru}
                      onChange={handleEditChange}
                      size="small"
                      sx={{ ...modalInputSx, '& .MuiOutlinedInput-root': { bgcolor: '#eff6ff' } }}
                      InputProps={{ endAdornment: <InputAdornment position="end">TL</InputAdornment> }}
                    />
                    <Box sx={{ mt: 'auto', pt: 0.25 }}>
                      <Box sx={{ py: 0.15, px: 0.65, borderRadius: 0.75, bgcolor: '#dbeafe', display: 'inline-block' }}>
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#1d4ed8', lineHeight: 1.2 }}>{acilModAktif ? 'Manuel Kur' : 'Canlı Kur'}</Typography>
                      </Box>
                    </Box>
                  </Paper>
                  <Paper elevation={0} sx={modalPriceCardSx}>
                    <TextField
                      fullWidth
                      margin="dense"
                      label="Otomatik Maliyet"
                      name="maliyetFiyati"
                      value={editForm.maliyetFiyati}
                      size="small"
                      sx={{ ...modalInputSx, '& .MuiOutlinedInput-root': { bgcolor: '#fff1f2' } }}
                      InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end">TL</InputAdornment> }}
                    />
                    <Box sx={{ mt: 'auto', pt: 0.25 }}>
                      <Box sx={{ py: 0.15, px: 0.5, borderRadius: 0.75, bgcolor: '#ffe4e6', display: 'block' }}>
                        <Typography sx={{ fontSize: '0.58rem', fontWeight: 600, color: '#be123c', lineHeight: 1.2 }}>
                          {safeParseFloat(editForm.iscilik) > 0
                            ? 'Gram × (Milyem + İşçilik) × Kur'
                            : safeParseFloat(editForm.adetIscilik) > 0
                              ? '(Gram × Milyem × Kur) + (Adet İşçiliği × Kur)'
                              : 'Gram × Milyem × Kur'}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                  <Paper elevation={0} sx={modalPriceCardSx}>
                    <TextField
                      fullWidth
                      margin="dense"
                      label="Mağaza Satış"
                      name="magazaFiyati"
                      type="number"
                      value={editForm.magazaFiyati}
                      onChange={handleEditChange}
                      size="small"
                      sx={modalInputSx}
                      InputProps={{ endAdornment: <InputAdornment position="end">TL</InputAdornment> }}
                    />
                    <Typography sx={{ mt: 'auto', pt: 0.25, fontSize: '0.58rem', color: '#6b7280', lineHeight: 1.2 }}>
                      Formül: (Birim Fiyat × Gram) veya Manuel
                    </Typography>
                  </Paper>
                  <Paper elevation={0} sx={modalPriceCardSx}>
                    <TextField
                      fullWidth
                      margin="dense"
                      label="Pazaryeri Satış"
                      name="pazaryeriFiyati"
                      type="number"
                      value={editForm.pazaryeriFiyati}
                      onChange={(e) => handleManualPriceChange(e, 'isManualPazaryeri')}
                      size="small"
                      sx={{ ...modalInputSx, bgcolor: editForm.isManualPazaryeri ? '#fff7ed' : 'transparent' }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {editForm.isManualPazaryeri && (
                              <Tooltip title="Otomatik fiyata dön">
                                <IconButton size="small" onClick={() => handleRevertAuto('isManualPazaryeri')}>
                                  <RestartAltIcon color="primary" fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            TL
                          </InputAdornment>
                        )
                      }}
                    />
                    <Typography sx={{ mt: 'auto', pt: 0.25, fontSize: '0.58rem', color: '#6b7280', lineHeight: 1.2 }}>
                      {editForm.isManualPazaryeri ? 'Manuel giriş' : 'Oto: Mağaza Fiyatı × 1.35'}
                    </Typography>
                  </Paper>
                  <Paper elevation={0} sx={modalPriceCardSx}>
                    <TextField
                      fullWidth
                      margin="dense"
                      label="Web Satış Fiyatı"
                      name="ws_websiteSatisFiyati"
                      type="number"
                      value={editForm.ws_websiteSatisFiyati ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEditForm((prev) => ({
                          ...prev,
                          ws_websiteSatisFiyati: val,
                          isManualSite: true,
                          priceSource: 'manual'
                        }));
                        setWebsiteForm((prev) => ({ ...prev, websiteSatisFiyati: val }));
                      }}
                      size="small"
                      sx={{ ...modalInputSx, bgcolor: editForm.isManualSite ? '#fff7ed' : 'transparent' }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {editForm.isManualSite && (
                              <Tooltip title="Otomatik fiyata dön">
                                <IconButton size="small" onClick={() => handleRevertAuto('isManualSite')}>
                                  <RestartAltIcon color="primary" fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            TL
                          </InputAdornment>
                        )
                      }}
                    />
                    {(() => {
                      const L = parseNumLoose(editForm?.ws_websiteSatisFiyati);
                      const ek = safeParseFloat(magazaForm.ekIndirim);
                      const y = safeParseFloat(magazaForm.indirimYuzde);
                      const hasD = (ek > 0 || y > 0) && L > 0;
                      const ind = hasD ? roundAutoPriceUpToTen(Math.max(0, L - ek - (L * y) / 100)) : 0;
                      if (!hasD) return null;
                      return (
                        <TextField
                          fullWidth
                          margin="dense"
                          label="Satış (indirimli)"
                          value={String(ind)}
                          size="small"
                          sx={{ ...modalInputSx, bgcolor: '#f0fdf4' }}
                          InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end">TL</InputAdornment> }}
                        />
                      );
                    })()}
                    <Typography sx={{ mt: 'auto', pt: 0.25, fontSize: '0.58rem', color: '#6b7280', lineHeight: 1.2 }}>
                      {editForm.isManualSite ? 'Manuel giriş' : 'Otomatik hesap'}
                    </Typography>
                  </Paper>
                </Box>
              </Box>

              {/* Bağlantılar — siteLinki / pazaryeriLinki alanları veride kalır; arayüzde yalnızca tıklanabilir linkler */}
              <Box sx={{ mb: 2.1, pt: 0.65, borderTop: '1px solid #ececec' }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#9ca3af', mb: 1 }}>Bağlantılar</Typography>
                <Stack spacing={0.85}>
                  {(() => {
                    const ikasHref = resolveUkbagoldProductUrl(editForm);
                    return ikasHref ? (
                      <Typography variant="caption" sx={{ display: 'block', wordBreak: 'break-all' }}>
                        İkas mağaza ürün bağlantısı:{' '}
                        <Link href={ikasHref} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 700 }}>
                          {ikasHref}
                        </Link>
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        İkas mağaza ürün bağlantısı yok.
                      </Typography>
                    );
                  })()}
                  {(() => {
                    const tyHref = pickTrendyolProductUrl(editForm);
                    return tyHref ? (
                      <Typography variant="caption" sx={{ display: 'block', wordBreak: 'break-all' }}>
                        Trendyol ürün bağlantısı:{' '}
                        <Link href={tyHref} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 700 }}>
                          {tyHref}
                        </Link>
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Trendyol ürün linki yok.
                      </Typography>
                    );
                  })()}
                </Stack>
              </Box>

              {/* Web + Trendyol görseller — ürün ekle ile aynı yan yana düzen */}
              <Box sx={{ pt: 1.1, borderTop: '1px solid #ececec' }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                    columnGap: { xs: 1.25, md: 1.75 },
                    rowGap: 2,
                    width: '100%',
                    alignItems: 'stretch'
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: '#fff4e6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <ImageIcon sx={{ color: '#e67e22', fontSize: 20 }} />
                      </Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '1.02rem', color: '#0f172a', lineHeight: 1.25 }}>Web Görselleri</Typography>
                    </Box>
                    <Box
                      sx={{
                        p: 1.25,
                        borderRadius: '12px',
                        bgcolor: '#faf7f2',
                        border: '1px solid #e7e5e4',
                        flex: 1,
                        minWidth: 0
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          flexWrap: 'nowrap',
                          gap: 0.75,
                          width: '100%',
                          overflowX: { xs: 'auto', md: 'auto' },
                          WebkitOverflowScrolling: 'touch',
                          boxSizing: 'border-box',
                          '& > *': { minWidth: 0 }
                        }}
                      >
                        <ImageUploadBox label="Görsel 1" preview={preview1} onChange={(e) => handleFileChange(e, 'gorsel1')} onRemove={() => handleRemoveImage('gorsel1')} onImageFile={(f) => applyImageFile(f, 'gorsel1')} size={78} />
                        <ImageUploadBox label="Görsel 2" preview={preview2} onChange={(e) => handleFileChange(e, 'gorsel2')} onRemove={() => handleRemoveImage('gorsel2')} onImageFile={(f) => applyImageFile(f, 'gorsel2')} size={78} />
                        <ImageUploadBox label="Görsel 3" preview={preview3} onChange={(e) => handleFileChange(e, 'gorsel3')} onRemove={() => handleRemoveImage('gorsel3')} onImageFile={(f) => applyImageFile(f, 'gorsel3')} size={78} />
                        <ImageUploadBox label="Görsel 4" preview={preview4} onChange={(e) => handleFileChange(e, 'gorsel4')} onRemove={() => handleRemoveImage('gorsel4')} onImageFile={(f) => applyImageFile(f, 'gorsel4')} size={78} />
                        <ImageUploadBox label="Görsel 5" preview={preview5} onChange={(e) => handleFileChange(e, 'gorsel5')} onRemove={() => handleRemoveImage('gorsel5')} onImageFile={(f) => applyImageFile(f, 'gorsel5')} size={78} />
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: '#fff4e6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <ImageIcon sx={{ color: '#e67e22', fontSize: 20 }} />
                      </Box>
                      <Typography sx={{ fontWeight: 800, fontSize: '1.02rem', color: '#0f172a', lineHeight: 1.25 }}>Trendyol Görselleri</Typography>
                    </Box>
                    <Box
                      sx={{
                        p: 1.25,
                        borderRadius: '12px',
                        bgcolor: '#faf7f2',
                        border: '1px solid #e7e5e4',
                        flex: 1,
                        minWidth: 0
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          flexWrap: 'nowrap',
                          gap: 0.75,
                          width: '100%',
                          overflowX: { xs: 'auto', md: 'auto' },
                          WebkitOverflowScrolling: 'touch',
                          boxSizing: 'border-box',
                          '& > *': { minWidth: 0 }
                        }}
                      >
                        <ImageUploadBox label="Görsel 1" preview={tyPreview1} onChange={(e) => handleFileChange(e, 'ty_gorsel1')} onRemove={() => handleRemoveImage('ty_gorsel1')} onImageFile={(f) => applyImageFile(f, 'ty_gorsel1')} size={78} />
                        <ImageUploadBox label="Görsel 2" preview={tyPreview2} onChange={(e) => handleFileChange(e, 'ty_gorsel2')} onRemove={() => handleRemoveImage('ty_gorsel2')} onImageFile={(f) => applyImageFile(f, 'ty_gorsel2')} size={78} />
                        <ImageUploadBox label="Görsel 3" preview={tyPreview3} onChange={(e) => handleFileChange(e, 'ty_gorsel3')} onRemove={() => handleRemoveImage('ty_gorsel3')} onImageFile={(f) => applyImageFile(f, 'ty_gorsel3')} size={78} />
                        <ImageUploadBox label="Görsel 4" preview={tyPreview4} onChange={(e) => handleFileChange(e, 'ty_gorsel4')} onRemove={() => handleRemoveImage('ty_gorsel4')} onImageFile={(f) => applyImageFile(f, 'ty_gorsel4')} size={78} />
                        <ImageUploadBox label="Görsel 5" preview={tyPreview5} onChange={(e) => handleFileChange(e, 'ty_gorsel5')} onRemove={() => handleRemoveImage('ty_gorsel5')} onImageFile={(f) => applyImageFile(f, 'ty_gorsel5')} size={78} />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            py: 2,
            bgcolor: '#fafafa',
            borderTop: '1px solid #ececec',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1
          }}
        >
          <Button
            onClick={handleDelete}
            color="error"
            startIcon={<DeleteIcon />}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Ürünü sil
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
            <Button onClick={handleCloseModal} sx={{ textTransform: 'none', color: '#374151', fontWeight: 600 }}>
              Vazgeç
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                px: 3,
                py: 1,
                borderRadius: 2,
                color: '#fff',
                background: 'linear-gradient(90deg, #5c2d0a 0%, #9d5317 45%, #d97706 100%)',
                boxShadow: '0 4px 14px rgba(157, 83, 23, 0.35)',
                '&:hover': { background: 'linear-gradient(90deg, #4a2408 0%, #854a14 45%, #b45309 100%)' }
              }}
            >
              Değişiklikleri kaydet
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openTrendyol}
        onClose={() => {
          setOpenTrendyol(false);
          setOpenTrendyolPayloadPreviewDialog(false);
        }}
        maxWidth={false}
        fullWidth
        scroll="paper"
        sx={{
          '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: 'min(1680px, 98vw)',
            borderRadius: 2,
            overflow: 'hidden',
            m: 2,
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '96vh'
          }
        }}
      >
        <DialogTitle
          component="div"
          sx={{
            bgcolor: '#ea580c',
            color: '#fff',
            py: 1.75,
            px: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Box
              component="img"
              src="https://cdn.dsmcdn.com/web/production/ty-web.svg"
              alt=""
              sx={{ width: 28, height: 28, flexShrink: 0, filter: 'brightness(0) invert(1)' }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.2 }}>Trendyol Ürün Detay</Typography>
              {editForm?.isim && (
                <Typography noWrap title={editForm.isim} sx={{ fontWeight: 500, fontSize: '0.9rem', opacity: 0.95, maxWidth: { xs: 220, sm: 520, md: 720 } }}>
                  {editForm.isim}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton
            onClick={() => {
              setOpenTrendyol(false);
              setOpenTrendyolPayloadPreviewDialog(false);
            }}
            size="small"
            sx={{ color: '#fff' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2.25, bgcolor: '#f5f5f5', overflowY: 'auto', flex: '1 1 auto' }}>
          {editForm && (
            <Grid container spacing={2} alignItems="stretch">
              <Grid size={{ xs: 12 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 1.5, sm: 2.25 },
                    borderRadius: 2.5,
                    border: '1px solid #fed7aa',
                    bgcolor: '#fff7ed'
                  }}
                >
                  <Stack spacing={1.75}>
                    <Box>
                      <Typography sx={{ fontWeight: 900, color: '#9a3412', fontSize: '1rem' }}>
                        Trendyol Ürün Bilgileri
                      </Typography>
                      <Typography sx={{ color: '#7c2d12', fontSize: '0.86rem', mt: 0.35, lineHeight: 1.45 }}>
                        Trendyol kategorisi ve gönderim alanları aşağıdaki Genel detaylar ve Ürün özellikleri kutularındadır. Zorunlu pazaryeri özelliklerini burada doldurun; paket önizleme ve gönderim için alttaki düğmeleri kullanın.
                      </Typography>
                    </Box>
                    <TrendyolProductMatchingPanel
                      subeId={String(selectedUrun?.sube || selectedUrun?.subeId || sube || '').trim()}
                      productId={String(editForm?.barkod || editForm?.id || selectedUrun?.barkod || selectedUrun?.id || '').trim()}
                      productSnapshot={editForm}
                      getAuthHeaders={getBackendAuthHeaders}
                      onMatchApplied={(patch) => {
                        setEditForm((prev) => (prev ? { ...prev, ...patch } : prev));
                        setSelectedUrun((prev) => (prev ? { ...prev, ...patch } : prev));
                      }}
                    />
                    <TrendyolProductInfoForm
                      hideAutoFields
                      hideTrendyolDescription
                      compactMandatoryAttributes
                      trendyolCategorySearchPortalEl={trendyolCategoryPortalHost}
                      trendyolShipmentFieldsPortalEl={trendyolShipmentPortalHost}
                      value={trendyolProductInfo}
                      onChange={setTrendyolProductInfo}
                      product={{
                        ...editForm,
                        hasTrendyolProductImage: Boolean(
                          tyPreview1 ||
                            tyPreview2 ||
                            tyPreview3 ||
                            tyPreview4 ||
                            tyPreview5 ||
                            editForm?.ty_gorsel1 ||
                            editForm?.ty_gorsel2 ||
                            editForm?.ty_gorsel3 ||
                            editForm?.ty_gorsel4 ||
                            editForm?.ty_gorsel5 ||
                            [1, 2, 3, 4, 5].some((i) =>
                              String(editForm?.[`ty_gorselURL${i}`] ?? '').trim()
                            )
                        )
                      }}
                    />

                    <Typography
                      variant="caption"
                      component="div"
                      sx={{
                        color: '#64748b',
                        fontSize: { xs: '0.74rem', sm: '0.78rem' },
                        lineHeight: 1.5,
                        maxWidth: '100%',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word'
                      }}
                    >
                      Canlı gönderim yalnızca Trendyol ayarlarında aktifse yapılır.
                    </Typography>

                    {trendyolSaveError ? (
                      <Alert severity="error" sx={{ borderRadius: 2, '& .MuiAlert-message': { width: '100%', overflowWrap: 'anywhere', wordBreak: 'break-word' } }}>
                        {trendyolSaveError}
                      </Alert>
                    ) : null}

                    {trendyolSaveResult ? (
                      <Alert
                        severity={
                          trendyolSaveResult.trendyolSyncStatus === 'pending_batch'
                            ? 'success'
                            : trendyolSaveResult.dryRun === true || trendyolSaveResult.trendyolSyncStatus === 'dry_run_ready'
                              ? 'info'
                            : trendyolSaveResult.trendyolSyncStatus === 'skipped_unchanged'
                              ? 'info'
                              : trendyolSaveResult.trendyolSyncStatus === 'skipped_matched_listing'
                                ? 'warning'
                                : trendyolSaveResult.trendyolSyncStatus === 'skipped_missing_fields'
                                  ? 'error'
                                  : trendyolSaveResult.trendyolSyncStatus === 'error' ||
                                      (trendyolSaveResult.success === false &&
                                        trendyolSaveResult.trendyolSyncStatus !== 'skipped_missing_fields' &&
                                        trendyolSaveResult.trendyolSyncStatus !== 'skipped_matched_listing')
                                    ? 'error'
                                    : 'info'
                        }
                        sx={{ borderRadius: 2, '& .MuiAlert-message': { width: '100%' } }}
                      >
                        <Stack spacing={1}>
                          {trendyolSaveResult.dryRun === true || trendyolSaveResult.trendyolSyncStatus === 'dry_run_ready' ? (
                            <Typography sx={{ fontWeight: 900, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                              Canlı gönderim kapalı. Trendyol paketi hazırlandı ama gönderilmedi.
                            </Typography>
                          ) : null}
                          {trendyolSaveResult.trendyolSyncStatus === 'skipped_matched_listing' ? (
                            <Typography sx={{ fontWeight: 850, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                              {trendyolSaveResult.message ||
                                'Eşleştirilmiş ürünler için yeni listeleme oluşturulamaz; doğrudan yükleme bu fazda kapalıdır.'}
                            </Typography>
                          ) : null}
                          {trendyolSaveResult.trendyolSyncStatus === 'skipped_unchanged' ? (
                            <Typography sx={{ fontWeight: 800, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                              Ürün paketi önceki gönderimle aynı; Trendyol’a yeniden istek gönderilmedi.
                            </Typography>
                          ) : null}
                          {trendyolSaveResult.trendyolSyncStatus === 'pending_batch' ? (
                            <Typography sx={{ fontWeight: 800, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                              Trendyol’a gönderildi, batch sonucu bekleniyor.
                            </Typography>
                          ) : null}
                          {trendyolSaveResult.trendyolSyncStatus === 'skipped_missing_fields' ? (
                            <Typography sx={{ fontWeight: 800, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                              {trendyolSaveResult.message || 'Paket eksik; Trendyol’a gönderilmedi.'}
                            </Typography>
                          ) : null}
                          {(trendyolSaveResult.trendyolSyncStatus === 'error' ||
                            (trendyolSaveResult.success === false &&
                              trendyolSaveResult.trendyolSyncStatus !== 'skipped_missing_fields' &&
                              trendyolSaveResult.trendyolSyncStatus !== 'skipped_matched_listing')) &&
                          trendyolSaveResult.message ? (
                            <Typography sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word', fontWeight: 700 }}>
                              {trendyolSaveResult.message}
                            </Typography>
                          ) : null}
                          {typeof trendyolSaveResult.batchRequestId === 'string' && trendyolSaveResult.batchRequestId.trim() ? (
                            <Typography
                              variant="body2"
                              component="div"
                              sx={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.8rem', overflowWrap: 'anywhere', wordBreak: 'break-all' }}
                            >
                              batchRequestId: {trendyolSaveResult.batchRequestId.trim()}
                            </Typography>
                          ) : trendyolSaveResult.trendyolSyncStatus === 'pending_batch' ? (
                            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                              batchRequestId dönüşü yok veya henüz okunamadı; Trendyol yanıtını kontrol edin.
                            </Typography>
                          ) : null}
                          {Array.isArray(trendyolSaveResult.validation?.missingFields) && trendyolSaveResult.validation.missingFields.length > 0 ? (
                            <Stack spacing={0.5}>
                              <Typography sx={{ fontWeight: 800, fontSize: '0.88rem' }}>Eksik alanlar</Typography>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {trendyolSaveResult.validation.missingFields.map((field) => (
                                  <Chip key={field} size="small" color="warning" label={field} sx={{ fontWeight: 700 }} />
                                ))}
                              </Stack>
                            </Stack>
                          ) : null}
                        </Stack>
                      </Alert>
                    ) : null}
                  </Stack>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Stack spacing={2} sx={{ height: '100%' }}>
                  <Paper elevation={0} sx={{ p: 1.15, borderRadius: 1.5, border: '1px solid #fed7aa', bgcolor: '#fff' }}>
                    <Typography sx={{ fontWeight: 800, color: '#9a3412', mb: 0.5, fontSize: '0.8rem' }}>Satış detayları</Typography>
                    <Alert
                      severity="info"
                      icon={<InfoOutlinedIcon sx={{ color: '#ea580c', fontSize: 15 }} />}
                      sx={{
                        mb: 1.1,
                        py: 0.5,
                        bgcolor: '#fff7ed',
                        border: '1px solid #fed7aa',
                        color: '#1e293b',
                        borderRadius: 1.5,
                        '& .MuiAlert-icon': { py: 0.15, mr: 0.75, alignItems: 'flex-start' },
                        '& .MuiAlert-message': { width: '100%', py: 0 }
                      }}
                    >
                      <Typography variant="body2" component="div" sx={{ lineHeight: 1.32, fontSize: '0.72rem' }}>
                        <b>Birim Fiyat:</b> {unitInfo.magazaBirim.toFixed(2)} TL
                        <br />
                        <b>Gram:</b>{' '}
                        {safeParseFloat(editForm?.gram).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} gr
                        <br />
                        <span style={{ opacity: 0.9 }}>(Kaynak: {unitInfo.kaynak})</span>
                      </Typography>
                    </Alert>
                    <Grid container spacing={0.85}>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          margin="dense"
                          size="small"
                          label="Güncel Has Altın Satış Kuru"
                          name="hasAltinKuru"
                          type="number"
                          value={trendyolForm.hasAltinKuru}
                          onChange={handleTrendyolChange}
                          sx={webSatisDetayInputSx}
                          InputProps={{ endAdornment: <InputAdornment position="end">TL</InputAdornment> }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          margin="dense"
                          size="small"
                          label="Trendyol satış fiyatı (liste)"
                          name="trendyolSatisFiyati"
                          type="number"
                          value={trendyolForm.trendyolSatisFiyati}
                          onChange={handleTrendyolChange}
                          sx={{
                            ...webSatisDetayInputSx,
                            '& .MuiOutlinedInput-root': {
                              ...(webSatisDetayInputSx['& .MuiOutlinedInput-root'] || {}),
                              bgcolor: editForm?.isManualPazaryeri ? '#fff7ed' : '#fff'
                            }
                          }}
                          helperText={
                            editForm?.isManualPazaryeri
                              ? 'Liste fiyatı (ty_trendyolSatisFiyati). Kampanya simülasyonu da bu liste üzerindendir.'
                              : 'Otomatik hesap'
                          }
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                {editForm?.isManualPazaryeri && (
                                  <Tooltip title="Otomatik fiyata dön">
                                    <IconButton size="small" onClick={() => handleRevertAuto('isManualPazaryeri')}>
                                      <RestartAltIcon color="primary" fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                TL
                              </InputAdornment>
                            )
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          margin="dense"
                          size="small"
                          label="Trendyol indirimli satış fiyatı"
                          name="trendyolIndirimliSatisFiyati"
                          type="number"
                          value={trendyolForm.trendyolIndirimliSatisFiyati}
                          onChange={handleTrendyolChange}
                          sx={{
                            ...webSatisDetayInputSx,
                            '& .MuiOutlinedInput-root': {
                              ...(webSatisDetayInputSx['& .MuiOutlinedInput-root'] || {}),
                              bgcolor: '#fffdfb'
                            }
                          }}
                          helperText={`Firestore'da pazaryeriFiyati; satış (indirimli) olarak analizde kullanılır.`}
                          InputProps={{ endAdornment: <InputAdornment position="end">TL</InputAdornment> }}
                        />
                      </Grid>
                      <Grid size={{ xs: 4 }}>
                        <TextField
                          fullWidth
                          margin="dense"
                          size="small"
                          label="Komisyon"
                          name="komisyonOrani"
                          type="number"
                          value={trendyolForm.komisyonOrani}
                          onChange={handleTrendyolChange}
                          sx={webSatisDetayInputSx}
                          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                        />
                      </Grid>
                      <Grid size={{ xs: 4 }}>
                        <TextField
                          fullWidth
                          margin="dense"
                          size="small"
                          label="Kargo"
                          name="kargoUcreti"
                          type="number"
                          value={trendyolForm.kargoUcreti}
                          onChange={handleTrendyolChange}
                          sx={webSatisDetayInputSx}
                          InputProps={{ endAdornment: <InputAdornment position="end">TL</InputAdornment> }}
                        />
                      </Grid>
                      <Grid size={{ xs: 4 }}>
                        <TextField
                          fullWidth
                          margin="dense"
                          size="small"
                          label="Ek Masraf"
                          name="ekMasraf"
                          type="number"
                          value={trendyolForm.ekMasraf}
                          onChange={handleTrendyolChange}
                          sx={webSatisDetayInputSx}
                          InputProps={{ endAdornment: <InputAdornment position="end">TL</InputAdornment> }}
                        />
                      </Grid>
                    </Grid>
                    <Box
                      sx={{
                        mt: 1,
                        pt: 1,
                        borderTop: '1px dashed #fed7aa'
                      }}
                    >
                      <Typography sx={{ fontWeight: 800, fontSize: '0.72rem', color: '#9a3412', mb: 0.65, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TrendingDownIcon sx={{ fontSize: 16 }} />
                        Kampanya simülasyonu
                      </Typography>
                      <Grid container spacing={0.85}>
                        <Grid size={{ xs: 12, md: 4 }}>
                          <FormControl fullWidth size="small" margin="dense" sx={{ ...webSatisDetayInputSx, minWidth: 0 }}>
                            <InputLabel id="ty-kampanya-tip">Kampanya tipi</InputLabel>
                            <Select
                              labelId="ty-kampanya-tip"
                              name="kampanyaTipi"
                              value={trendyolForm.kampanyaTipi}
                              onChange={handleTrendyolChange}
                              label="Kampanya tipi"
                            >
                              <MenuItem value="yok">Kampanya yok</MenuItem>
                              <MenuItem value="yuzde">Yüzde indirim (%)</MenuItem>
                              <MenuItem value="tutar">Tutar indirimi (TL)</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                          <TextField
                            fullWidth
                            margin="dense"
                            size="small"
                            label="İndirim değeri"
                            name="kampanyaDegeri"
                            type="number"
                            disabled={trendyolForm.kampanyaTipi === 'yok'}
                            value={trendyolForm.kampanyaDegeri}
                            onChange={handleTrendyolChange}
                            sx={webSatisDetayInputSx}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.76rem', pl: { md: 0.5 } }}>
                            Kampanyalı fiyat:{' '}
                            {trendyolDetailMetrics
                              ? `${trendyolDetailMetrics.kampanyaFiyati.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL`
                              : '—'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Paper>

                  {trendyolDetailMetrics && (
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e5e7eb', bgcolor: '#fff' }}>
                      <Typography sx={{ fontWeight: 800, color: '#9a3412', mb: 1.15, fontSize: '0.82rem' }}>Sonuç analizi</Typography>
                      <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: '0.76rem', py: 0.65 } }}>
                        <TableBody>
                          <TableRow sx={{ '& td': { borderColor: '#ececec' } }}>
                            <TableCell sx={{ color: '#475569', fontWeight: 600 }}>Maliyet</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              {trendyolDetailMetrics.maliyet.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{ '& td': { borderColor: '#ececec' } }}>
                            <TableCell sx={{ color: '#475569', fontWeight: 600 }}>Trendyol satış fiyatı (liste)</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              {trendyolDetailMetrics.satisListe.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                            </TableCell>
                          </TableRow>
                          {trendyolDetailMetrics.toplamIndirim > 0 && (
                            <TableRow sx={{ '& td': { borderColor: '#ececec' } }}>
                              <TableCell sx={{ color: '#475569' }}>Toplam indirim (liste − indirimli)</TableCell>
                              <TableCell align="right" sx={{ color: '#dc2626', fontWeight: 700 }}>
                                -{trendyolDetailMetrics.toplamIndirim.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                              </TableCell>
                            </TableRow>
                          )}
                          <TableRow sx={{ '& td': { borderColor: '#ececec' } }}>
                            <TableCell sx={{ color: '#475569', fontWeight: 600 }}>Satış (indirimli)</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.88rem' }}>
                              {trendyolDetailMetrics.efektifSatis.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                            </TableCell>
                          </TableRow>
                          {safeParseFloat(trendyolForm.komisyonOrani) > 0 && (
                            <TableRow sx={{ '& td': { borderColor: '#ececec' } }}>
                              <TableCell sx={{ color: '#475569' }}>Komisyon ({trendyolForm.komisyonOrani}%)</TableCell>
                              <TableCell align="right" sx={{ color: '#dc2626', fontWeight: 700 }}>
                                -{trendyolDetailMetrics.komisyonTutari.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                              </TableCell>
                            </TableRow>
                          )}
                          <TableRow sx={{ '& td': { borderColor: '#ececec' } }}>
                            <TableCell sx={{ color: '#475569' }}>{`KDV (matrah %${KUYUMCU_KDV_ORANI})`}</TableCell>
                            <TableCell align="right" sx={{ color: '#dc2626', fontWeight: 700 }}>
                              -{trendyolDetailMetrics.kdvTutari.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                            </TableCell>
                          </TableRow>
                          {trendyolDetailMetrics.ekMasraf > 0 && (
                            <TableRow sx={{ '& td': { borderColor: '#ececec' } }}>
                              <TableCell sx={{ color: '#475569' }}>Ek masraf</TableCell>
                              <TableCell align="right" sx={{ color: '#dc2626', fontWeight: 700 }}>
                                -{trendyolDetailMetrics.ekMasraf.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                              </TableCell>
                            </TableRow>
                          )}
                          {trendyolDetailMetrics.kargo > 0 && (
                            <TableRow sx={{ '& td': { borderColor: '#ececec' } }}>
                              <TableCell sx={{ color: '#475569' }}>Kargo</TableCell>
                              <TableCell align="right" sx={{ color: '#dc2626', fontWeight: 700 }}>
                                -{trendyolDetailMetrics.kargo.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                              </TableCell>
                            </TableRow>
                          )}
                          <TableRow sx={{ '& td': { borderColor: '#ececec', py: 0.75, borderTop: '2px solid #e2e8f0' } }}>
                            <TableCell sx={{ fontWeight: 800 }}>NET Kalan</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>
                              {trendyolDetailMetrics.netKalan.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{ bgcolor: trendyolDetailMetrics.netKar >= 0 ? '#ecfdf5' : '#fef2f2', '& td': { border: 'none', py: 1 } }}>
                            <TableCell sx={{ fontWeight: 800 }}>NET KAR</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${trendyolDetailMetrics.netKar.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL`}
                                sx={{
                                  borderRadius: 999,
                                  fontWeight: 800,
                                  height: 'auto',
                                  bgcolor: trendyolDetailMetrics.netKar >= 0 ? '#166534' : '#b91c1c',
                                  color: '#fff',
                                  '& .MuiChip-label': { px: 1.1, py: 0.2, fontSize: '0.72rem' }
                                }}
                              />
                            </TableCell>
                          </TableRow>
                          {trendyolForm.kampanyaTipi !== 'yok' && (
                            <TableRow sx={{ bgcolor: '#fff7ed', '& td': { borderColor: '#ffedd5' } }}>
                              <TableCell sx={{ fontWeight: 800 }}>NET KAR (kampanyalı)</TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={`${trendyolDetailMetrics.netKarKamp.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL`}
                                  sx={{
                                    borderRadius: 999,
                                    fontWeight: 800,
                                    height: 'auto',
                                    bgcolor: trendyolDetailMetrics.netKarKamp >= 0 ? '#166534' : '#b91c1c',
                                    color: '#fff',
                                    '& .MuiChip-label': { px: 1.1, py: 0.2, fontSize: '0.72rem' }
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                      <Box
                        sx={{
                          mt: 2,
                          p: 1.1,
                          bgcolor: '#fffbeb',
                          borderRadius: 2,
                          border: '1px solid #fde68a'
                        }}
                      >
                        <Typography variant="caption" display="block" align="center" fontWeight="800" color="#92400e" sx={{ letterSpacing: 0.02, fontSize: '0.68rem' }}>
                          MİNİMUM SATIŞ (Zarar Etmemek İçin)
                        </Typography>
                        <Typography variant="h6" align="center" color="#92400e" fontWeight="800" sx={{ mt: 0.3, fontSize: '0.88rem' }}>
                          {trendyolDetailMetrics.minimumSatisFiyati.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                        </Typography>
                      </Box>
                    </Paper>
                  )}
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Stack spacing={2} sx={{ height: '100%' }}>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0', bgcolor: '#fff' }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#37474f', mb: 1.5 }}>Genel Detaylar</Typography>
                    <Grid container spacing={1.5}>
                      <Grid size={{ xs: 12, md: 5 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Ürün Adı"
                          name="isim"
                          value={editForm.isim || ''}
                          onChange={handleEditChange}
                          sx={modalInputSx}
                          InputProps={{ endAdornment: <InputAdornment position="end"><Tooltip title="Kopyala"><IconButton size="small" onClick={() => handleCopyToClipboard(editForm.isim || '', 'Ürün adı')}><ContentCopyIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip></InputAdornment> }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Box
                          sx={{
                            ...modalInputSx,
                            minHeight: 40,
                            px: 1.5,
                            py: 0.75,
                            borderRadius: 1.5,
                            border: '1px solid #d5dbe1',
                            bgcolor: '#fafafa'
                          }}
                        >
                          <Typography sx={{ fontSize: '0.68rem', color: '#64748b', mb: 0.45, fontWeight: 600 }}>
                            Ürün Kategorisi
                          </Typography>
                          <Stack direction="row" spacing={0.45} flexWrap="wrap" useFlexGap>
                            {(webKategoriEtiketleri.length ? webKategoriEtiketleri : ['-']).map((etiket, idx) => (
                              <Chip
                                key={`web-kategori-${idx}-${etiket}`}
                                label={etiket}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.62rem',
                                  bgcolor: '#fff6e5',
                                  color: '#8a5b22',
                                  border: '1px solid #f7dfb5',
                                  fontWeight: 700,
                                  '& .MuiChip-label': { px: 0.55, whiteSpace: 'nowrap' }
                                }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }} sx={{ minWidth: 0 }}>
                        <Box
                          ref={setTrendyolCategoryPortalHost}
                          sx={{
                            minHeight: 40,
                            width: '100%',
                            '& .MuiAutocomplete-root': { width: '100%', minWidth: 0 }
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0', bgcolor: '#fff', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 420 }}>
                    <TextField
                      fullWidth
                      label="Ürün Açıklaması"
                      name="aciklama"
                      multiline
                      minRows={22}
                      value={editForm.aciklama || ''}
                      onChange={handleEditChange}
                      sx={{ ...modalInputSx, flex: 1, '& .MuiInputBase-root': { alignItems: 'flex-start' } }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Kopyala">
                              <IconButton size="small" onClick={() => handleCopyToClipboard(editForm.aciklama || '', 'Trendyol ürün açıklaması')}>
                                <ContentCopyIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Paper>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <Stack spacing={2}>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0', bgcolor: '#fff' }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#37474f', mb: 1.5 }}>Ürün Özellikleri</Typography>
                    <Grid container spacing={1.25}>
                      <Grid size={{ xs: 6 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Barkod"
                          value={editForm.barkod || ''}
                          sx={{ ...modalInputSx, '& .MuiInputBase-root': { bgcolor: '#f5f5f5' } }}
                          InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Tooltip title="Kopyala"><IconButton size="small" onClick={() => handleCopyToClipboard(editForm.barkod || '', 'Barkod')}><ContentCopyIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip></InputAdornment> }}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Stok kodu"
                          name="stokKodu"
                          value={editForm.stokKodu || editForm.barkod || ''}
                          onChange={handleEditChange}
                          sx={modalInputSx}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField fullWidth size="small" label="Desi" name="desi" value={editForm.desi ?? ''} onChange={handleEditChange} sx={modalInputSx} />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField fullWidth size="small" label="Marka" name="marka" value={editForm.marka || 'UKBA GOLD'} onChange={handleEditChange} sx={modalInputSx} />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Gram"
                          name="gram"
                          type="number"
                          value={editForm.gram}
                          onChange={handleEditChange}
                          sx={modalInputSx}
                          InputProps={{ endAdornment: <InputAdornment position="end">gr</InputAdornment> }}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField fullWidth size="small" label="Milyem" name="urunMilyem" value={editForm.urunMilyem || ''} onChange={handleEditChange} sx={modalInputSx} />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField fullWidth size="small" label="İşçilik" name="iscilik" value={editForm.iscilik || ''} onChange={handleEditChange} sx={modalInputSx} />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField fullWidth size="small" label="Adet İşçiliği" name="adetIscilik" value={editForm.adetIscilik || ''} onChange={handleEditChange} sx={modalInputSx} />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField fullWidth size="small" label="Renk" name="urunRengi" value={editForm.urunRengi ?? editForm.renk ?? ''} onChange={handleEditChange} sx={modalInputSx} />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField fullWidth size="small" label="Ürün Çapı" name="urunCapi" value={editForm.urunCapi || ''} onChange={handleEditChange} sx={modalInputSx} />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField fullWidth size="small" label="Taş Özelliği" name="tasOzelligi" value={editForm.tasOzelligi || ''} onChange={handleEditChange} sx={modalInputSx} />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField fullWidth size="small" label="Zincir Uzunluğu" name="zincirUzunlugu" value={editForm.zincirUzunlugu || ''} onChange={handleEditChange} sx={modalInputSx} />
                      </Grid>
                      <Grid size={{ xs: 6 }}>{renderEditFormUrunTipiSelect('Ürün tipi')}</Grid>
                      <Grid size={{ xs: 12 }}>
                        <Divider sx={{ my: 0.35, borderColor: '#ececec' }} />
                        <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 700, mb: 0.75 }}>
                          Trendyol gönderim (KDV, desi, kargo firması, teslimat süresi)
                        </Typography>
                        <Box ref={setTrendyolShipmentPortalHost} sx={{ width: '100%', minWidth: 0 }} />
                      </Grid>
                    </Grid>
                  </Paper>

                </Stack>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            py: 1.75,
            bgcolor: '#fafafa',
            borderTop: '1px solid #e0e0e0',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap'
          }}
        >
          <Box sx={{ flex: '1 1 220px', minWidth: 0, pr: 1 }}>
            {trendyolListingMatched ? (
              <Typography variant="caption" sx={{ display: 'block', color: '#92400e', fontWeight: 650, lineHeight: 1.45 }}>
                Bu ürün Trendyol’da mevcut listeyle eşleşmiş. İşlem yeni ürün oluşturmaz; mevcut kaydı günceller/gönderir.
              </Typography>
            ) : null}
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ ml: 'auto' }}>
          <Button
            onClick={handleOpenTrendyolPayloadPreview}
            disabled={trendyolPreviewLoading || trendyolSaveLoading}
            variant="outlined"
            sx={{ color: '#c2410c', borderColor: '#ea580c', textTransform: 'none', fontWeight: 800 }}
          >
            {trendyolPreviewLoading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            Paket Önizle
          </Button>
          <Button
            onClick={handleTrendyolSaveProduct}
            disabled={
              trendyolPreviewLoading ||
              trendyolSaveLoading ||
              (!trendyolListingMatched && trendyolSendBlockedByBrand)
            }
            variant="contained"
            startIcon={trendyolSaveLoading ? null : <SendIcon sx={{ fontSize: 20 }} />}
            sx={{
              bgcolor: '#ea580c',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 800,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#c2410c', boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: '#fdba74', color: '#fff' }
            }}
          >
            {trendyolSaveLoading ? <CircularProgress size={18} sx={{ mr: 0.75, color: 'inherit' }} /> : null}
            {trendyolSaveLoading ? 'Gönderiliyor…' : `Trendyol’a Gönder`}
          </Button>
          <Button
            onClick={() => {
              setOpenTrendyol(false);
              setOpenTrendyolPayloadPreviewDialog(false);
            }}
            sx={{ color: '#424242', textTransform: 'none', fontWeight: 700 }}
          >
            Kapat
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{ bgcolor: '#ea580c', textTransform: 'none', fontWeight: 800, px: 2.5, '&:hover': { bgcolor: '#c2410c' } }}
          >
            Onayla ve kaydet
          </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openTrendyolPayloadPreviewDialog}
        onClose={() => setOpenTrendyolPayloadPreviewDialog(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Trendyol paket önizleme</DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#fafafa' }}>
          {trendyolPreviewLoading ? (
            <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ py: 3 }}>
              <CircularProgress />
              <Typography color="text.secondary" sx={{ fontWeight: 700 }}>
                Önizleme hazırlanıyor…
              </Typography>
            </Stack>
          ) : null}
          {!trendyolPreviewLoading && trendyolPreviewError ? (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {trendyolPreviewError}
            </Alert>
          ) : null}
          {!trendyolPreviewLoading && trendyolPreviewResult ? (
            <Stack spacing={1.5}>
              <Alert
                severity={trendyolPreviewResult?.validation?.ok ? 'success' : 'warning'}
                sx={{ borderRadius: 2, '& .MuiAlert-message': { width: '100%' } }}
              >
                <Stack spacing={0.75}>
                  <Typography sx={{ fontWeight: 900 }}>
                    {trendyolPreviewResult?.validation?.ok
                      ? 'Payload önizlemesi hazır.'
                      : 'Payload eksik alanlarla hazırlandı.'}
                  </Typography>
                  {Array.isArray(trendyolPreviewResult?.validation?.missingFields) &&
                  trendyolPreviewResult.validation.missingFields.length > 0 ? (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {trendyolPreviewResult.validation.missingFields.map((field) => (
                        <Chip key={field} size="small" color="warning" label={field} sx={{ fontWeight: 700 }} />
                      ))}
                    </Stack>
                  ) : null}
                </Stack>
              </Alert>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  maxHeight: { xs: 320, sm: 480 },
                  overflow: 'auto',
                  borderRadius: 2,
                  bgcolor: '#0f172a',
                  color: '#e2e8f0',
                  fontSize: '0.76rem',
                  lineHeight: 1.55,
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere'
                }}
              >
                {JSON.stringify(trendyolPreviewResult.payload || trendyolPreviewResult.preview?.payload || {}, null, 2)}
              </Box>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.25 }}>
          <Button onClick={() => setOpenTrendyolPayloadPreviewDialog(false)} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Kapat
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openWebsite}
        onClose={() => setOpenWebsite(false)}
        maxWidth={false}
        fullWidth
        scroll="paper"
        sx={{
          '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: 'min(1680px, 98vw)',
            borderRadius: 2,
            overflow: 'hidden',
            m: 2,
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '96vh'
          }
        }}
      >
        <DialogTitle
          component="div"
          sx={{
            bgcolor: '#26a69a',
            color: '#fff',
            py: 1.75,
            px: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <LanguageIcon sx={{ fontSize: 28, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.2 }}>Web Site Ürün Detay</Typography>
              {editForm?.isim && (
                <Typography noWrap title={editForm.isim} sx={{ fontWeight: 500, fontSize: '0.9rem', opacity: 0.95, maxWidth: { xs: 220, sm: 520, md: 720 } }}>
                  {editForm.isim}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton onClick={() => setOpenWebsite(false)} size="small" sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2.25, bgcolor: '#f5f5f5', overflowY: 'auto', flex: '1 1 auto' }}>
          {websiteSaveBanner && (
            <Alert
              severity={websiteSaveBanner.severity}
              onClose={() => setWebsiteSaveBanner(null)}
              sx={{
                mb: 1.5,
                fontWeight: 700,
                fontSize: '0.95rem',
                borderWidth: 2,
                alignItems: 'center',
                '& .MuiAlert-message': { width: '100%' }
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: 'inherit' }}>{websiteSaveBanner.message}</Typography>
                {websiteSaveBanner.detail ? (
                  <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500, opacity: 0.92 }}>
                    {websiteSaveBanner.detail}
                  </Typography>
                ) : null}
              </Box>
            </Alert>
          )}
          {websiteSeoGenBanner && (
            <Alert
              severity={websiteSeoGenBanner.severity}
              onClose={() => setWebsiteSeoGenBanner(null)}
              sx={{
                mb: 1.5,
                fontWeight: 700,
                fontSize: '0.95rem',
                borderWidth: 2,
                alignItems: 'center',
                '& .MuiAlert-message': { width: '100%' }
              }}
            >
              {websiteSeoGenBanner.message}
            </Alert>
          )}
          {editForm && (
            <>
            {hasMissingFields && (
              <Alert severity="warning" sx={{ mb: 1.5, border: '1px solid #fde68a', bgcolor: '#fffbeb' }}>
                Bu ürün İkas’a gönderilemez. Eksik alanlar:
                <br />
                <strong>{uniqueMissingFields.join(', ')}</strong>
              </Alert>
            )}
            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1.25 }}>
              <Button
                type="button"
                variant="outlined"
                color="primary"
                size="small"
                disabled={webSeoFillLoading}
                onClick={handleFillMissingWebSeo}
                sx={{ textTransform: 'none', fontWeight: 700, borderWidth: 2 }}
              >
                Eksik Açıklama ve SEO Oluştur
              </Button>
            </Stack>
            <Paper
              elevation={0}
              sx={{
                mb: 1.5,
                p: 1.25,
                borderRadius: 1.5,
                border: '1px solid #b2dfdb',
                bgcolor: '#e0f2f1'
              }}
            >
              <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: '#00695c', mb: 0.75 }}>
                İkas senkron durumu
              </Typography>
              <Stack spacing={0.75}>
                {editForm?.ikasSyncStatus === 'synced' ? (
                  <Alert severity="success" sx={{ py: 0.55, '& .MuiAlert-message': { width: '100%' } }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.78rem' }}>
                      Son tam gönderim başarılı (İkas ile uyumlu).
                    </Typography>
                  </Alert>
                ) : null}
                {editForm?.ikasSyncStatus === 'skipped_unchanged' ? (
                  <Alert severity="info" sx={{ py: 0.55, '& .MuiAlert-message': { width: '100%' } }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.78rem' }}>
                      Paket değişmediği için İkas’a yeniden gönderilmedi.
                    </Typography>
                  </Alert>
                ) : null}
                {editForm?.ikasSyncStatus === 'skipped_missing_fields' ||
                (hasMissingFields && editForm?.ikasSyncStatus !== 'synced') ? (
                  <Alert severity="warning" sx={{ py: 0.55, '& .MuiAlert-message': { width: '100%' } }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.78rem' }}>
                      İkas gönderimi için zorunlu alanlar eksik olabilir. Listeyi üstte kontrol edin.
                    </Typography>
                  </Alert>
                ) : null}
                {editForm?.ikasSyncStatus === 'error' ? (
                  <Alert severity="error" sx={{ py: 0.55, '& .MuiAlert-message': { width: '100%' } }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.78rem' }}>
                      Son İkas gönderiminde hata oluştu.
                    </Typography>
                  </Alert>
                ) : null}
                {editForm?.ikasSyncStatus === 'remote_missing' ? (
                  <Alert severity="warning" sx={{ py: 0.55, '& .MuiAlert-message': { width: '100%' } }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.78rem' }}>
                      İkas panelinde bu ürün bulunamadı; yerel kayıttaki İkas bağlantısı temizlendi. Tekrar göndermek için «İkas’a Gönder» kullanın.
                    </Typography>
                  </Alert>
                ) : null}
                {editForm?.ikasSyncStatus === 'error' &&
                (editForm?.ikasProductId || editForm?.ikasVariantId) ? (
                  <Alert severity="info" sx={{ py: 0.65, '& .MuiAlert-message': { width: '100%' } }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.35, fontSize: '0.8rem' }}>
                      Kayıtlı İkas eşlemesi (önceki başarılı senkron olabilir)
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.78rem', opacity: 0.95 }}>
                      Ürün / varyant kimlikleri Firestore’da duruyor; son deneme hatalı olsa bile İkas’ta kayıt bulunabilir.
                    </Typography>
                  </Alert>
                ) : null}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 88 }}>
                    Son işlem durumu:
                  </Typography>
                  {editForm?.ikasSyncStatus ? (
                    <Chip
                      size="small"
                      label={String(editForm.ikasSyncStatus)}
                      color={
                        editForm.ikasSyncStatus === 'synced'
                          ? 'success'
                          : editForm.ikasSyncStatus === 'error'
                            ? 'error'
                            : editForm.ikasSyncStatus === 'skipped_missing_fields' ||
                                editForm.ikasSyncStatus === 'remote_missing'
                              ? 'warning'
                              : editForm.ikasSyncStatus === 'pending' ||
                                  editForm.ikasSyncStatus === 'queued'
                                ? 'primary'
                                : 'default'
                      }
                      sx={{
                        fontWeight: 700,
                        ...(editForm.ikasSyncStatus === 'skipped_unchanged'
                          ? { bgcolor: '#cfd8dc', color: '#37474f' }
                          : {})
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      —
                    </Typography>
                  )}
                </Box>
                {editForm?.ikasProductId ? (
                  <Typography variant="body2" sx={{ fontSize: '0.78rem', wordBreak: 'break-all' }}>
                    <b>İkas ürün id:</b> {String(editForm.ikasProductId)}
                  </Typography>
                ) : null}
                {editForm?.ikasVariantId ? (
                  <Typography variant="body2" sx={{ fontSize: '0.78rem', wordBreak: 'break-all' }}>
                    <b>İkas varyant id:</b> {String(editForm.ikasVariantId)}
                  </Typography>
                ) : null}
                {editForm?.ikasLastSyncedAt ? (
                  <Typography variant="body2" sx={{ fontSize: '0.76rem', color: 'text.secondary' }}>
                    <b>Son gönderim:</b> {formatFirestoreTs(editForm.ikasLastSyncedAt)}
                  </Typography>
                ) : null}
                {editForm?.ikasLastCheckedAt ? (
                  <Typography variant="body2" sx={{ fontSize: '0.76rem', color: 'text.secondary' }}>
                    <b>Son kontrol:</b> {formatFirestoreTs(editForm.ikasLastCheckedAt)}
                  </Typography>
                ) : null}
                {editForm?.ikasLastError ? (
                  <Alert severity="error" sx={{ py: 0.5, '& .MuiAlert-message': { width: '100%' } }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.35, fontSize: '0.78rem' }}>
                      Son hata
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                      {String(editForm.ikasLastError)}
                    </Typography>
                  </Alert>
                ) : null}
                {editForm?.ikasStockSyncStatus === 'skipped_no_location' ? (
                  <Alert severity="warning" sx={{ py: 0.65, '& .MuiAlert-message': { width: '100%' } }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.78rem', mb: 0.35 }}>
                      Stok İkas’a yazılmadı: depo konumu tanımlı değil.
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.76rem', lineHeight: 1.4 }}>
                      Ürün listesi → <strong>İkas Ayarları</strong> → <strong>Stok deposu</strong> seçin veya UUID girin,
                      kaydedin; ardından bu üründe tekrar «İkas’a Gönder» deyin.
                    </Typography>
                  </Alert>
                ) : null}
                {editForm?.ikasStockSyncStatus === 'error' ? (
                  <Alert severity="error" sx={{ py: 0.55, '& .MuiAlert-message': { width: '100%' } }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.78rem', mb: 0.35 }}>
                      Stok senkronunda hata
                    </Typography>
                    {editForm?.ikasStockLastError ? (
                      <Typography variant="body2" sx={{ fontSize: '0.76rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {String(editForm.ikasStockLastError)}
                      </Typography>
                    ) : null}
                  </Alert>
                ) : null}
                {editForm?.ikasStockSyncStatus === 'synced' ? (
                  <Typography variant="body2" sx={{ fontSize: '0.74rem', color: 'text.secondary' }}>
                    <b>Stok (İkas):</b> son gönderimde depoya yazıldı.
                  </Typography>
                ) : null}
                {editForm?.isManualSite === true || editForm?.priceSource === 'manual' ? (
                  <Alert severity="warning" sx={{ py: 0.65, mt: 0.5, '& .MuiAlert-message': { width: '100%' } }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.78rem' }}>
                      Web fiyatı manuel. Otomatik fiyat polleri bu ürüne fiyat göndermez; tam gönderim için «İkas’a Gönder»
                      kullanın.
                    </Typography>
                  </Alert>
                ) : (
                  <Alert severity="info" sx={{ py: 0.65, mt: 0.5, '& .MuiAlert-message': { width: '100%' } }}>
                    <Typography variant="body2" component="div" sx={{ fontWeight: 600, fontSize: '0.78rem', lineHeight: 1.45 }}>
                      Fiyat otomatik güncelleniyor.
                      <br />
                      Fiyat değiştiğinde sistem 15 dakikada bir İkas’a gönderir.
                      <br />
                      Tam ürün güncellemesi için «İkas’a Gönder» butonunu kullanın.
                    </Typography>
                  </Alert>
                )}
                {editForm?.ikasPriceSyncStatus === 'error' ? (
                  <Alert severity="error" sx={{ py: 0.65, mt: 0.5, '& .MuiAlert-message': { width: '100%' } }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.78rem', mb: 0.35 }}>
                      Son fiyat senkronunda hata oluştu.
                    </Typography>
                    {editForm?.ikasPriceLastError ? (
                      <Typography variant="body2" sx={{ fontSize: '0.76rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {String(editForm.ikasPriceLastError)}
                      </Typography>
                    ) : null}
                  </Alert>
                ) : null}
              </Stack>
            </Paper>
            <Grid container spacing={2} alignItems="stretch">
              {/* Sol: Web satış fiyatı + sonuç analizi */}
              <Grid size={{ xs: 12, md: 3 }}>
                <Stack spacing={2} sx={{ height: '100%' }}>
                  <Paper elevation={0} sx={{ p: 1.15, borderRadius: 1.5, border: '1px solid #e9e0f4', bgcolor: '#fff' }}>
                    <Typography sx={{ fontWeight: 800, color: '#6b21a8', mb: 0.5, fontSize: '0.8rem' }}>Satış detayları</Typography>
                    <Alert
                      severity="info"
                      icon={<InfoOutlinedIcon sx={{ color: '#2563eb', fontSize: 15 }} />}
                      sx={{
                        mb: 1.1,
                        py: 0.5,
                        bgcolor: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        color: '#1e293b',
                        borderRadius: 1.5,
                        '& .MuiAlert-icon': { py: 0.15, mr: 0.75, alignItems: 'flex-start' },
                        '& .MuiAlert-message': { width: '100%', py: 0 }
                      }}
                    >
                      <Typography variant="body2" component="div" sx={{ lineHeight: 1.32, fontSize: '0.72rem' }}>
                        <b>Web birim fiyat:</b> {unitInfo.webBirim.toFixed(2)} TL
                        <br />
                        <b>Gram:</b>{' '}
                        {safeParseFloat(editForm?.gram).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} gr
                        <br />
                        <span style={{ opacity: 0.9 }}>(Kaynak: {unitInfo.kaynak})</span>
                      </Typography>
                    </Alert>
                    <Grid container spacing={0.85}>
                      <Grid size={{ xs: 6 }}>
                        <TextField
                          fullWidth
                          margin="dense"
                          size="small"
                          label="Web Satış Fiyatı"
                          name="websiteSatisFiyati"
                          type="number"
                          value={websiteForm.websiteSatisFiyati}
                          onChange={handleWebsiteTabWebSatisChange}
                          sx={webSatisDetayInputSx}
                          helperText={
                            editForm?.isManualSite
                              ? 'Bu değer Ikas’a web satış fiyatı olarak gider. İndirim varsa “Satış (indirimli)” ayrıca gönderilir.'
                              : 'Otomatik hesap'
                          }
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                {editForm?.isManualSite && (
                                  <Tooltip title="Otomatik fiyata dön">
                                    <IconButton size="small" onClick={() => handleRevertAuto('isManualSite')}>
                                      <RestartAltIcon color="primary" fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                TL
                              </InputAdornment>
                            )
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField
                          fullWidth
                          margin="dense"
                          size="small"
                          label="Kredi Kartı Komisyonu"
                          name="kkKomisyonu"
                          type="number"
                          value={magazaForm.kkKomisyonu}
                          onChange={handleMagazaChange}
                          sx={webSatisDetayInputSx}
                          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                        />
                      </Grid>
                      <Grid size={{ xs: 4 }}>
                        <TextField
                          fullWidth
                          margin="dense"
                          size="small"
                          label="KDV Oranı"
                          type="number"
                          value={KUYUMCU_KDV_ORANI}
                          disabled
                          helperText="Kuyumcu matrahında sabit %20"
                          sx={webSatisDetayInputSx}
                          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                        />
                      </Grid>
                      <Grid size={{ xs: 4 }}>
                        <TextField
                          fullWidth
                          margin="dense"
                          size="small"
                          label="Ek Masraf"
                          name="ekMasraf"
                          type="number"
                          value={magazaForm.ekMasraf}
                          onChange={handleMagazaChange}
                          sx={webSatisDetayInputSx}
                          InputProps={{ endAdornment: <InputAdornment position="end">TL</InputAdornment> }}
                        />
                      </Grid>
                      <Grid size={{ xs: 4 }}>
                        <TextField
                          fullWidth
                          margin="dense"
                          size="small"
                          label="Kargo bedeli"
                          name="kargoBedeli"
                          type="number"
                          value={magazaForm.kargoBedeli}
                          onChange={handleMagazaChange}
                          sx={webSatisDetayInputSx}
                          InputProps={{ endAdornment: <InputAdornment position="end">TL</InputAdornment> }}
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={0.85} sx={{ mt: 0.75 }}>
                      <Grid size={{ xs: 6 }}>
                        <TextField
                          fullWidth
                          margin="dense"
                          size="small"
                          label="Müşteriye yapılan indirim"
                          name="ekIndirim"
                          type="number"
                          value={magazaForm.ekIndirim}
                          onChange={handleMagazaChange}
                          sx={webSatisDetayInputSx}
                          InputProps={{ endAdornment: <InputAdornment position="end">TL</InputAdornment> }}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField
                          fullWidth
                          margin="dense"
                          size="small"
                          label="Müşteriye yapılan % indirim"
                          name="indirimYuzde"
                          type="number"
                          value={magazaForm.indirimYuzde}
                          onChange={handleMagazaChange}
                          sx={webSatisDetayInputSx}
                          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>

                  {webSiteMetrics && (
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e5e7eb', bgcolor: '#fff' }}>
                      <Typography sx={{ fontWeight: 800, color: '#6b21a8', mb: 1.15, fontSize: '0.82rem' }}>Sonuç analizi</Typography>
                      <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: '0.76rem', py: 0.65 } }}>
                        <TableBody>
                          <TableRow sx={{ '& td': { borderColor: '#ececec' } }}>
                            <TableCell sx={{ color: '#475569', fontWeight: 600 }}>Maliyet</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              {webSiteMetrics.maliyet.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{ '& td': { borderColor: '#ececec' } }}>
                            <TableCell sx={{ color: '#475569', fontWeight: 600 }}>Web satış fiyatı</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              {webSiteMetrics.satisBrut.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                            </TableCell>
                          </TableRow>
                          {webSiteMetrics.toplamIndirim > 0 && (
                            <TableRow sx={{ '& td': { borderColor: '#ececec' } }}>
                              <TableCell sx={{ color: '#475569' }}>Toplam indirim (TL + %)</TableCell>
                              <TableCell align="right" sx={{ color: '#dc2626', fontWeight: 700 }}>
                                -{webSiteMetrics.toplamIndirim.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                              </TableCell>
                            </TableRow>
                          )}
                          <TableRow sx={{ '& td': { borderColor: '#ececec' } }}>
                            <TableCell sx={{ color: '#475569', fontWeight: 600 }}>Satış (indirimli)</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.88rem' }}>
                              {webSiteMetrics.efektifSatis.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                            </TableCell>
                          </TableRow>
                          {safeParseFloat(magazaForm.kkKomisyonu) > 0 && (
                            <TableRow sx={{ '& td': { borderColor: '#ececec' } }}>
                              <TableCell sx={{ color: '#475569' }}>Banka Kesintisi ({magazaForm.kkKomisyonu}%)</TableCell>
                              <TableCell align="right" sx={{ color: '#dc2626', fontWeight: 700 }}>
                                -{webSiteMetrics.bankaKesintisi.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                              </TableCell>
                            </TableRow>
                          )}
                          <TableRow sx={{ '& td': { borderColor: '#ececec' } }}>
                            <TableCell sx={{ color: '#475569' }}>{`KDV (matrah %${KUYUMCU_KDV_ORANI})`}</TableCell>
                            <TableCell align="right" sx={{ color: '#dc2626', fontWeight: 700 }}>
                              -{webSiteMetrics.kdvTutari.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                            </TableCell>
                          </TableRow>
                          {webSiteMetrics.ekMasraf > 0 && (
                            <TableRow sx={{ '& td': { borderColor: '#ececec' } }}>
                              <TableCell sx={{ color: '#475569' }}>Ek masraf</TableCell>
                              <TableCell align="right" sx={{ color: '#dc2626', fontWeight: 700 }}>
                                -{webSiteMetrics.ekMasraf.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                              </TableCell>
                            </TableRow>
                          )}
                          {webSiteMetrics.kargo > 0 && (
                            <TableRow sx={{ '& td': { borderColor: '#ececec' } }}>
                              <TableCell sx={{ color: '#475569' }}>Kargo bedeli</TableCell>
                              <TableCell align="right" sx={{ color: '#dc2626', fontWeight: 700 }}>
                                -{webSiteMetrics.kargo.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                              </TableCell>
                            </TableRow>
                          )}
                          <TableRow sx={{ '& td': { borderColor: '#ececec', py: 0.75, borderTop: '2px solid #e2e8f0' } }}>
                            <TableCell sx={{ fontWeight: 800 }}>NET Kalan</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800 }}>
                              {webSiteMetrics.netKalan.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{ bgcolor: webSiteMetrics.netKar >= 0 ? '#ecfdf5' : '#fef2f2', '& td': { border: 'none', py: 1 } }}>
                            <TableCell sx={{ fontWeight: 800 }}>NET KAR</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${webSiteMetrics.netKar.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL`}
                                sx={{
                                  borderRadius: 999,
                                  fontWeight: 800,
                                  height: 'auto',
                                  bgcolor: webSiteMetrics.netKar >= 0 ? '#166534' : '#b91c1c',
                                  color: '#fff',
                                  '& .MuiChip-label': { px: 1.1, py: 0.2, fontSize: '0.72rem' }
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                      <Box
                        sx={{
                          mt: 2,
                          p: 1.1,
                          bgcolor: '#fffbeb',
                          borderRadius: 2,
                          border: '1px solid #fde68a'
                        }}
                      >
                        <Typography variant="caption" display="block" align="center" fontWeight="800" color="#92400e" sx={{ letterSpacing: 0.02, fontSize: '0.68rem' }}>
                          MİNİMUM SATIŞ (Zarar Etmemek İçin)
                        </Typography>
                        <Typography variant="h6" align="center" color="#92400e" fontWeight="800" sx={{ mt: 0.3, fontSize: '0.88rem' }}>
                          {webSiteMetrics.minFiyat.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                        </Typography>
                      </Box>
                    </Paper>
                  )}
                </Stack>
              </Grid>

              {/* Orta: genel + açıklama */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Stack spacing={2} sx={{ height: '100%' }}>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0', bgcolor: '#fff' }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#37474f', mb: 1.5 }}>Genel Detaylar</Typography>
                    <Grid container spacing={1.5}>
                      <Grid size={{ xs: 12, md: 8 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Ürün Adı"
                          name="isim"
                          value={editForm.isim || ''}
                          onChange={handleEditChange}
                          sx={modalInputSx}
                          InputProps={{ endAdornment: <InputAdornment position="end"><Tooltip title="Kopyala"><IconButton size="small" onClick={() => handleCopyToClipboard(editForm.isim || '', 'Ürün adı')}><ContentCopyIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip></InputAdornment> }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <Box
                          sx={{
                            ...modalInputSx,
                            minHeight: 40,
                            px: 1.5,
                            py: 0.75,
                            borderRadius: 1.5,
                            border: '1px solid #d5dbe1',
                            bgcolor: '#fafafa'
                          }}
                        >
                          <Typography sx={{ fontSize: '0.68rem', color: '#64748b', mb: 0.45, fontWeight: 600 }}>
                            Ürün Kategorisi
                          </Typography>
                          <Stack direction="row" spacing={0.45} flexWrap="wrap" useFlexGap>
                            {(webKategoriEtiketleri.length ? webKategoriEtiketleri : ['-']).map((etiket, idx) => (
                              <Chip
                                key={`web-kategori-2-${idx}-${etiket}`}
                                label={etiket}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.62rem',
                                  bgcolor: '#fff6e5',
                                  color: '#8a5b22',
                                  border: '1px solid #f7dfb5',
                                  fontWeight: 700,
                                  '& .MuiChip-label': { px: 0.55, whiteSpace: 'nowrap' }
                                }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0', bgcolor: '#fff', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 420 }}>
                    <TextField
                      fullWidth
                      label="Ürün Açıklaması"
                      name="aciklama"
                      multiline
                      minRows={22}
                      value={editForm.aciklama || ''}
                      onChange={handleEditChange}
                      sx={{ ...modalInputSx, flex: 1, '& .MuiInputBase-root': { alignItems: 'flex-start' } }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Kopyala">
                              <IconButton size="small" onClick={() => handleCopyToClipboard(editForm.aciklama || '', 'Web ürün açıklaması')}>
                                <ContentCopyIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Paper>
                </Stack>
              </Grid>

              {/* Sağ: özellikler + SEO */}
              <Grid size={{ xs: 12, md: 3 }}>
                <Stack spacing={2}>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0', bgcolor: '#fff' }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#37474f', mb: 1.5 }}>Ürün Özellikleri</Typography>
                    <Grid container spacing={1.25}>
                      <Grid size={{ xs: 6 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Barkod"
                          value={editForm.barkod || ''}
                          sx={{ ...modalInputSx, '& .MuiInputBase-root': { bgcolor: '#f5f5f5' } }}
                          InputProps={{ readOnly: true, endAdornment: <InputAdornment position="end"><Tooltip title="Kopyala"><IconButton size="small" onClick={() => handleCopyToClipboard(editForm.barkod || '', 'Barkod')}><ContentCopyIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip></InputAdornment> }}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Stok kodu"
                          name="stokKodu"
                          value={editForm.stokKodu || editForm.barkod || ''}
                          onChange={handleEditChange}
                          sx={modalInputSx}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField fullWidth size="small" label="Desi" name="desi" value={editForm.desi ?? ''} onChange={handleEditChange} sx={modalInputSx} />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField fullWidth size="small" label="Marka" name="marka" value={editForm.marka || 'UKBA GOLD'} onChange={handleEditChange} sx={modalInputSx} />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Gram"
                          name="gram"
                          type="number"
                          value={editForm.gram}
                          onChange={handleEditChange}
                          sx={modalInputSx}
                          InputProps={{ endAdornment: <InputAdornment position="end">gr</InputAdornment> }}
                        />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField fullWidth size="small" label="Milyem" name="urunMilyem" value={editForm.urunMilyem || ''} onChange={handleEditChange} sx={modalInputSx} />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField fullWidth size="small" label="İşçilik" name="iscilik" value={editForm.iscilik || ''} onChange={handleEditChange} sx={modalInputSx} />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField fullWidth size="small" label="Adet İşçiliği" name="adetIscilik" value={editForm.adetIscilik || ''} onChange={handleEditChange} sx={modalInputSx} />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField fullWidth size="small" label="Renk" name="urunRengi" value={editForm.urunRengi ?? editForm.renk ?? ''} onChange={handleEditChange} sx={modalInputSx} />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField fullWidth size="small" label="Ürün Çapı" name="urunCapi" value={editForm.urunCapi || ''} onChange={handleEditChange} sx={modalInputSx} />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField fullWidth size="small" label="Taş Özelliği" name="tasOzelligi" value={editForm.tasOzelligi || ''} onChange={handleEditChange} sx={modalInputSx} />
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <TextField fullWidth size="small" label="Zincir Uzunluğu" name="zincirUzunlugu" value={editForm.zincirUzunlugu || ''} onChange={handleEditChange} sx={modalInputSx} />
                      </Grid>
                      <Grid size={{ xs: 6 }}>{renderEditFormUrunTipiSelect('Ürün tipi')}</Grid>
                    </Grid>
                  </Paper>

                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0', bgcolor: '#fff' }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#37474f', mb: 1.5 }}>SEO Çalışması</Typography>
                    <Stack spacing={1.5}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Slug"
                        name="ws_slug"
                        value={editForm.ws_slug || ''}
                        onChange={handleEditChange}
                        sx={modalInputSx}
                        InputProps={{ endAdornment: <InputAdornment position="end"><Tooltip title="Kopyala"><IconButton size="small" onClick={() => handleCopyToClipboard(editForm.ws_slug || '', 'Web slug')}><ContentCopyIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip></InputAdornment> }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="SEO Başlık"
                        name="ws_seoBaslik"
                        value={editForm.ws_seoBaslik || ''}
                        onChange={handleEditChange}
                        sx={modalInputSx}
                        InputProps={{ endAdornment: <InputAdornment position="end"><Tooltip title="Kopyala"><IconButton size="small" onClick={() => handleCopyToClipboard(editForm.ws_seoBaslik || '', 'Web SEO başlık')}><ContentCopyIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip></InputAdornment> }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="SEO Açıklama"
                        name="ws_seoAciklama"
                        multiline
                        minRows={3}
                        value={editForm.ws_seoAciklama || ''}
                        onChange={handleEditChange}
                        sx={modalInputSx}
                        InputProps={{ endAdornment: <InputAdornment position="end"><Tooltip title="Kopyala"><IconButton size="small" onClick={() => handleCopyToClipboard(editForm.ws_seoAciklama || '', 'Web SEO açıklama')}><ContentCopyIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip></InputAdornment> }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="SEO Anahtar Kelimeler"
                        name="ws_seoAnahtarKelimeler"
                        value={editForm.ws_seoAnahtarKelimeler || ''}
                        onChange={handleEditChange}
                        sx={modalInputSx}
                        InputProps={{ endAdornment: <InputAdornment position="end"><Tooltip title="Kopyala"><IconButton size="small" onClick={() => handleCopyToClipboard(editForm.ws_seoAnahtarKelimeler || '', 'Web SEO etiketler')}><ContentCopyIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip></InputAdornment> }}
                      />
                      <Alert severity="info" sx={{ py: 0.65, fontSize: '0.78rem', '& .MuiAlert-message': { width: '100%' } }}>
                        <Typography variant="body2" sx={{ fontSize: '0.78rem', lineHeight: 1.35 }}>
                          <b>Canonical URL:</b> İkas paket önizlemesinde tam adres üretilebilir; şu an İkas Admin API{' '}
                          <code style={{ fontSize: '0.72rem' }}>saveProduct</code> ile gönderilmez (canonical alanı{' '}
                          <code style={{ fontSize: '0.72rem' }}>metaData.canonicals</code> kullanılmaz — İkas tarafında Argument
                          Validation Error oluşturuyordu). Canonical şimdilik İkas panelinde manuel kontrol edilmelidir.
                        </Typography>
                      </Alert>
                      <TextField
                        fullWidth
                        size="small"
                        label="Canonical URL"
                        name="ws_canonicalUrl"
                        value={editForm.ws_canonicalUrl || ''}
                        onChange={handleEditChange}
                        sx={modalInputSx}
                        InputProps={{ endAdornment: <InputAdornment position="end"><Tooltip title="Kopyala"><IconButton size="small" onClick={() => handleCopyToClipboard(editForm.ws_canonicalUrl || '', 'Web canonical')}><ContentCopyIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip></InputAdornment> }}
                      />
                    </Stack>
                  </Paper>
                </Stack>
              </Grid>
            </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.75, bgcolor: '#fafafa', borderTop: '1px solid #e0e0e0', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            onClick={handleOpenIkasPayloadPreview}
            variant="outlined"
            disabled={ikasPreviewLoading || hasMissingFields}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {ikasPreviewLoading ? 'Önizleme hazırlanıyor...' : 'İkas Paketini Önizle'}
          </Button>
          <Button onClick={() => setOpenWebsite(false)} sx={{ color: '#424242', textTransform: 'none', fontWeight: 700 }}>
            Kapat
          </Button>
          <Tooltip
            title={
              ikasSendLoading
                ? 'Gönderiliyor…'
                : isIkasSendDisabled
                  ? `Gönderilemez. Eksik alanlar: ${uniqueMissingFields.join(', ')}`
                  : 'Ürünü İkas’a gönder'
            }
          >
            <span>
              <Button
                variant="contained"
                color="secondary"
                disabled={isIkasSendDisabled || ikasSendLoading}
                onClick={handleSendProductToIkas}
                startIcon={<SendIcon />}
                sx={{ textTransform: 'none', fontWeight: 800, px: 2 }}
              >
                {ikasSendLoading ? 'İkas’a gönderiliyor…' : 'İkas’a Gönder'}
              </Button>
            </span>
          </Tooltip>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{ bgcolor: '#26a69a', textTransform: 'none', fontWeight: 800, px: 2.5, '&:hover': { bgcolor: '#00897b' } }}
          >
            Onayla ve kaydet
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openIkasPreview}
        onClose={() => setOpenIkasPreview(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>İkas Paket Önizleme</DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#fafafa' }}>
          {ikasPreviewLoading ? (
            <Typography>Önizleme hazırlanıyor...</Typography>
          ) : (
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 2,
                borderRadius: 1.5,
                bgcolor: '#111827',
                color: '#e5e7eb',
                fontSize: '0.78rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
            >
              {JSON.stringify(ikasPreviewPayload || { message: 'Önizleme verisi yok' }, null, 2)}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenIkasPreview(false)} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Kapat
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openMagaza}
        onClose={() => setOpenMagaza(false)}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 2,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '92vh',
            minHeight: { xs: 'min(88vh, 780px)', sm: 'min(88vh, 800px)' }
          }
        }}
      >
        <DialogTitle
          component="div"
          sx={{
            background: 'linear-gradient(90deg, #934daf 0%, #7c3aed 42%, #6d28d9 100%)',
            color: '#fff',
            py: 1.75,
            px: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <StoreIcon sx={{ fontSize: 26, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: '1rem', lineHeight: 1.2 }}>Mağaza Satış Hesaplayıcı</Typography>
              {editForm?.isim && (
                <Typography noWrap title={editForm.isim} sx={{ fontWeight: 400, fontSize: '0.88rem', opacity: 0.92, maxWidth: { xs: 240, sm: 480, md: 640 } }}>
                  {editForm.isim}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton onClick={() => setOpenMagaza(false)} size="small" sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2.5, bgcolor: '#fafafa', overflowY: 'visible' }}>
          <Grid container spacing={2.25}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper elevation={0} sx={{ p: 1.35, borderRadius: 1.5, border: '1px solid #e9e0f4', bgcolor: '#fff', height: '100%' }}>
                <Typography sx={{ fontWeight: 800, color: '#6b21a8', mb: 0.65, fontSize: '0.82rem' }}>Satış detayları</Typography>
                <Alert
                  severity="info"
                  icon={<InfoOutlinedIcon sx={{ color: '#2563eb', fontSize: 16 }} />}
                  sx={{
                    mb: 1,
                    py: 0.5,
                    bgcolor: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    color: '#1e293b',
                    borderRadius: 1.5,
                    '& .MuiAlert-message': { width: '100%', py: 0 }
                  }}
                >
                  <Typography variant="body2" component="div" sx={{ lineHeight: 1.32, fontSize: '0.74rem' }}>
                    <b>Birim Fiyat:</b> {unitInfo.magazaBirim.toFixed(2)} TL
                    <br />
                    <b>Gram:</b>{' '}
                    {safeParseFloat(editForm?.gram).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} gr
                    <br />
                    <span style={{ opacity: 0.9 }}>(Kaynak: {unitInfo.kaynak})</span>
                  </Typography>
                </Alert>

                <Grid container spacing={1}>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      fullWidth
                      margin="dense"
                      size="small"
                      label="Mağaza Satış Fiyatı"
                      name="satisFiyati"
                      type="number"
                      value={magazaForm.satisFiyati}
                      onChange={handleMagazaChange}
                      sx={modalInputSx}
                      InputProps={{ endAdornment: <InputAdornment position="end">TL</InputAdornment> }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      fullWidth
                      margin="dense"
                      size="small"
                      label="Kredi Kartı Komisyonu"
                      name="kkKomisyonu"
                      type="number"
                      value={magazaForm.kkKomisyonu}
                      onChange={handleMagazaChange}
                      sx={modalInputSx}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      fullWidth
                      margin="dense"
                      size="small"
                      label="KDV Oranı"
                      type="number"
                      value={KUYUMCU_KDV_ORANI}
                      disabled
                      helperText="Kuyumcu matrahında sabit %20"
                      sx={modalInputSx}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      fullWidth
                      margin="dense"
                      size="small"
                      label="Ek Masraf"
                      name="ekMasraf"
                      type="number"
                      value={magazaForm.ekMasraf}
                      onChange={handleMagazaChange}
                      sx={modalInputSx}
                      InputProps={{ endAdornment: <InputAdornment position="end">TL</InputAdornment> }}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={1} sx={{ mt: 1 }}>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      fullWidth
                      margin="dense"
                      size="small"
                      label="Müşteriye yapılan indirim"
                      name="ekIndirim"
                      type="number"
                      value={magazaForm.ekIndirim}
                      onChange={handleMagazaChange}
                      sx={modalInputSx}
                      InputProps={{ endAdornment: <InputAdornment position="end">TL</InputAdornment> }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      fullWidth
                      margin="dense"
                      size="small"
                      label="Müşteriye yapılan % indirim"
                      name="indirimYuzde"
                      type="number"
                      value={magazaForm.indirimYuzde}
                      onChange={handleMagazaChange}
                      sx={modalInputSx}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField
                      fullWidth
                      margin="dense"
                      size="small"
                      label="Kargo bedeli"
                      name="kargoBedeli"
                      type="number"
                      value={magazaForm.kargoBedeli}
                      onChange={handleMagazaChange}
                      sx={modalInputSx}
                      InputProps={{ endAdornment: <InputAdornment position="end">TL</InputAdornment> }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              {magazaMetrics && (
                <Paper elevation={0} sx={{ p: 2, height: '100%', bgcolor: '#fff', borderRadius: 2, border: '1px solid #e5e7eb' }}>
                  <Typography sx={{ fontWeight: 800, color: '#6b21a8', mb: 1.15, fontSize: '0.82rem' }}>Sonuç analizi</Typography>
                  <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: '0.76rem', py: 0.65 } }}>
                    <TableBody>
                      <TableRow sx={{ '& td': { borderColor: '#ececec' } }}>
                        <TableCell sx={{ color: '#475569', fontWeight: 600 }}>Maliyet</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {magazaMetrics.maliyet.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ '& td': { borderColor: '#ececec' } }}>
                        <TableCell sx={{ color: '#475569', fontWeight: 600 }}>Mağaza satış (liste)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {magazaMetrics.satisBrut.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                        </TableCell>
                      </TableRow>
                      {magazaMetrics.toplamIndirim > 0 && (
                        <TableRow sx={{ '& td': { borderColor: '#ececec' } }}>
                          <TableCell sx={{ color: '#475569' }}>Toplam indirim (TL + %)</TableCell>
                          <TableCell align="right" sx={{ color: '#dc2626', fontWeight: 700 }}>
                            -{magazaMetrics.toplamIndirim.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow sx={{ '& td': { borderColor: '#ececec' } }}>
                        <TableCell sx={{ color: '#475569', fontWeight: 600 }}>Satış (indirimli)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.88rem' }}>
                          {magazaMetrics.efektifSatis.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                        </TableCell>
                      </TableRow>
                      {safeParseFloat(magazaForm.kkKomisyonu) > 0 && (
                        <TableRow sx={{ '& td': { borderColor: '#ececec' } }}>
                          <TableCell sx={{ color: '#475569' }}>Banka Kesintisi ({magazaForm.kkKomisyonu}%)</TableCell>
                          <TableCell align="right" sx={{ color: '#dc2626', fontWeight: 700 }}>
                            -{magazaMetrics.bankaKesintisi.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow sx={{ '& td': { borderColor: '#ececec' } }}>
                        <TableCell sx={{ color: '#475569' }}>{`KDV (matrah %${KUYUMCU_KDV_ORANI})`}</TableCell>
                        <TableCell align="right" sx={{ color: '#dc2626', fontWeight: 700 }}>
                          -{magazaMetrics.kdvTutari.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                        </TableCell>
                      </TableRow>
                      {magazaMetrics.ekMasraf > 0 && (
                        <TableRow sx={{ '& td': { borderColor: '#ececec' } }}>
                          <TableCell sx={{ color: '#475569' }}>Ek masraf</TableCell>
                          <TableCell align="right" sx={{ color: '#dc2626', fontWeight: 700 }}>
                            -{magazaMetrics.ekMasraf.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                          </TableCell>
                        </TableRow>
                      )}
                      {magazaMetrics.kargo > 0 && (
                        <TableRow sx={{ '& td': { borderColor: '#ececec' } }}>
                          <TableCell sx={{ color: '#475569' }}>Kargo bedeli</TableCell>
                          <TableCell align="right" sx={{ color: '#dc2626', fontWeight: 700 }}>
                            -{magazaMetrics.kargo.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow sx={{ '& td': { borderColor: '#ececec', py: 0.75, borderTop: '2px solid #e2e8f0' } }}>
                        <TableCell sx={{ fontWeight: 800 }}>NET Kalan</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800 }}>
                          {magazaMetrics.netKalan.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                        </TableCell>
                      </TableRow>
                      <TableRow sx={{ bgcolor: magazaMetrics.netKar >= 0 ? '#ecfdf5' : '#fef2f2', '& td': { border: 'none', py: 1 } }}>
                        <TableCell sx={{ fontWeight: 800 }}>NET KAR</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${magazaMetrics.netKar.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL`}
                            sx={{
                              borderRadius: 999,
                              fontWeight: 800,
                              height: 'auto',
                              bgcolor: magazaMetrics.netKar >= 0 ? '#166534' : '#b91c1c',
                              color: '#fff',
                              '& .MuiChip-label': { px: 1.1, py: 0.2, fontSize: '0.72rem' }
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  <Box
                    sx={{
                      mt: 2,
                      p: 1.1,
                      bgcolor: '#fffbeb',
                      borderRadius: 2,
                      border: '1px solid #fde68a'
                    }}
                  >
                    <Typography variant="caption" display="block" align="center" fontWeight="800" color="#92400e" sx={{ letterSpacing: 0.02, fontSize: '0.68rem' }}>
                      MİNİMUM SATIŞ (Zarar Etmemek İçin)
                    </Typography>
                    <Typography variant="h6" align="center" color="#92400e" fontWeight="800" sx={{ mt: 0.3, fontSize: '0.88rem' }}>
                      {magazaMetrics.minFiyat.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                    </Typography>
                  </Box>
                </Paper>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2.5,
            py: 1.75,
            bgcolor: '#f4f4f5',
            borderTop: '1px solid #e5e5e5',
            justifyContent: 'flex-end',
            gap: 1.25
          }}
        >
          <Button onClick={() => setOpenMagaza(false)} sx={{ color: '#111827', fontWeight: 700, textTransform: 'none' }}>
            Kapat
          </Button>
          <Button
            onClick={() => {
              setEditForm((prev) => ({ ...prev, magazaFiyati: magazaForm.satisFiyati }));
              setOpenMagaza(false);
            }}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              px: 2.5,
              bgcolor: '#7c3aed',
              boxShadow: '0 4px 12px rgba(124, 58, 237, 0.35)',
              '&:hover': { bgcolor: '#6d28d9' }
            }}
          >
            Fiyatı güncelle
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={ikasJobDialogOpen}
        onClose={() => {
          if (!ikasJobRunning) setIkasJobDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>{ikasJobTitle}</DialogTitle>
        <DialogContent dividers>
          {ikasJobRunning ? <LinearProgress sx={{ mb: 2 }} /> : null}
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontWeight: 700, mb: 1 }}>
            {ikasJobSummary}
          </Typography>
          {ikasJobDetail.length > 0 ? (
            <Box component="ul" sx={{ m: 0, pl: 2, maxHeight: 280, overflow: 'auto' }}>
              {ikasJobDetail.slice(0, 400).map((line, idx) => (
                <li key={`${idx}-${line.slice(0, 24)}`}>
                  <Typography variant="caption" sx={{ wordBreak: 'break-word', display: 'block' }}>
                    {line}
                  </Typography>
                </li>
              ))}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={ikasJobRunning}
            onClick={() => setIkasJobDialogOpen(false)}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Kapat
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- BİLDİRİM BALONU (SNACKBAR) --- */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      <IkasSettingsDialog open={openIkasSettings} onClose={() => setOpenIkasSettings(false)} />
      <Dialog
        open={openTrendyolTest}
        onClose={() => setOpenTrendyolTest(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 3 },
            m: { xs: 0, sm: 2 },
            width: { xs: '100%', sm: 'calc(100% - 32px)' },
            maxHeight: { xs: '100%', sm: '90vh' }
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 900, color: '#0f172a', pb: 1 }}>
          Trendyol Ayarları / Test
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, bgcolor: '#f8fafc' }}>
          <TrendyolConnectionTestPanel />
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.25 }}>
          <Button
            onClick={() => setOpenTrendyolTest(false)}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            Kapat
          </Button>
        </DialogActions>
      </Dialog>

      <style>{`
        .ul-mobile-list-head {
          display: none;
        }

        /* --- MOBİL: Ürün Listesi (masaüstü sx stillerine dokunulmaz) --- */
        @media (max-width: 600px) {
          .ul-page {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            min-height: 0 !important;
            max-height: none !important;
            height: auto !important;
            overflow-x: hidden !important;
            overflow-y: visible !important;
            padding: 0 0 80px !important;
            box-sizing: border-box !important;
          }

          .ul-card {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            margin: 0 !important;
            border-radius: 12px !important;
            overflow: visible !important;
            max-height: none !important;
            flex: none !important;
          }

          .ul-header {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 10px !important;
            padding: 12px 10px !important;
          }

          .ul-header-title-wrap {
            width: 100% !important;
          }

          .ul-header-mid {
            width: 100% !important;
            max-width: 100% !important;
          }

          .ul-header-search {
            width: 100% !important;
          }

          .ul-header-search .MuiOutlinedInput-root {
            width: 100% !important;
          }

          .ul-header-search .MuiInputBase-input {
            font-size: 16px !important;
          }

          .ul-header-selection {
            width: 100% !important;
          }

          .ul-header-selection .MuiButton-root {
            flex: 1 1 auto !important;
            min-width: 0 !important;
          }

          .ul-header-tools {
            width: 100% !important;
            justify-content: stretch !important;
          }

          .ul-header-tools .MuiButton-root {
            flex: 1 1 calc(50% - 4px) !important;
            min-width: 0 !important;
            font-size: 0.68rem !important;
            padding-left: 6px !important;
            padding-right: 6px !important;
          }

          .ul-content {
            overflow: visible !important;
            min-height: 0 !important;
          }

          .ul-tabs {
            overflow-x: auto !important;
            overflow-y: hidden !important;
            flex-wrap: nowrap !important;
            -webkit-overflow-scrolling: touch;
            padding-bottom: 4px !important;
            /* Serit 9px'e cokup kategori yazilari kesiliyordu */
            flex: 0 0 auto !important;
            height: auto !important;
            min-height: 44px !important;
            align-items: center !important;
          }

          .ul-tabs .MuiButton-root {
            flex-shrink: 0 !important;
          }

          .ul-table-wrap {
            flex: none !important;
            overflow: visible !important;
            width: 100% !important;
            max-width: 100% !important;
            border-top: 1px solid #ece8df !important;
          }

          .ul-mobile-list-head {
            display: grid !important;
            grid-template-columns: 78px minmax(0, 1fr) auto;
            gap: 8px;
            padding: 8px 10px 6px;
            background: #fffdf8;
            border-bottom: 1px solid #eee7da;
            position: sticky;
            top: 0;
            z-index: 2;
          }

          .ul-mobile-list-head span {
            font-size: 11px;
            font-weight: 800;
            color: #64748b;
            letter-spacing: 0.03em;
            text-transform: uppercase;
          }

          .ul-mobile-list-head span:nth-child(2) {
            text-align: right;
          }

          .ul-mobile-list-head span:nth-child(3) {
            text-align: right;
            min-width: 72px;
          }

          .ul-table {
            display: block !important;
            min-width: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            table-layout: auto !important;
          }

          .ul-table colgroup {
            display: none !important;
          }

          .ul-table thead {
            display: none !important;
          }

          .ul-table tbody {
            display: block !important;
            width: 100% !important;
            max-width: 100% !important;
          }

          /* Kompakt liste satırı — tablo hissi, ürün başına ~5 satır değil 1 blok */
          .ul-table tbody tr.ul-data-row {
            display: grid !important;
            grid-template-columns: 28px 42px minmax(0, 1fr) auto auto;
            grid-template-rows: auto auto auto auto auto;
            gap: 2px 6px;
            align-items: start;
            width: 100% !important;
            max-width: 100% !important;
            height: auto !important;
            max-height: none !important;
            margin: 0 !important;
            padding: 10px 10px 10px 8px !important;
            border: none !important;
            border-bottom: 1px solid #ece8df !important;
            border-radius: 0 !important;
            overflow: visible !important;
            background: #fff !important;
            box-shadow: none !important;
            box-sizing: border-box !important;
          }

          .ul-table tbody tr.ul-data-row:nth-of-type(even) {
            background: #fffdf9 !important;
          }

          .ul-table tbody tr.ul-data-row.Mui-selected,
          .ul-table tbody tr.ul-data-row.Mui-selected:nth-of-type(even) {
            background: #fff8ef !important;
          }

          .ul-table tbody tr.ul-data-row .MuiTableCell-root {
            display: block !important;
            width: auto !important;
            max-width: none !important;
            min-width: 0 !important;
            height: auto !important;
            max-height: none !important;
            padding: 0 !important;
            margin: 0 !important;
            border: none !important;
            text-align: inherit !important;
            font-size: inherit !important;
            overflow: visible !important;
            box-sizing: border-box !important;
          }

          .ul-table tbody tr.ul-data-row .MuiTableCell-root::before {
            display: none !important;
            content: none !important;
          }

          .ul-cell-select {
            grid-column: 1 !important;
            grid-row: 1 / 6 !important;
            align-self: center !important;
          }

          .ul-cell-select .MuiCheckbox-root {
            padding: 2px !important;
          }

          .ul-cell-image {
            grid-column: 2 !important;
            grid-row: 1 / 6 !important;
            align-self: start !important;
            padding-top: 2px !important;
          }

          .ul-cell-image .MuiAvatar-root {
            width: 40px !important;
            height: 40px !important;
            border-radius: 8px !important;
          }

          .ul-cell-name {
            grid-column: 3 !important;
            grid-row: 1 !important;
            min-width: 0 !important;
          }

          .ul-cell-name > div {
            align-items: flex-start !important;
            min-height: 0 !important;
          }

          .ul-cell-name .MuiTypography-root {
            white-space: normal !important;
            display: -webkit-box !important;
            -webkit-line-clamp: 2 !important;
            -webkit-box-orient: vertical !important;
            overflow: hidden !important;
            min-height: 0 !important;
            font-size: 13px !important;
            line-height: 1.25 !important;
            text-align: left !important;
          }

          .ul-cell-name .MuiIconButton-root {
            display: none !important;
          }

          .ul-cell-store {
            grid-column: 4 / 6 !important;
            grid-row: 1 !important;
            align-self: start !important;
            text-align: right !important;
            white-space: nowrap !important;
            padding-left: 4px !important;
          }

          .ul-cell-store .MuiTypography-root {
            font-size: 13px !important;
            font-weight: 800 !important;
            color: #111827 !important;
          }

          .ul-cell-barcode {
            grid-column: 3 / 6 !important;
            grid-row: 2 !important;
            text-align: left !important;
          }

          .ul-cell-barcode > div {
            justify-content: flex-start !important;
          }

          .ul-cell-barcode .MuiTypography-root {
            font-size: 10.5px !important;
            max-width: 100% !important;
          }

          .ul-cell-barcode .MuiIconButton-root {
            display: none !important;
          }

          .ul-cell-category {
            grid-column: 3 / 6 !important;
            grid-row: 3 !important;
            text-align: left !important;
          }

          .ul-cell-category > div {
            height: auto !important;
            min-height: 0 !important;
            max-height: none !important;
            justify-content: flex-start !important;
          }

          .ul-cell-category .MuiChip-root {
            height: 18px !important;
            font-size: 9px !important;
          }

          .ul-cell-gram {
            grid-column: 3 !important;
            grid-row: 4 !important;
            text-align: left !important;
            color: #64748b !important;
            font-weight: 600 !important;
            font-size: 10.5px !important;
            white-space: nowrap !important;
          }

          .ul-cell-web {
            grid-column: 4 !important;
            grid-row: 4 !important;
            text-align: right !important;
            font-size: 10px !important;
            white-space: nowrap !important;
          }

          .ul-cell-web .MuiTypography-root {
            font-size: 10px !important;
          }

          .ul-cell-web .MuiBox-root {
            align-items: flex-end !important;
            min-height: 0 !important;
            max-height: none !important;
          }

          .ul-cell-marketplace {
            grid-column: 5 !important;
            grid-row: 4 !important;
            text-align: right !important;
            font-size: 10px !important;
            white-space: nowrap !important;
          }

          .ul-cell-marketplace .MuiTypography-root {
            font-size: 10px !important;
            color: #b45309 !important;
            font-weight: 700 !important;
          }

          /* Iki dugme yan yana dursun; arada bos alan kaliyordu */
          .ul-cell-ikas {
            grid-column: 3 / 5 !important;
            grid-row: 5 !important;
            text-align: right !important;
          }

          .ul-cell-trendyol {
            grid-column: 5 !important;
            grid-row: 5 !important;
            text-align: right !important;
          }

          .ul-cell-ikas .MuiButton-root,
          .ul-cell-trendyol .MuiButton-root {
            min-width: 0 !important;
            width: 100% !important;
            max-width: 92px !important;
            font-size: 10px !important;
            padding: 2px 6px !important;
            min-height: 26px !important;
          }

          .ul-footer {
            padding: 12px 8px !important;
          }

          .ul-footer .MuiPagination-ul {
            flex-wrap: wrap !important;
            justify-content: center !important;
          }

          .ul-bulk-dialog-paper {
            width: calc(100vw - 16px) !important;
            max-width: calc(100vw - 16px) !important;
            margin: 8px !important;
            max-height: calc(100svh - 16px) !important;
          }

          .ul-edit-dialog .MuiDialog-paper {
            width: 100% !important;
            max-width: 100% !important;
            height: 100svh !important;
            max-height: 100svh !important;
            margin: 0 !important;
            border-radius: 0 !important;
          }

          .ul-edit-dialog .MuiInputBase-input,
          .ul-edit-dialog .MuiSelect-select,
          .ul-bulk-dialog .MuiInputBase-input,
          .ul-bulk-dialog .MuiSelect-select {
            font-size: 16px !important;
          }
        }
      `}</style>
    </Box>
  );
}

export default UrunListesi;